import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Icons } from '../utils/icons';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { collection, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { mapEstados, calcularKpisMantenimientos } from '../utils/mantenimientosKpis';
import { ESTADOS_VIGENTE_AMPLIO } from '../utils/calcularEstado';
import { buscarZona } from '../utils/zonas';
import ObrasView from './mantenimientos/ObrasView';
import ObrasAnalitica from './mantenimientos/ObrasAnalitica';
import useObrasConEstado from './mantenimientos/utils/useObrasConEstado';

// ─── Constantes ───────────────────────────────────────────────────────────────

const PACKS = {
  basico:    { label: 'Pack Hogar Básico',  cls: 'bg-blue-100 text-blue-800'    },
  silver:    { label: 'Pack Hogar Silver',  cls: 'bg-purple-100 text-purple-800' },
  gold:      { label: 'Pack Hogar Gold',    cls: 'bg-amber-100 text-amber-800'   },
  caldera:   { label: 'Pack Caldera',       cls: 'bg-orange-100 text-orange-800' },
  comunidad: { label: 'Comunidad',          cls: 'bg-green-100 text-green-800'   },
};

const TIPOS_EQUIPO = {
  nuos_ariston:    'NUOS Ariston',
  aerotermia_otra: 'Aerotermia otra marca',
  split:           'Split',
  conductos:       'Conductos',
  caldera:         'Caldera',
  acumulador:      'Acumulador',
};

const TIPOS_ASSET = [
  'BOMBA DE CALOR',
  'AIRE ACONDICIONADO',
  'CONDUCTOS',
  'BOMBA DE CALOR CALEFACCION',
  'CALDERA',
  'TERMO ELECTRICO',
  'CALENTADOR DE GAS',
];

const CANALES = {
  puesta_en_marcha:     'Puesta en marcha',
  llamada_base_dormida: 'Base dormida',
  instalador:           'Instalador',
  promotora:            'Promotora',
  admin_fincas:         'Admin. fincas',
  digital:              'Digital',
  otro:                 'Otro',
};

const RESULTADOS_LLAMADA = [
  { value: 'interesado',      label: 'Interesado'       },
  { value: 'firmado',         label: 'Firmado ✓'        },
  { value: 'vuelvo_a_llamar', label: 'Volver a llamar'  },
  { value: 'no_interesado',   label: 'No interesado'    },
];

const TECNICOS_NOMBRES = ['Benito', 'Pablo', 'Dani', 'Ivan', 'Junior'];
const TECNICOS_IDS     = { Benito: 21936, Pablo: 23415, Dani: 21945, Ivan: 31171, Junior: null };

// Traducción de campos crudos de las workorders StelOrder (colección Firebase
// `ordenes`, sin transformar — ver netlify/functions/_technicians.js y
// updateWorkorderState.js, de donde salen estos mismos ids).
const WO_TECNICO_POR_ID = { 21936: 'Benito', 23415: 'Pablo', 21945: 'Dani', 31171: 'Ivan', 25901: 'Joaquin' };
const WO_ESTADO_LABEL = {
  651457: 'Pendiente', 1018827: 'En ruta', 1016222: 'Pendiente material',
  1016221: 'Pendiente sustitución', 1007173: 'Pendiente pago', 651428: 'Cerrada',
  1016219: 'Cobrada', 1004686: 'Garantía', 1016220: 'Ausente', 651446: 'Rechazada',
  1040205: 'Pendiente presupuesto', 1305009: 'Instalación', 1041994: 'Preliq. Ariston',
  1069646: 'Preliq. Nofer', 1069647: 'Preliq. Svan', 1070405: 'Preliq. Logman', 1226452: 'Preliq. Beretta',
};
const ZONAS            = ['Zona 1-2', 'Zona 3', 'Zona 4', 'Zona 5', 'Zona 6'];
const RECARGO_ZONA     = { 'Zona 1-2': 15, 'Zona 3': 0, 'Zona 4': 20, 'Zona 5': 20, 'Zona 6': 25 };
const POR_PAGINA       = 50;
const PAGINA_KANBAN    = 30; // tarjetas visibles por columna antes de "Cargar más"

const getZonaTecnicoFromCP = buscarZona;

// Días en los que cada técnico trabaja en cada zona (espejo del backend getDisponibilidad.js)
const CALENDARIO_MANTENIMIENTO = {
  Lunes:     { Benito: ['Zona 5'],        Ivan: ['Zona 4'], Pablo: ['Zona 1-2'], Dani: ['Zona 3'] },
  Martes:    { Benito: ['Zona 6'],        Ivan: ['Zona 4'], Pablo: ['Zona 1-2'], Dani: ['Zona 3'] },
  Miercoles: { Benito: ['Zona 5'],        Ivan: ['Zona 4'], Pablo: ['Zona 1-2'], Dani: ['Zona 3'] },
  Jueves:    { Benito: ['Zona 5'],        Ivan: ['Zona 4'], Pablo: ['Zona 1-2'], Dani: ['Zona 3'] },
  Viernes:   { Benito: ['Zona 6'],        Ivan: ['Zona 4'], Pablo: ['Zona 1-2'], Dani: ['Zona 3'] },
};
const DIAS_SEMANA_ES = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];

const ESTADO_CFG = {
  activo:                 { label: 'Activo',              bg: 'bg-green-100',  text: 'text-green-800',  dot: 'bg-green-500'  },
  firmado_activo:         { label: 'Activo',              bg: 'bg-green-100',  text: 'text-green-800',  dot: 'bg-green-500'  },
  sepa_generado:          { label: 'SEPA generado',       bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
  pendiente_factura:      { label: 'Pendiente factura',   bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  pendiente_agendar:      { label: 'Pendiente agendar',   bg: 'bg-cyan-100',   text: 'text-cyan-800',   dot: 'bg-cyan-500'   },
  mantenimiento_agendado: { label: 'Agendado',            bg: 'bg-blue-100',   text: 'text-blue-800',   dot: 'bg-blue-500'   },
  pendiente_renovar:      { label: 'Pendiente renovar',   bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
  pendiente_reagendar:    { label: 'Pendiente reagendar', bg: 'bg-amber-100',  text: 'text-amber-800',  dot: 'bg-amber-500'  },
  sepa_devuelto:          { label: 'SEPA devuelto',       bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  vence_pronto:           { label: 'Vence pronto',        bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  vencido:                { label: 'Vencido',             bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  sin_contrato:           { label: 'Sin contrato',        bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400'   },
  contrato_enviado:       { label: 'Enviado a firmar',    bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
  sin_respuesta:          { label: 'Sin respuesta',       bg: 'bg-gray-100',   text: 'text-gray-500',   dot: 'bg-gray-400'   },
  contactado:             { label: 'Contactado',          bg: 'bg-indigo-100', text: 'text-indigo-800', dot: 'bg-indigo-400' },
  interesado:             { label: 'Interesado',          bg: 'bg-cyan-100',   text: 'text-cyan-800',   dot: 'bg-cyan-500'   },
  no_interesado:          { label: 'No interesado',       bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-400'    },
  baja:                   { label: 'Baja',                bg: 'bg-gray-100',   text: 'text-gray-500',   dot: 'bg-gray-300'   },
};

// Estados que aparecen en el filtro de la tabla de contratos (excluye estados internos/legacy)
const ESTADOS_FILTRO = [
  'activo', 'vence_pronto', 'vencido',
  'sepa_generado', 'sepa_devuelto', 'pendiente_factura',
  'pendiente_agendar', 'mantenimiento_agendado', 'pendiente_renovar', 'pendiente_reagendar', 'baja',
];

// Estados que pertenecen al flujo de captación (kanban — no aparecen en tab Contratos)
const ESTADOS_CAPTACION = ['sin_contrato', 'contactado', 'interesado', 'sin_respuesta', 'contrato_enviado', 'no_interesado'];
// Estados de "lead frío" — cliente al que aún se le está ofreciendo el contrato.
// 'contrato_enviado' NO está aquí: es una fase legítima del embudo (enviado a firmar).
const ESTADOS_LEAD_FRIO = ['sin_contrato', 'contactado', 'interesado', 'sin_respuesta', 'no_interesado'];

// Leads de captación de verdad. Excluye:
//  · docs cuyo estado ya no es de captación, o son baja
//  · leads fríos de un cliente que YA tiene un contrato (activo o enviado a
//    firmar) en otro registro — no tiene sentido seguir "captándolo"
//  · docs con estado de lead pero con señales claras de contrato real
//    (firma, orden de mantenimiento, referencia o fecha de inicio)
function leadsCaptacion(data) {
  const clientesConContrato = new Set(); // por clienteId
  // Además de clienteId, por teléfono normalizado — cubre el caso en que el
  // contrato se creó con un clienteId distinto al del lead (p. ej. en el
  // wizard se dio de alta el cliente como "nuevo" en StelOrder por error, en
  // vez de seleccionar el ya existente), que si no dejaba el lead huérfano
  // en captación para siempre aunque el cliente ya tuviera contrato real.
  const telsConContrato = new Set();
  for (const m of data) {
    const tieneContratoReal = (!ESTADOS_CAPTACION.includes(m.estado) && m.estado !== 'baja') || m.estado === 'contrato_enviado';
    if (!tieneContratoReal) continue;
    if (m.clienteId) clientesConContrato.add(String(m.clienteId));
    const tel = normTelefonoCorto(m.clienteTelefono);
    if (tel) telsConContrato.add(tel);
  }
  return data.filter(m => {
    if (!ESTADOS_CAPTACION.includes(m.estado) || m.estado === 'baja') return false;
    if (ESTADOS_LEAD_FRIO.includes(m.estado)) {
      if (m.clienteId && clientesConContrato.has(String(m.clienteId))) return false;
      const tel = normTelefonoCorto(m.clienteTelefono);
      if (tel && telsConContrato.has(tel)) return false;
      if (m.woMantenimientoId || m.fechaFirma || m.referenciaContrato || m.fechaInicioContrato) return false;
    }
    return true;
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────


// Días de CALENDARIO (medianoche a medianoche local) — no la hora exacta del
// día, para que una fecha de HOY no se vea como "vencida" solo porque ya
// pasó el mediodía.
function hoyMedianoche() { const h = new Date(); h.setHours(0, 0, 0, 0); return h; }
function diasDesde(dateStr) { if (!dateStr) return 0; return Math.round((hoyMedianoche() - new Date(dateStr + 'T00:00:00')) / 86400000); }
function diasHasta(dateStr) { if (!dateStr) return 999; return Math.round((new Date(dateStr + 'T00:00:00') - hoyMedianoche()) / 86400000); }
function fmt(s)   { if (!s) return '—'; return new Date(s + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
function fmtL(s)  { if (!s) return '—'; return new Date(s + 'T12:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }); }
function hoy()      { return new Date().toISOString().split('T')[0]; }

// Tiempo transcurrido desde un instante concreto (no solo días de calendario
// como diasDesde) — para el reloj del contrato enviado: "hace 40 min" o
// "hace 3h" dice mucho más que "Hace 0d" si el enlace se mandó esta misma
// mañana. Acepta un timestamp ISO completo (contratoEnviadoAt) o, como
// reserva para contratos de antes de que ese campo existiera, una fecha
// simple YYYY-MM-DD (createdAt) — en ese caso se ancla a mediodía para no
// mostrar horas negativas por el huso horario.
function tiempoDesdeEnvio(iso) {
  if (!iso) return null;
  const fecha = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(iso + 'T12:00:00') : new Date(iso);
  if (isNaN(fecha.getTime())) return null;
  const minutos = Math.max(0, Math.round((Date.now() - fecha.getTime()) / 60000));
  if (minutos < 60)         return minutos <= 1 ? 'hace un momento' : `hace ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24)           return `hace ${horas}h`;
  const dias = Math.round(horas / 24);
  if (dias < 30)            return `hace ${dias}d`;
  return `hace ${Math.round(dias / 30)} ${Math.round(dias / 30) === 1 ? 'mes' : 'meses'}`;
}
function fmtEur0(n) { return Number(n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }); }
function en1Ano()   { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().split('T')[0]; }
function addMeses(dateStr, meses) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T12:00:00');
  d.setMonth(d.getMonth() + meses);
  return d.toISOString().split('T')[0];
}
// Clientes que ya se mantuvieron este año (Grupo A, detectado por calcularGruposMantenimiento.js
// a partir del histórico de StelOrder ya importado en Firebase) pero aún sin contrato firmado.
// Se les sugiere contactar 1 mes antes de cumplirse el año desde su último mantenimiento.
const MESES_AVISO_RENOVACION = 11;
function esClientePotencial(m) {
  return m.estado === 'sin_contrato' && m.grupo === 'A' && !!m.fechaUltimoMantenimiento;
}
function fechaContactoSugerida(m) {
  return esClientePotencial(m) ? addMeses(m.fechaUltimoMantenimiento, MESES_AVISO_RENOVACION) : null;
}
// Clientes potenciales "calientes": el técnico ha estado en su casa hace muy
// poco (mantenimiento reciente, no la renovación que toca dentro de meses).
// La relación está fresca — son los primeros a los que ofrecer el contrato,
// por delante del resto del Grupo A (que se ordena por fecha de renovación).
const DIAS_CALIENTE = 30;
function esClienteCaliente(m) {
  return esClientePotencial(m) && diasDesde(m.fechaUltimoMantenimiento) <= DIAS_CALIENTE;
}

// Contrato "en visita" (sin ciclo SEPA/factura) cuya próxima visita ya toca o
// está a ≤30 días, sin que haya nada agendado todavía. Es la ÚNICA función
// que decide esto — la usan tanto el KPI "Visita pendiente" como el badge en
// cada fila/ficha, para que cuenten y muestren siempre lo mismo. Independiente
// del estado de vencimiento del contrato (puede salir "Vencido" a la vez).
const DIAS_VISITA_PENDIENTE = 30;
function necesitaAgendarVisita(m) {
  return !!(m.tieneContrato && m.modoCobro === 'visita' &&
    !['pendiente_agendar', 'mantenimiento_agendado', 'pendiente_renovar', 'pendiente_reagendar'].includes(m.estado) &&
    !ESTADOS_CAPTACION.includes(m.estado) && m.estado !== 'baja' &&
    m.fechaProximoMantenimiento && diasHasta(m.fechaProximoMantenimiento) <= DIAS_VISITA_PENDIENTE);
}
function esMesActual(s) {
  if (!s) return false;
  const d = new Date(s + 'T12:00:00'), n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
}
function normTel(tel) {
  if (!tel) return '';
  let t = tel.replace(/\D/g, '');
  if (!t.startsWith('34') && t.length === 9) t = '34' + t;
  return t;
}
function generarMensaje(m, tipo) {
  const nombre     = m.clienteNombre?.split(' ')[0] || 'cliente';
  const packLabel  = PACKS[m.pack]?.label || 'contrato de mantenimiento';
  const precio     = (m.precioAnual || 0).toFixed(2).replace('.', ',');
  const servicios  = (m.serviciosIncluidos || []).join(', ') || 'mantenimiento anual';
  const formaPago  = m.modoCobro !== 'visita' ? 'domiciliación bancaria el 10 de enero' : 'se abona en la propia visita';
  if (tipo === 'captacion') {
    return `Hola ${nombre}, le contactamos de Climatización Zapata, SAT oficial Ariston.
Le ofrecemos el *${packLabel}* para su domicilio en ${m.direccion || m.municipio || 'su domicilio'}.
Incluye: ${servicios}.
Precio anual: *${precio}€* + IVA (${formaPago}).
¿Le interesa? Puede responder aquí o llamarnos.
_Climatización Zapata_`;
  }
  // Usar fecha de vencimiento o próximo mantenimiento, pero solo si es futura.
  // Si la fecha es pasada (contrato vencido/pendiente_renovar), no la mencionamos.
  const fechaRef  = m.fechaVencimientoContrato || m.fechaProximoMantenimiento;
  const esFutura  = fechaRef && fechaRef >= hoy();
  const fechaTexto = esFutura ? ` vence el *${fmtL(fechaRef)}* y` : '';
  return `Hola ${nombre}, le contactamos de Climatización Zapata.
Su *${packLabel}*${fechaTexto} tiene pendiente la revisión anual.
¿Le agendamos la visita? Incluye extensión de garantía y revisión completa del equipo.
Responda a este mensaje o llámenos.
_Climatización Zapata_`;
}
function generarMensajeKanban(item) {
  const nombre  = (item.clienteNombre || '').split(' ')[0] || 'cliente';
  const { estado } = item;
  const tel     = normTel(item.clienteTelefono || '');
  const es      = !tel || tel.startsWith('34');

  if (estado === 'sin_contrato') {
    const lugar = item.municipio ? (es ? ` en ${item.municipio}` : ` in ${item.municipio}`) : '';
    return es
      ? `Hola ${nombre}, le contactamos de Climatización Zapata, SAT oficial Ariston.\n\nTenemos registrado su equipo${lugar} y queremos ofrecerle un *contrato de mantenimiento anual* para garantizar su correcto funcionamiento.\n\n¿Le gustaría que le informáramos de las opciones disponibles?\n\n_Climatización Zapata_`
      : `Hi ${nombre}, we are contacting you from Climatización Zapata, official Ariston SAT.\n\nWe have your equipment registered${lugar} and would like to offer you an *annual maintenance contract* to ensure it works correctly.\n\nWould you be interested in learning about the available options?\n\n_Climatización Zapata_`;
  }
  if (estado === 'contactado' || estado === 'sin_respuesta') {
    return es
      ? `Hola ${nombre}, le escribimos de nuevo de Climatización Zapata.\n\nIntentamos contactarle hace unos días sobre el mantenimiento de su equipo. ¿Sigue teniendo interés?\n\nEstamos a su disposición para cualquier consulta.\n\n_Climatización Zapata_`
      : `Hi ${nombre}, we are following up from Climatización Zapata.\n\nWe tried to reach you a few days ago regarding the maintenance of your equipment. Are you still interested?\n\nWe are happy to answer any questions.\n\n_Climatización Zapata_`;
  }
  if (estado === 'interesado') {
    const packLabel = PACKS[item.pack]?.label || (es ? 'contrato de mantenimiento' : 'maintenance contract');
    const precio    = item.precioAnual ? `*${Number(item.precioAnual).toFixed(2).replace('.', ',')}€* + IVA` : (es ? 'a consultar' : 'to be confirmed');
    const servicios = (item.serviciosIncluidos || []).length > 0
      ? '\n' + item.serviciosIncluidos.map(s => `  · ${s}`).join('\n')
      : '';
    const formaPago = item.modoCobro !== 'visita'
      ? (es ? 'domiciliación bancaria el 10 de enero' : 'direct debit on January 10th')
      : (es ? 'se abona en la propia visita' : 'paid at the visit');
    return es
      ? `Hola ${nombre}, le contactamos de Climatización Zapata.\n\nComo habíamos comentado, le preparamos la propuesta del *${packLabel}*:${servicios}\n\nPrecio anual: ${precio} (${formaPago}).\n\n¿Desea que procedamos con el contrato?\n\n_Climatización Zapata_`
      : `Hi ${nombre}, we are contacting you from Climatización Zapata.\n\nAs discussed, here is our proposal for the *${packLabel}*:${servicios}\n\nAnnual price: ${precio} (${formaPago}).\n\nWould you like us to proceed with the contract?\n\n_Climatización Zapata_`;
  }
  if (estado === 'contrato_enviado') {
    return es
      ? `Hola ${nombre}, le contactamos de Climatización Zapata.\n\nLe recordamos que tiene pendiente la firma de su *contrato de mantenimiento anual*. Si tiene alguna duda sobre el proceso de firma o el contenido del contrato, estamos a su disposición.\n\n_Climatización Zapata_`
      : `Hi ${nombre}, we are contacting you from Climatización Zapata.\n\nThis is a reminder that your *annual maintenance contract* is pending your signature. If you have any questions about the signing process or the contract itself, please don't hesitate to ask.\n\n_Climatización Zapata_`;
  }
  return '';
}
async function saveApi(payload) {
  const r = await fetch('/.netlify/functions/saveMantenimiento', {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() }, body: JSON.stringify(payload),
  });
  const d = await r.json();
  if (!d.success) throw new Error(d.error);
  return d;
}
function exportarCSV(data) {
  const cols = ['Cliente','Teléfono','Email','Municipio','Tipo equipo','Marca','Modelo','Pack','Precio/año','Técnico','Estado','Próximo mant.','Vence contrato','Canal'];
  const rows = data.map(m => [
    m.clienteNombre, m.clienteTelefono, m.clienteEmail, m.municipio,
    TIPOS_EQUIPO[m.tipoEquipo] || m.tipoEquipo, m.marca, m.modelo,
    PACKS[m.pack]?.label || m.pack, m.precioAnual, m.tecnicoAsignado,
    ESTADO_CFG[m.estado]?.label, m.fechaProximoMantenimiento, m.fechaVencimientoContrato,
    CANALES[m.canalCaptacion] || m.canalCaptacion,
  ]);
  const csv = [cols, ...rows].map(r => r.map(c => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })),
    download: `mantenimientos_${hoy()}.csv`,
  });
  a.click();
}

// ─── UI compartida ────────────────────────────────────────────────────────────

function EstadoBadge({ estado }) {
  const c = ESTADO_CFG[estado] || ESTADO_CFG.sin_contrato;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}
// Aviso independiente del estado de vencimiento — un contrato puede salir
// "Vencido" y a la vez necesitar que se agende su visita (son preguntas
// distintas), así que este badge se muestra en paralelo, nunca en su lugar.
function VisitaPendienteBadge({ item }) {
  if (!necesitaAgendarVisita(item)) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-700" title="Contrato en visita con la próxima visita sin agendar">
      📅 Visita pendiente
    </span>
  );
}
function PackBadge({ pack }) {
  const p = PACKS[pack];
  if (!p) return <span className="text-gray-400 text-xs">—</span>;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.cls}`}>{p.label}</span>;
}

// ─── Tareas pendientes de un contrato ────────────────────────────────────────
// Una sola función decide QUÉ hay que hacer con un contrato y CÓN QUÉ URGENCIA.
// La usan a la vez: (1) el chip de tarea de cada fila —visualmente distinto del
// badge de estado, que dice en qué situación está el contrato, no qué hacer— y
// (2) el orden por defecto de la lista (contratos con tareas arriba, y sus
// tareas de más a menos importantes). `prio` menor = más urgente.
function tareasDeContrato(m) {
  const t = [];
  const push = (prio, tono, label) => t.push({ prio, tono, label });
  const dProx = m.fechaProximoMantenimiento ? diasHasta(m.fechaProximoMantenimiento) : null;

  // Dinero parado — lo más grave
  if (m.estadoPago === 'devuelto' || m.estado === 'sepa_devuelto') push(10, 'critico', 'Recibo SEPA devuelto');
  if (m.facturaError) push(15, 'critico', 'Error al facturar');

  // Visita de mantenimiento
  if (m.estado === 'pendiente_reagendar') push(20, 'critico', 'Visita agendada sin realizar');
  if (m.estado === 'pendiente_agendar') {
    if (dProx != null && dProx <= 0)       push(25, 'critico', `Visita vencida ${Math.abs(dProx)} d — agendar`);
    else if (dProx != null && dProx <= 15) push(35, 'alto',    `Agendar visita · ${dProx} d`);
    else                                   push(45, 'medio',   dProx != null ? `Agendar visita · ${dProx} d` : 'Agendar visita');
  }
  if (necesitaAgendarVisita(m)) {
    if (dProx != null && dProx <= 0) push(28, 'critico', `Visita vencida ${Math.abs(dProx)} d (en visita)`);
    else                            push(48, 'medio',   dProx != null ? `Agendar visita (en visita) · ${dProx} d` : 'Agendar visita (en visita)');
  }

  // Renovación
  if (m.estado === 'vencido')            push(30, 'critico', 'Contrato vencido — renovar');
  if (m.estado === 'pendiente_renovar')  push(40, 'alto',    'Renovado — falta agendar visita');
  if (m.estado === 'vence_pronto')       push(60, 'medio',   'Vence pronto — avisar cliente');

  // Firma pendiente
  if (m.estado === 'contrato_enviado' && diasDesde(m.createdAt) > 7)
    push(50, 'alto', `Sin firmar hace ${diasDesde(m.createdAt)} d`);

  // Datos incompletos — no urgente, pero hay que resolverlo
  if (m.extensionGarantia && !m.idContratoAriston) push(80, 'bajo', 'Falta ID contrato Ariston');
  if (m.tieneContrato && !m.fechaVencimientoContrato) push(85, 'bajo', 'Falta fecha de vencimiento');

  return t.sort((a, b) => a.prio - b.prio);
}
function prioridadContrato(m) {
  const t = tareasDeContrato(m);
  return t.length ? t[0].prio : 9999;
}

const TAREA_TONO = {
  critico: 'border-red-500 bg-red-50 text-red-700',
  alto:    'border-orange-400 bg-orange-50 text-orange-700',
  medio:   'border-amber-400 bg-amber-50 text-amber-700',
  bajo:    'border-slate-300 bg-slate-50 text-slate-600',
};
// Solo las tareas realmente urgentes tiñen la fila entera; el resto se ve solo
// en el chip, para que el tinte de fila siga significando "atención ya".
const TAREA_ROW = { critico: 'bg-red-50 hover:bg-red-100', alto: 'bg-orange-50 hover:bg-orange-100', medio: '', bajo: '' };

function TareaChip({ tono, label }) {
  return (
    <span className={`inline-flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-md border-l-[3px] text-[11px] font-semibold leading-tight ${TAREA_TONO[tono] || TAREA_TONO.bajo}`}>
      <span className="text-[8px] opacity-60">▸</span>{label}
    </span>
  );
}
function TareasCelda({ m, max = 3 }) {
  const tareas = tareasDeContrato(m);
  if (tareas.length === 0) return <span className="text-xs text-gray-300">—</span>;
  return (
    <div className="flex flex-col gap-1 items-start">
      {tareas.slice(0, max).map((t, i) => <TareaChip key={i} tono={t.tono} label={t.label} />)}
      {tareas.length > max && <span className="text-[10px] text-gray-400 pl-1">+{tareas.length - max} más</span>}
    </div>
  );
}
function KPICard({ icon, label, value, sub, colorBg, colorIcon, alert, onClick }) {
  return (
    <button onClick={onClick} className={`text-left bg-white rounded-2xl border shadow-sm p-4 hover:shadow-md transition-shadow w-full ${alert ? 'ring-2 ring-red-400' : ''} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${colorBg}`}>
        <span className={colorIcon}>{icon}</span>
      </div>
      <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
      <p className="text-xs font-medium text-gray-600 mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </button>
  );
}
function Paginacion({ total, pagina, onChange, porPagina = POR_PAGINA }) {
  const paginas = Math.ceil(total / porPagina);
  if (paginas <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 py-4 border-t border-gray-100">
      <button disabled={pagina === 0} onClick={() => onChange(pagina - 1)}
        className="px-3 py-1 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40 transition-colors">
        ← Anterior
      </button>
      <span className="text-sm text-gray-500">{pagina + 1} / {paginas} &middot; {total} registros</span>
      <button disabled={pagina >= paginas - 1} onClick={() => onChange(pagina + 1)}
        className="px-3 py-1 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-40 transition-colors">
        Siguiente →
      </button>
    </div>
  );
}
function Field({ label, value }) {
  if (!value || value === '—') return null;
  return (
    <div className="flex text-sm gap-2">
      <span className="text-gray-400 w-28 flex-shrink-0 leading-snug">{label}</span>
      <span className="text-gray-800 font-medium flex-1 leading-snug">{value}</span>
    </div>
  );
}
function Seccion({ title, children }) {
  return (
    <div>
      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

// Mini-tarjeta de dato para la cabecera del panel — el puñado de cifras que
// un coordinador quiere ver sin hacer scroll (próximo mant., técnico, precio…).
function MiniStat({ label, value, sub, tone }) {
  const toneCls = tone === 'alert' ? 'text-red-600' : tone === 'warn' ? 'text-amber-600' : 'text-gray-900';
  return (
    <div className="bg-gray-50 rounded-lg px-2.5 py-1.5 min-w-0">
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide truncate">{label}</div>
      <div className={`text-sm font-bold leading-tight truncate ${toneCls}`}>{value || '—'}</div>
      {sub && <div className="text-[10px] text-gray-400 leading-tight truncate">{sub}</div>}
    </div>
  );
}

// ─── Equipos del contrato ────────────────────────────────────────────────────
// Unifica la forma de las tres fuentes posibles de activos (getAssetsByAccount
// para cliente con ID numérico de StelOrder, getActivos para UUID del esquema
// viejo, getAssetsByIds como último recurso por assetIds).
function normalizarEquipo(a) {
  return {
    id:             String(a.id ?? a._firebaseId ?? a.stelorderId ?? ''),
    marca:          a.brand || a.marca || '',
    modelo:         a.model || a.modelo || '',
    nombre:         a.name || a.nombre || '',
    descripcion:    a.description || a.descripcion || '',
    serie:          a.serialNumber || a['serial-number'] || a.numeroSerie || '',
    garantiaInicio: a.warrantyStartDate || a['warranty-start-date'] || a.fechaInstalacion || null,
    garantiaFin:    a.warrantyEndDate || a['warranty-end-date'] || a.fechaFinGarantia || null,
    mantenimiento:  a['subject-to-maintenance'] ?? a.mantenimiento ?? null,
    referencia:     a.fullReference || a.reference || null,
  };
}

function useEquiposContrato(item, activo) {
  const [equipos, setEquipos] = useState(null);   // null = aún sin cargar
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const cid = item.clienteId != null ? String(item.clienteId) : '';
  const idsFallback = (item.assetIds?.length ? item.assetIds : (item.assetId ? [item.assetId] : [])).map(String);

  useEffect(() => {
    if (!activo || equipos !== null || loading) return;
    let url;
    if (/^\d+$/.test(cid))  url = `/.netlify/functions/getAssetsByAccount?accountId=${cid}`;
    else if (cid)           url = `/.netlify/functions/getActivos?clienteId=${encodeURIComponent(cid)}&soloActivos=false`;
    else if (idsFallback.length) url = `/.netlify/functions/getAssetsByIds?ids=${idsFallback.join(',')}`;
    else { setEquipos([]); return; }

    setLoading(true); setError(null);
    fetch(url, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => {
        if (d && d.success === false) throw new Error(d.error || 'No se pudieron cargar los equipos');
        const list = (d.assets || d.activos || []).map(normalizarEquipo).filter(e => e.id);
        setEquipos(list);
      })
      .catch(e => { setError(e.message); setEquipos([]); })
      .finally(() => setLoading(false));
  }, [activo, cid]); // eslint-disable-line

  return { equipos, loading, error };
}

// "En garantía / fuera de garantía" a partir de la fecha de fin.
function estadoGarantia(fin) {
  if (!fin) return null;
  const dias = diasHasta(fin);
  if (dias < 0)    return { txt: `Fuera de garantía · caducó ${fmt(fin)}`, cls: 'bg-gray-100 text-gray-500' };
  if (dias <= 90)  return { txt: `En garantía · caduca ${fmt(fin)} (${dias} d)`, cls: 'bg-amber-100 text-amber-700' };
  return { txt: `En garantía · hasta ${fmt(fin)}`, cls: 'bg-green-100 text-green-700' };
}

function EquipoCard({ eq, cubierto, mostrarCobertura }) {
  const g = estadoGarantia(eq.garantiaFin);
  const titulo = `${eq.marca} ${eq.modelo}`.trim() || eq.nombre || `Equipo ${eq.id}`;
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${cubierto ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-bold text-gray-800 text-sm leading-tight">{titulo}</span>
        {mostrarCobertura && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${cubierto ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
            {cubierto ? 'En el contrato' : 'Otro del cliente'}
          </span>
        )}
      </div>
      <div className="mt-1 space-y-0.5 text-xs text-gray-500">
        {eq.serie && <div>S/N <span className="text-gray-700 font-medium">{eq.serie}</span></div>}
        {(eq.nombre || eq.descripcion) && eq.nombre !== titulo && <div className="truncate">{eq.nombre || eq.descripcion}</div>}
        <div className="text-gray-300">StelOrder #{eq.id}</div>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {g
          ? <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${g.cls}`}>{g.txt}</span>
          : <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400">Sin datos de garantía</span>}
        {cubierto && eq.mantenimiento === false && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700"
            title="StelOrder no marca este equipo como sujeto a mantenimiento">⚠ StelOrder no lo marca p/ mant.</span>
        )}
      </div>
    </div>
  );
}

// ─── Cronología del contrato (timeline unificado) ────────────────────────────
function TimelineWO({ wo, onClick }) {
  const titulo  = wo['full-reference'] || `WO #${wo.id}`;
  const estado  = WO_ESTADO_LABEL[wo['document-state-id']] || '';
  const importe = wo.amount != null ? `${Number(wo.amount).toFixed(2)}€` : '';
  const tecnico = WO_TECNICO_POR_ID[wo['assignee-id'] || wo['agent-id']] || '';
  const notas   = wo['private-comments'] || wo.comments || '';
  return (
    <div onClick={onClick}
      className="bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 text-sm cursor-pointer hover:border-orange-300 transition-colors">
      <div className="flex justify-between items-start gap-2">
        <span className="font-semibold text-gray-800 leading-tight">{titulo}</span>
        {importe && <span className="text-xs font-bold text-orange-700 shrink-0">{importe}</span>}
      </div>
      <div className="flex gap-2 flex-wrap mt-0.5">
        {estado  && <span className="text-xs text-gray-500">{estado}</span>}
        {tecnico && <span className="text-xs text-gray-500">· {tecnico}</span>}
      </div>
      {notas && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notas}</p>}
    </div>
  );
}
function TimelineContacto({ c }) {
  const res = RESULTADOS_LLAMADA.find(r => r.value === c.resultado);
  return (
    <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-sm">
      <div className="flex justify-between items-center gap-2">
        <span className="font-semibold text-gray-800">Contacto</span>
        {res && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.resultado === 'firmado' ? 'bg-green-100 text-green-700' : c.resultado === 'no_interesado' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{res.label}</span>}
      </div>
      {c.nota && <p className="text-xs text-gray-500 mt-0.5">{c.nota}</p>}
      {c.proximoContacto && <p className="text-xs text-gray-400 mt-0.5">Próx. contacto: {fmt(c.proximoContacto)}</p>}
    </div>
  );
}
function TimelineRenovacion({ r }) {
  return (
    <div className="bg-purple-50 border border-purple-100 rounded-xl px-3 py-2 text-sm">
      <span className="font-semibold text-gray-800">Contrato renovado</span>
      <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
        {r.fechaVencimientoAnterior && <div>Vencía el {fmt(r.fechaVencimientoAnterior)}</div>}
        {r.diasDesdeVencimiento != null && (
          <div>{r.diasDesdeVencimiento > 0 ? `Renovado ${r.diasDesdeVencimiento} d tras vencer` : 'Renovado antes de vencer'}</div>
        )}
        {r.precioAnualAnterior != null && <div>Precio anterior: {Number(r.precioAnualAnterior).toFixed(0)}€/año</div>}
        {r.modoCobroAnterior && <div>Cobro anterior: {r.modoCobroAnterior === 'visita' ? 'en visita' : 'SEPA'}</div>}
      </div>
    </div>
  );
}
function Timeline({ eventos, onWo }) {
  const DOT = { wo: 'bg-orange-400', mant: 'bg-green-400', contacto: 'bg-blue-400', renovacion: 'bg-purple-400' };
  return (
    <div>
      {eventos.map((e, i) => (
        <div key={i} className="flex gap-3 pb-3 last:pb-0">
          <div className="flex flex-col items-center pt-1">
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${DOT[e.tipo] || 'bg-gray-300'}`} />
            {i < eventos.length - 1 && <span className="w-px flex-1 bg-gray-200 mt-1" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-gray-400 font-semibold mb-0.5">{e.fecha ? fmt(e.fecha) : 'Sin fecha'}</div>
            {e.tipo === 'wo' && <TimelineWO wo={e.wo} onClick={() => onWo(e.wo)} />}
            {e.tipo === 'mant' && (
              <div className="bg-green-50 border border-green-100 rounded-xl px-3 py-2 text-sm">
                <span className="font-semibold text-gray-800">Mantenimiento realizado</span>
                {e.tecnico && <span className="text-xs text-gray-500 ml-2">{e.tecnico}</span>}
                {e.notas && <p className="text-xs text-gray-500 mt-0.5">{e.notas}</p>}
              </div>
            )}
            {e.tipo === 'contacto' && <TimelineContacto c={e} />}
            {e.tipo === 'renovacion' && <TimelineRenovacion r={e} />}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Popup de detalle de workorder (solo lectura) ────────────────────────────
function WorkorderDetalleModal({ wo, onClose }) {
  const [assets,        setAssets]        = useState([]);
  const [loadingAssets, setLoadingAssets]  = useState(false);

  useEffect(() => {
    const ids = (wo.assets || []).map(a => a?.id).filter(Boolean);
    if (ids.length === 0) { setAssets([]); return; }
    setLoadingAssets(true);
    fetch(`/.netlify/functions/getAssetsByIds?ids=${ids.join(',')}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => setAssets(d.assets || []))
      .catch(() => setAssets([]))
      .finally(() => setLoadingAssets(false));
  }, [wo]);

  const fecha       = (wo['date'] || wo['start-date'] || wo['creation-date'] || '').split('T')[0];
  const titulo      = wo['full-reference'] || `WO #${wo.id}`;
  const estado      = WO_ESTADO_LABEL[wo['document-state-id']] || '';
  const tecnico     = WO_TECNICO_POR_ID[wo['assignee-id'] || wo['agent-id']] || '';
  const importe     = wo['amount'] != null ? `${Number(wo['amount']).toFixed(2)}€` : '';
  const notas       = wo['private-comments'] || '';
  const comentarios = wo['comments'] || '';
  const descripcion = wo['description'] || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h3 className="font-bold text-gray-900">{titulo}</h3>
            <p className="text-xs text-gray-400">Solo lectura</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha"    value={fmt(fecha)} />
            <Field label="Estado"   value={estado} />
            <Field label="Técnico"  value={tecnico} />
            <Field label="Importe"  value={importe} />
          </div>

          {(descripcion || notas || comentarios) && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Comentarios</p>
              <div className="space-y-1.5">
                {descripcion && <p className="text-sm text-gray-700 whitespace-pre-wrap">{descripcion}</p>}
                {notas       && <p className="text-sm text-gray-700 whitespace-pre-wrap">{notas}</p>}
                {comentarios && <p className="text-sm text-gray-700 whitespace-pre-wrap">{comentarios}</p>}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Equipo</p>
            {loadingAssets && <p className="text-sm text-gray-400 animate-pulse">Cargando…</p>}
            {!loadingAssets && assets.length === 0 && <p className="text-sm text-gray-300">Sin equipo vinculado</p>}
            <div className="space-y-1.5">
              {assets.map(a => (
                <div key={a.id} className="bg-gray-50 rounded-xl px-3 py-2 text-sm">
                  <p className="font-semibold text-gray-800">{[a.brand, a.model].filter(Boolean).join(' ') || a.name || `Activo #${a.id}`}</p>
                  {a.serialNumber && <p className="text-xs text-gray-400">Nº serie: {a.serialNumber}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-bold rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Panel lateral ────────────────────────────────────────────────────────────

function SidePanel({ item, onClose, onRefresh, canEditPrecios, onNuevoContrato }) {
  const { user }      = useAuth();
  // Mismo criterio de "admin" que el resto de la pantalla (MantenimientosView
  // trata a coordinador_operaciones como admin para precios/tabs/Kanban) —
  // antes este panel solo miraba role==='admin', dejando a coordinador sin
  // dar de baja/eliminar/revertir aunque sí puede en el resto de la app.
  const isAdmin       = user?.role === 'admin' || user?.role === 'coordinador_operaciones';
  const [accion,      setAccion]    = useState(null);
  const [panelTab,    setPanelTab]  = useState('detalles');
  const [loading,     setLoading]   = useState(false);
  const [pdfLoading,  setPdfLoading] = useState(null); // 'ver' | 'descargar' | null
  // Workorders StelOrder
  const [woLoading,   setWoLoading] = useState(false);
  const [woList,      setWoList]    = useState(null); // null = no cargado aún
  const [woDebug,     setWoDebug]   = useState(null);
  const [woDetalle,   setWoDetalle] = useState(null); // workorder abierta en el popup de detalle
  const [msg,         setMsg]       = useState({ type: '', text: '' });
  // Agendar — si fechaProximoMantenimiento ya pasó (caso "Pendiente reagendar",
  // visita agendada que no se hizo), no se precarga: si el usuario no se fija
  // y pulsa directamente, se reenviaría la misma fecha ya vencida a StelOrder.
  const [fechaWO,     setFechaWO]   = useState(() => {
    const f = item.fechaProximoMantenimiento;
    return (f && f >= hoy()) ? f : '';
  });
  const [woCreado,    setWoCreado]  = useState(null); // null | { fecha }
  // Renovar
  // Renovar = +1 año sobre el vencimiento ANTERIOR (o sobre hoy si ya venció
  // hace mucho / no hay fecha). El usuario puede ajustarlo en el input.
  const [nuevaFin,    setNuevaFin]  = useState(() =>
    addMeses(item.fechaVencimientoContrato && item.fechaVencimientoContrato >= hoy() ? item.fechaVencimientoContrato : hoy(), 12));
  const [domiciliarRenovacion, setDomiciliarRenovacion] = useState(false);
  // Pack
  const [nuevoPack,   setNuevoPack] = useState(item.pack || 'basico');
  // WA
  const [tipoMsg,     setTipoMsg]   = useState(item.estado === 'sin_contrato' ? 'captacion' : 'vencimiento');
  const [waCopiado,   setWaCopiado] = useState(false);
  // Reenviar enlace firma
  const [enlaceUrl,       setEnlaceUrl]       = useState(null);
  const [enlaceIsNew,     setEnlaceIsNew]     = useState(false);
  const [enlaceLoading,   setEnlaceLoading]   = useState(false);
  const [enlaceCopiado,   setEnlaceCopiado]   = useState(false);
  // Baja modal
  const [showBajaModal,   setShowBajaModal]   = useState(false);
  const [motivoBaja,      setMotivoBaja]      = useState('');
  // ID contrato Ariston
  const [aristonEdit,     setAristonEdit]     = useState(false);
  const [aristonId,       setAristonId]       = useState(item.idContratoAriston || '');
  // Re-vincular a otra orden de StelOrder (cuando la guardada se borró y se
  // creó una nueva en su lugar)
  const [woEdit,          setWoEdit]          = useState(false);
  const [woIdInput,       setWoIdInput]       = useState('');
  const [woVinculando,    setWoVinculando]    = useState(false);
  // Edición admin de tipo de cobro y fecha del próximo mantenimiento
  const [cobroEdit,   setCobroEdit] = useState(false);
  const [cobroVal,    setCobroVal]  = useState(item.modoCobro === 'sepa' ? 'sepa' : 'visita');
  const [fmEdit,      setFmEdit]     = useState(false);
  const [fmVal,       setFmVal]      = useState(item.fechaProximoMantenimiento || '');
  const [vcEdit,      setVcEdit]     = useState(false);
  const [vcVal,       setVcVal]      = useState(item.fechaVencimientoContrato || '');
  // Ampliar contrato
  const [showAmpliacion,  setShowAmpliacion]  = useState(false);

  // Equipos del contrato — se cargan al abrir el panel (respuesta cacheada
  // 30 min en el servidor) para poder mostrar el nº en la cabecera y que la
  // pestaña Equipos sea instantánea.
  const { equipos, loading: equiposLoading, error: equiposError } = useEquiposContrato(item, true);
  const coveredIds = React.useMemo(() => {
    const ids = (item.assetIds?.length ? item.assetIds : (item.assetId ? [item.assetId] : [])).map(String);
    return new Set(ids);
  }, [item.assetIds, item.assetId]);
  const equiposOrden = React.useMemo(() => {
    if (!equipos) return null;
    return [...equipos].sort((a, b) => (coveredIds.has(b.id) ? 1 : 0) - (coveredIds.has(a.id) ? 1 : 0));
  }, [equipos, coveredIds]);

  const toggle = (a) => { setAccion(v => v === a ? null : a); setMsg({ type: '', text: '' }); };

  const loadWorkorders = React.useCallback(async (force = false) => {
    if ((woList !== null && !force) || woLoading) return;
    setWoLoading(true);
    setWoList(null);
    try {
      const params = new URLSearchParams();
      if (item.clienteId)            params.set('clienteId', item.clienteId);
      else if (item.clienteTelefono) params.set('phone', item.clienteTelefono);
      const resp = await fetch(`/.netlify/functions/buscarWorkordersStelOrder?${params}`, { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      console.log('[StelOrder WO debug]', data.debug, 'total:', data.workorders?.length);
      setWoList(data.workorders || []);
      setWoDebug(data.debug || null);
    } catch (e) { setWoList([]); setWoDebug([e.message]); }
    finally  { setWoLoading(false); }
  }, [item.clienteId, item.clienteTelefono, woList, woLoading]);

  React.useEffect(() => {
    if (panelTab === 'historial') loadWorkorders();
  }, [panelTab]); // eslint-disable-line

  // Cronología unificada: workorders de StelOrder + mantenimientos internos +
  // contactos + renovaciones, en un solo hilo ordenado por fecha desc. Antes
  // eran tres listas apiladas sin orden entre sí y las renovaciones no se veían.
  const eventos = React.useMemo(() => {
    const ev = [];
    for (const wo of (woList || [])) {
      ev.push({ tipo: 'wo', fecha: (wo.date || wo['start-date'] || wo['creation-date'] || '').split('T')[0], wo });
    }
    for (const h of (item.historialMantenimientos || [])) ev.push({ tipo: 'mant', fecha: h.fecha, tecnico: h.tecnico, notas: h.notas });
    for (const c of (item.historialContactos || []))      ev.push({ tipo: 'contacto', ...c });
    for (const r of (item.historialRenovaciones || []))   ev.push({ tipo: 'renovacion', ...r });
    return ev.sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || '')));
  }, [woList, item.historialMantenimientos, item.historialContactos, item.historialRenovaciones]);

  // getContratoPDF.js exige sesión (DNI/IBAN/firma del cliente en el PDF) —
  // por eso ya no puede ser un <a href> plano, que no manda cabeceras. Se
  // trae como blob autenticado y se abre/descarga desde ahí.
  const abrirContratoPDF = async (modo) => {
    setPdfLoading(modo);
    try {
      const resp = await fetch(`/.netlify/functions/getContratoPDF?id=${item.id}`, { headers: authService.authHeader() });
      if (!resp.ok) { const d = await resp.json().catch(() => ({})); throw new Error(d.error || 'No se pudo obtener el PDF'); }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (modo === 'ver') {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `contrato_${item.clienteNombre?.replace(/\s+/g, '_') || 'mantenimiento'}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setPdfLoading(null);
    }
  };

  const run = async (fn) => {
    setLoading(true); setMsg({ type: '', text: '' });
    try {
      await fn();
      setMsg({ type: 'ok', text: 'Guardado correctamente' });
      onRefresh();
      setTimeout(() => { setAccion(null); setMsg({ type: '', text: '' }); }, 1800);
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally     { setLoading(false); }
  };

  // Ref (no state) para bloquear un doble clic real: dos clics casi
  // simultáneos disparan ambos handlers antes de que React re-renderice el
  // botón con disabled={loading}, y cada uno crearía su propia WO en
  // StelOrder. La ref se marca de forma síncrona, antes de cualquier await.
  const agendandoRef = React.useRef(false);

  const agendarWO = async () => {
    if (agendandoRef.current) return;
    agendandoRef.current = true;
    setLoading(true); setMsg({ type: '', text: '' });
    try {
      if (!item.clienteId) throw new Error('Falta ID de cliente StelOrder. Edita el registro primero.');
      if (!fechaWO)        throw new Error('Selecciona una fecha');
      if (item.tecnicoAsignado && TECNICOS_IDS[item.tecnicoAsignado] == null)
        throw new Error(`El técnico "${item.tecnicoAsignado}" no tiene ID en StelOrder. Añade su ID en TECNICOS_IDS para poder asignarle la orden de trabajo.`);
      const resp = await fetch('/.netlify/functions/createMantenimientoWorkOrder', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          mantenimientoId:    item.id,
          clienteId:          item.clienteId,
          addressId:          item.addressId,
          tecnicoId:          TECNICOS_IDS[item.tecnicoAsignado],
          modelo:             `${item.marca || ''} ${item.modelo || ''}`.trim(),
          serialNumber:       item.numeroSerie,
          clienteNombre:      item.clienteNombre,
          clienteDni:         item.clienteDni || '',
          fechaVisita:        fechaWO,
          pack:               PACKS[item.pack]?.label || item.pack || '',
          serviciosIncluidos: item.serviciosIncluidos || [],
          precioAnual:        item.precioAnual || 0,
          assetIds:           item.assetIds?.length > 0 ? item.assetIds : (item.assetId ? [item.assetId] : []),
        }),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error);
      setWoCreado({ fecha: fechaWO });
      onRefresh();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally     { setLoading(false); agendandoRef.current = false; }
  };

  const confirmarWoUrgente = async () => {
    if (agendandoRef.current) return;
    agendandoRef.current = true;
    setLoading(true); setMsg({ type: '', text: '' });
    try {
      const resp = await fetch('/.netlify/functions/createMantenimientoWorkOrder', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ mantenimientoId: item.id, clienteId: item.clienteId }),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error);
      setWoCreado({ fecha: item.fechaProximoMantenimiento });
      onRefresh();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally     { setLoading(false); agendandoRef.current = false; }
  };

  const renovarContrato = () => {
    if (!window.confirm(`¿Renovar el contrato de ${item.clienteNombre} hasta el ${fmt(nuevaFin)}? Se actualizan las fechas del contrato y se borra cualquier visita agendada actualmente.`)) return;
    return run(async () => {
      // Historial de renovaciones — no existía ningún rastro antes de esto (cada
      // renovación sobreescribía las fechas del propio documento). Se usa para
      // analítica: tasa de renovación real y tiempo medio vencido→renovado.
      // Empieza a acumular datos desde ahora, no hay forma de reconstruir el pasado.
      const entradaHistorial = {
        fecha: hoy(),
        fechaVencimientoAnterior: item.fechaVencimientoContrato || null,
        precioAnualAnterior: item.precioAnual || 0,
        modoCobroAnterior: item.modoCobro === 'visita' ? 'visita' : 'sepa',
        diasDesdeVencimiento: item.fechaVencimientoContrato ? diasDesde(item.fechaVencimientoContrato) : null,
      };
      await saveApi({
        id: item.id, estado: 'pendiente_renovar', tieneContrato: true,
        // NO se toca fechaInicioContrato — el contrato mantiene su fecha de
        // alta original; la renovación solo extiende el vencimiento.
        fechaVencimientoContrato: nuevaFin, fechaProximoMantenimiento: nuevaFin,
        woMantenimientoId: null, woMantenimientoRef: null, woAgendadaUrgente: false,
        vencidoEmailsEnviados: 0,
        historialRenovaciones: [...(item.historialRenovaciones || []), entradaHistorial],
        ...(domiciliarRenovacion ? { modoCobro: 'sepa' } : {}),
      });
    });
  };

  const cambiarPack = () => {
    if (!window.confirm(`¿Cambiar el pack de ${item.clienteNombre} de "${PACKS[item.pack]?.label || item.pack || '—'}" a "${PACKS[nuevoPack]?.label || nuevoPack}"? Esto puede afectar al precio del contrato.`)) return;
    return run(async () => {
      await saveApi({ id: item.id, pack: nuevoPack });
    });
  };

  const guardarAriston = () => run(async () => {
    await saveApi({ id: item.id, idContratoAriston: aristonId.trim() });
    setAristonEdit(false);
  });

  const guardarCobro = () => run(async () => {
    await saveApi({ id: item.id, modoCobro: cobroVal });
    setCobroEdit(false);
  });

  // La fecha del próximo mantenimiento solo se edita a mano cuando NO hay una
  // orden vinculada — con orden, la fecha la fija la orden (createMantenimiento
  // WorkOrder / vincularOrdenContrato ya escriben fechaProximoMantenimiento =
  // fecha de la visita).
  const guardarFechaMant = () => run(async () => {
    if (item.woMantenimientoId) throw new Error('Hay una orden vinculada — la fecha la marca la orden.');
    if (!fmVal) throw new Error('Selecciona una fecha');
    await saveApi({ id: item.id, fechaProximoMantenimiento: fmVal });
    setFmEdit(false);
  });

  // La fecha de vencimiento del contrato la puede corregir a mano un admin
  // (es inicio + 1 año, independiente de las visitas — a veces hay que
  // ajustarla en casos heredados o acuerdos puntuales).
  const guardarVencimiento = () => run(async () => {
    if (!vcVal) throw new Error('Selecciona una fecha');
    await saveApi({ id: item.id, fechaVencimientoContrato: vcVal });
    setVcEdit(false);
  });

  // Re-vincular el contrato a otra orden de StelOrder — para cuando la orden
  // guardada se borró en StelOrder y se creó una nueva en su lugar (el
  // borrado no se detecta solo). Comprueba que la orden exista de verdad y
  // trae su fecha, así el contrato queda al día en un solo paso.
  const vincularOrden = async () => {
    if (!woIdInput.trim()) return;
    setWoVinculando(true); setMsg({ type: '', text: '' });
    try {
      const resp = await fetch('/.netlify/functions/vincularOrdenContrato', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ mantenimientoId: item.id, woId: woIdInput.trim() }),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error);
      setWoEdit(false); setWoIdInput('');
      setMsg({ type: 'ok', text: `Vinculado a la orden ${d.woMantenimientoRef}` });
      onRefresh();
      setTimeout(() => setMsg({ type: '', text: '' }), 2500);
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally { setWoVinculando(false); }
  };

  const copiarWA = () => {
    navigator.clipboard.writeText(generarMensaje(item, tipoMsg));
    setWaCopiado(true);
    setTimeout(() => setWaCopiado(false), 2000);
  };
  const abrirWA = () => {
    const tel = normTel(item.clienteTelefono);
    if (!tel) return alert('Sin teléfono registrado');
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(generarMensaje(item, tipoMsg))}`, '_blank', 'noopener,noreferrer');
  };

  const fetchEnlace = async () => {
    setEnlaceLoading(true); setEnlaceUrl(null); setMsg({ type: '', text: '' });
    try {
      const r = await fetch('/.netlify/functions/reenviarEnlaceContrato', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          contratoId: item.id,
          contratoData: {
            clienteNombre: item.clienteNombre, clienteEmail: item.clienteEmail,
            clienteTelefono: item.clienteTelefono, clienteDni: item.clienteDni,
            direccion: item.direccion, municipio: item.municipio,
            marca: item.marca, modelo: item.modelo, numeroSerie: item.numeroSerie,
            serviciosIncluidos: item.serviciosIncluidos || [],
            pack: item.pack || '',
            precioAnual: item.precioAnual || 0,
            tipoServicio: item.tipoServicio || 'siguiente_anio',
            referenciaMandatoSepa: item.referenciaMandatoSepa || '',
          },
        }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error);
      setEnlaceUrl(d.url);
      setEnlaceIsNew(d.isNew);
    } catch (e) { setMsg({ type: 'err', text: e.message }); setAccion(null); }
    finally { setEnlaceLoading(false); }
  };

  const handleDarBaja = () => { setMotivoBaja(''); setShowBajaModal(true); };
  const confirmarBaja = async () => {
    setShowBajaModal(false);
    setLoading(true); setMsg({ type: '', text: '' });
    try {
      await saveApi({ id: item.id, estado: 'baja', motivoBaja: motivoBaja.trim() || 'No especificado', updatedAt: hoy() });
      setMsg({ type: 'ok', text: 'Contrato dado de baja' });
      onRefresh();
      setTimeout(() => { onClose(); }, 1800);
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally { setLoading(false); }
  };

  const handleRevertir = async (accion) => {
    const mensajes = {
      revertir_sepa_generado:    `¿Revertir este contrato a su estado anterior (antes de la remesa SEPA)? El contrato volverá a estar disponible para la próxima remesa.`,
      revertir_pendiente_factura: `¿Revertir este contrato a "SEPA generado"? Úsalo si procesaste un pain.002 por error. La comisión del técnico (si existe) también se anulará.`,
    };
    if (!window.confirm(mensajes[accion])) return;
    setLoading(true); setMsg({ type: '', text: '' });
    try {
      const r = await fetch('/.netlify/functions/revertirMantenimiento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ mantenimientoId: item.id, accion }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al revertir');
      setMsg({ type: 'ok', text: `Estado revertido a: ${d.estadoRestaurado}` });
      onRefresh();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally { setLoading(false); }
  };

  const handleEliminar = async () => {
    if (!window.confirm(`¿Eliminar el contrato de ${item.clienteNombre}? Esta acción no se puede deshacer.`)) return;
    setLoading(true); setMsg({ type: '', text: '' });
    try {
      const r = await fetch(`/.netlify/functions/saveMantenimiento?id=${item.id}`, { method: 'DELETE', headers: authService.authHeader() });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al eliminar');
      onClose();
      onRefresh();
    } catch (e) { setMsg({ type: 'err', text: e.message }); }
    finally { setLoading(false); }
  };

  const Spinner = () => <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />;
  const btnBase = 'flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl transition-colors';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] bg-white shadow-2xl flex flex-col border-l overflow-hidden">

        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 text-base leading-tight truncate">{item.clienteNombre}</h3>
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {[item.municipio, item.zona].filter(Boolean).join(' · ') || '—'}
                {equipos?.length ? ` · ${equipos.length} equipo${equipos.length !== 1 ? 's' : ''}` : ''}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none mt-0.5 flex-shrink-0">✕</button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <EstadoBadge estado={item.estado} />
            <VisitaPendienteBadge item={item} />
            {item.pack && <PackBadge pack={item.pack} />}
          </div>
          {/* Resumen — las cifras que se quieren ver sin hacer scroll (solo con contrato) */}
          {item.tieneContrato && (() => {
            const dProx  = item.fechaProximoMantenimiento ? diasHasta(item.fechaProximoMantenimiento) : null;
            const dVence = item.fechaVencimientoContrato ? diasHasta(item.fechaVencimientoContrato) : null;
            return (
              <div className="grid grid-cols-2 gap-1.5 mt-3">
                <MiniStat label="Próximo mant." value={fmt(item.fechaProximoMantenimiento)}
                  sub={dProx == null ? null : dProx < 0 ? `vencido hace ${-dProx} d` : dProx === 0 ? 'hoy' : `en ${dProx} d`}
                  tone={dProx == null ? undefined : dProx < 0 ? 'alert' : dProx <= 15 ? 'warn' : undefined} />
                <MiniStat label="Técnico" value={item.tecnicoAsignado} sub={item.zona} />
                {canEditPrecios
                  ? <MiniStat label="Precio / año" value={item.precioAnual ? `${Number(item.precioAnual).toFixed(0)}€` : undefined} sub={PACKS[item.pack]?.label} />
                  : <MiniStat label="Pack" value={PACKS[item.pack]?.label} />}
                <MiniStat label="Vence contrato" value={fmt(item.fechaVencimientoContrato)}
                  sub={dVence == null ? null : dVence < 0 ? 'vencido' : `en ${dVence} d`}
                  tone={dVence == null ? undefined : dVence < 0 ? 'alert' : dVence <= 45 ? 'warn' : undefined} />
              </div>
            );
          })()}
        </div>

        {/* Botones de acción */}
        <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap gap-1.5 flex-shrink-0">
          {/* Agendar: solo cuando hay visita pendiente de programar */}
          {item.estado === 'pendiente_agendar' && (
            <button onClick={() => toggle('agendar')} className={`${btnBase} ${accion === 'agendar' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
              <Icons.Calendar className="w-3.5 h-3.5" />Agendar
            </button>
          )}
          {/* Agendar urgente: sin WO todavía, en cualquier fase pre-factura */}
          {['firmado_activo', 'sepa_generado', 'pendiente_factura'].includes(item._storedEstado) && !item.woMantenimientoId && (
            <button onClick={() => toggle('agendar')} className={`${btnBase} ${accion === 'agendar' ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}>
              <Icons.Calendar className="w-3.5 h-3.5" />Agendar urgente
            </button>
          )}
          {/* Aviso: WO auto-creación falló al firmar */}
          {item._storedEstado === 'firmado_activo' && !item.woMantenimientoId && item.woAutoCreacionError && (
            <div className="w-full mt-1 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-800">
              ⚠️ No se pudo crear la orden de trabajo automáticamente al firmar. Agrégala manualmente con el botón de arriba.
            </div>
          )}
          {/* Badge: visita urgente ya agendada (WO creada, pendiente de cobro/factura)
              firmado_activo: siempre es FRST, basta con woMantenimientoId
              sepa_generado / pendiente_factura: puede ser RCUR → exigir además woAgendadaUrgente */}
          {item.woMantenimientoId &&
           ['firmado_activo', 'sepa_generado', 'pendiente_factura'].includes(item._storedEstado) &&
           (item._storedEstado === 'firmado_activo' || item.woAgendadaUrgente) && (
            <span className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2 font-semibold">
              ⚡ Visita urgente: {fmt(item.fechaProximoMantenimiento)}
            </span>
          )}
          {/* Renovar: para extender contrato manualmente */}
          {['activo', 'vence_pronto', 'vencido'].includes(item.estado) && item.tieneContrato && (
            <button onClick={() => toggle('renovar')} className={`${btnBase} ${accion === 'renovar' ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}>
              <Icons.Refresh className="w-3.5 h-3.5" />Renovar
            </button>
          )}
          {/* Ampliar/editar servicios: contrato activo al que aún se le puede
              añadir algún servicio, O contrato ya enviado a firmar pero
              TODAVÍA sin firmar — corregirlo aquí actualiza el enlace ya
              enviado (misma URL) para que el cliente vea la versión buena. */}
          {item.tieneContrato && ['firmado_activo', 'sepa_generado', 'pendiente_factura', 'pendiente_agendar', 'mantenimiento_agendado', 'pendiente_renovar', 'contrato_enviado'].includes(item._storedEstado) && ['basico', 'ac_solo', 'silver', 'gold', 'caldera'].includes(item.pack) && (
            <button onClick={() => setShowAmpliacion(true)} className={`${btnBase} bg-violet-50 text-violet-700 hover:bg-violet-100`}>
              <Icons.Plus className="w-3.5 h-3.5" />{item._storedEstado === 'contrato_enviado' ? 'Editar servicios' : 'Ampliar contrato'}
            </button>
          )}
          {/* WhatsApp: siempre que haya teléfono */}
          {item.clienteTelefono && (
            <button onClick={() => toggle('wa')} className={`${btnBase} ${accion === 'wa' ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
              <Icons.MessageCircle className="w-3.5 h-3.5" />WhatsApp
            </button>
          )}
          {canEditPrecios && item.tieneContrato && (
            <button onClick={() => toggle('pack')} className={`${btnBase} ${accion === 'pack' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}><Icons.Tag className="w-3.5 h-3.5" />Pack</button>
          )}
          {item.estado === 'sin_contrato' && onNuevoContrato && (
            <button onClick={onNuevoContrato} className={`${btnBase} bg-red-600 text-white hover:bg-red-700`}>
              <Icons.Plus className="w-3.5 h-3.5" />Nuevo contrato
            </button>
          )}
          {['interesado', 'contactado'].includes(item.estado) && onNuevoContrato && (
            <button onClick={onNuevoContrato} className={`${btnBase} bg-red-600 text-white hover:bg-red-700`}>
              <Icons.Plus className="w-3.5 h-3.5" />Crear contrato
            </button>
          )}
          {['contrato_enviado', 'sepa_devuelto'].includes(item.estado) && (
            <button onClick={() => { if (accion === 'enlace') { setAccion(null); } else { setAccion('enlace'); setEnlaceUrl(null); setMsg({ type: '', text: '' }); setTimeout(fetchEnlace, 0); } }}
              className={`${btnBase} ${accion === 'enlace' ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}>
              <Icons.Copy className="w-3.5 h-3.5" />{item.estado === 'sepa_devuelto' ? 'Actualizar IBAN' : 'Reenviar enlace'}
            </button>
          )}
          {/* Cobro por transferencia: sepa_devuelto sin necesidad de re-firma.
              Marca el cobro como recibido sin verificación bancaria real —
              es una decisión de tesorería, admin/coordinador solamente. */}
          {isAdmin && item.estado === 'sepa_devuelto' && (
            <button onClick={() => toggle('cobro_transferencia')}
              className={`${btnBase} ${accion === 'cobro_transferencia' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
              <Icons.DollarSign className="w-3.5 h-3.5" />Cobro por transferencia
            </button>
          )}
          {/* Sin respuesta: contactado/interesado sin actividad 10+ días */}
          {['contactado', 'interesado'].includes(item.estado) && diasDesde(item.ultimoContacto) >= 10 && (
            <button onClick={async () => {
              setLoading(true); setMsg({ type: '', text: '' });
              try {
                await saveApi({ id: item.id, estado: 'sin_respuesta', grupoCliente: 'C', updatedAt: hoy() });
                setMsg({ type: 'ok', text: 'Marcado como sin respuesta (Grupo C)' });
                onRefresh();
                setTimeout(() => { onClose(); }, 1800);
              } catch (e) { setMsg({ type: 'err', text: e.message }); }
              finally { setLoading(false); }
            }} disabled={loading} className={`${btnBase} bg-gray-100 text-gray-500 hover:bg-gray-200`}>
              Sin respuesta
            </button>
          )}
          {/* Recuperar: sin_respuesta → volver a contactado */}
          {item.estado === 'sin_respuesta' && (
            <button onClick={async () => {
              setLoading(true); setMsg({ type: '', text: '' });
              try {
                await saveApi({ id: item.id, estado: 'contactado', ultimoContacto: hoy(), contactadoAt: new Date().toISOString(), updatedAt: hoy() });
                setMsg({ type: 'ok', text: 'Contacto reactivado' });
                onRefresh();
                setTimeout(() => { onClose(); }, 1800);
              } catch (e) { setMsg({ type: 'err', text: e.message }); }
              finally { setLoading(false); }
            }} disabled={loading} className={`${btnBase} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}>
              Reactivar contacto
            </button>
          )}
          {/* Recuperar: no_interesado → volver a contactado. Antes solo era
              posible arrastrando la tarjeta en el Kanban de captación (otra
              pestaña) — un contrato encontrado por búsqueda en Contratos no
              tenía ningún botón para reactivarlo. */}
          {item.estado === 'no_interesado' && (
            <button onClick={async () => {
              setLoading(true); setMsg({ type: '', text: '' });
              try {
                await saveApi({ id: item.id, estado: 'contactado', ultimoContacto: hoy(), contactadoAt: new Date().toISOString(), updatedAt: hoy() });
                setMsg({ type: 'ok', text: 'Contacto reactivado' });
                onRefresh();
                setTimeout(() => { onClose(); }, 1800);
              } catch (e) { setMsg({ type: 'err', text: e.message }); }
              finally { setLoading(false); }
            }} disabled={loading} className={`${btnBase} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}>
              Reactivar contacto
            </button>
          )}
          {/* Reagendar visita: contratos en modo visita (sin ciclo SEPA) que se
              quedan sin ningún botón — ya sea porque el admin acaba de pulsar
              "Renovar" (pendiente_renovar: fechas nuevas, falta agendar) o
              porque la visita agendada quedó pasada sin que el cron detectara
              el cobro (pendiente_reagendar: mismo contrato, solo hay que mover
              la fecha de la visita). En modo SEPA ambos casos los recoge solo
              la siguiente remesa (generarRemesaSepa.js), así que aquí no hace falta. */}
          {item.modoCobro === 'visita' && ['pendiente_renovar', 'pendiente_reagendar'].includes(item.estado) && (
            <button onClick={() => toggle('agendar')} className={`${btnBase} ${accion === 'agendar' ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}>
              <Icons.Calendar className="w-3.5 h-3.5" />Reagendar visita
            </button>
          )}
          {/* Aviso si falta clienteId y el estado requiere StelOrder */}
          {item.estado === 'pendiente_agendar' && !item.clienteId && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 font-semibold">
              ⚠ Falta ID StelOrder — no se puede crear WO ni factura
            </span>
          )}
          {isAdmin && item._storedEstado === 'sepa_generado' && (
            <button onClick={() => handleRevertir('revertir_sepa_generado')} disabled={loading}
              className={`${btnBase} bg-amber-50 text-amber-700 hover:bg-amber-100`}>
              ↩ Revertir remesa
            </button>
          )}
          {isAdmin && item._storedEstado === 'pendiente_factura' && (
            <button onClick={() => handleRevertir('revertir_pendiente_factura')} disabled={loading}
              className={`${btnBase} bg-amber-50 text-amber-700 hover:bg-amber-100`}>
              ↩ Revertir pain.002
            </button>
          )}
          {isAdmin && item.estado !== 'baja' && !ESTADOS_CAPTACION.includes(item.estado) && (
            <button onClick={handleDarBaja} disabled={loading}
              className={`${btnBase} bg-gray-100 text-gray-600 hover:bg-gray-200`}>
              <Icons.Trash className="w-3.5 h-3.5" />Dar de baja
            </button>
          )}
          {isAdmin && (
            <button onClick={handleEliminar} disabled={loading}
              className={`${btnBase} bg-red-50 text-red-700 hover:bg-red-100`}>
              <Icons.Trash className="w-3.5 h-3.5" />Eliminar
            </button>
          )}
        </div>

        {/* Aviso mantenimiento próximo — fila completa bajo los botones */}
        {item.estado === 'pendiente_agendar' && item.fechaProximoMantenimiento && diasHasta(item.fechaProximoMantenimiento) <= 30 && (
          <div className={`px-5 py-2 border-b text-xs font-semibold flex-shrink-0 flex items-center gap-2 ${diasHasta(item.fechaProximoMantenimiento) <= 0 ? 'bg-red-50 text-red-700 border-red-100' : diasHasta(item.fechaProximoMantenimiento) <= 15 ? 'bg-orange-50 text-orange-700 border-orange-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
            📅 Mantenimiento previsto el {fmt(item.fechaProximoMantenimiento)} — contactar cliente para confirmar fecha
          </div>
        )}

        {/* Feedback */}
        {msg.text && (
          <div className={`px-5 py-2 text-xs font-semibold flex-shrink-0 ${msg.type === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {msg.text}
          </div>
        )}

        {/* Paneles de acción inline */}
        {accion === 'cobro_transferencia' && (
          <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100 space-y-3 flex-shrink-0">
            <p className="text-xs font-bold text-emerald-800">Confirmar cobro por transferencia</p>
            <p className="text-xs text-emerald-700">El contrato pasará a <strong>Pendiente factura</strong> como si el cobro SEPA hubiera sido aceptado. Usa esto cuando el cliente haya realizado una transferencia bancaria en lugar del cargo domiciliado.</p>
            <div className="flex gap-2">
              <button onClick={async () => {
                setLoading(true); setMsg({ type: '', text: '' });
                try {
                  await saveApi({ id: item.id, estado: 'pendiente_factura', estadoPago: 'cobrado', motivoDevolucion: null, infoDevolucion: null, updatedAt: hoy() });
                  setMsg({ type: 'ok', text: 'Marcado como cobrado — pendiente de factura' });
                  setAccion(null);
                  onRefresh();
                } catch (e) { setMsg({ type: 'err', text: e.message }); }
                finally { setLoading(false); }
              }} disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-xl">
                {loading ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : null}
                Confirmar cobro
              </button>
              <button onClick={() => setAccion(null)} disabled={loading}
                className="px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-100 rounded-xl">
                Cancelar
              </button>
            </div>
          </div>
        )}
        {accion === 'enlace' && (
          <div className="px-5 py-3 bg-indigo-50 border-b border-indigo-100 space-y-2 flex-shrink-0">
            {enlaceLoading && (
              <div className="flex items-center gap-2 text-sm text-indigo-600">
                <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                Buscando enlace…
              </div>
            )}
            {enlaceUrl && !enlaceLoading && (
              <>
                <p className="text-xs font-bold text-indigo-700">{enlaceIsNew ? 'Enlace nuevo generado (válido 7 días)' : 'Enlace existente (aún válido)'}</p>
                <p className="text-xs text-indigo-500 break-all bg-white border border-indigo-200 rounded-xl px-3 py-2 select-all">{enlaceUrl}</p>
                <div className="flex gap-2">
                  <button onClick={() => { navigator.clipboard.writeText(enlaceUrl); setEnlaceCopiado(true); setTimeout(() => setEnlaceCopiado(false), 2000); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-white border border-indigo-200 hover:bg-indigo-50 rounded-xl">
                    <Icons.Copy className="w-3.5 h-3.5" />{enlaceCopiado ? '¡Copiado!' : 'Copiar'}
                  </button>
                  <button onClick={() => {
                    const tel = normTel(item.clienteTelefono);
                    if (!tel) return alert('Sin teléfono registrado');
                    const nombre = (item.clienteNombre || '').split(' ')[0];
                    const msg = encodeURIComponent(`Hola ${nombre}, le enviamos el enlace para firmar su contrato de mantenimiento:\n\n${enlaceUrl}\n\nEnlace válido 7 días. Cualquier duda llámenos.\n_Climatización Zapata_`);
                    window.open(`https://wa.me/${tel}?text=${msg}`, '_blank', 'noopener,noreferrer');
                  }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-xl">
                    <Icons.MessageCircle className="w-3.5 h-3.5" />Enviar por WA
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {accion === 'agendar' && !woCreado && item.woMantenimientoId && item.estado === 'pendiente_agendar' && (
          <div className="px-5 py-3 bg-orange-50 border-b border-orange-100 space-y-2 flex-shrink-0">
            <p className="text-xs font-bold text-orange-700">⚡ Visita urgente ya creada — {fmt(item.fechaProximoMantenimiento)}</p>
            <p className="text-xs text-orange-600">La WO está en StelOrder. Confirma para marcar el contrato como agendado.</p>
            <button onClick={confirmarWoUrgente} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-xl">
              {loading ? <Spinner /> : null}Confirmar agendado
            </button>
          </div>
        )}
        {accion === 'agendar' && !woCreado && !(item.woMantenimientoId && item.estado === 'pendiente_agendar') && (
          <div className="px-5 py-3 bg-blue-50 border-b border-blue-100 flex gap-2 items-end flex-shrink-0">
            <div className="flex-1">
              <label className="block text-xs font-bold text-blue-700 mb-1">Fecha de visita</label>
              <input type="date" value={fechaWO} onChange={e => setFechaWO(e.target.value)} min={hoy()}
                className="w-full border border-blue-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none" />
            </div>
            <button onClick={agendarWO} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl">
              {loading ? <Spinner /> : null}Crear WO
            </button>
          </div>
        )}
        {woCreado && (() => {
          const tel    = normTel(item.clienteTelefono || '');
          const es     = !tel || tel.startsWith('34');
          const nombre = (item.clienteNombre || '').split(' ')[0] || 'cliente';
          const tec    = item.tecnicoAsignado ? (es ? ` con ${item.tecnicoAsignado}` : ` with ${item.tecnicoAsignado}`) : '';
          const msgWA  = es
            ? `Hola ${nombre}, le confirmamos su visita de mantenimiento${tec} el *${fmtL(woCreado.fecha)}*.\n\nLe rogamos que esté disponible en su domicilio o nos avise con antelación si necesita cambiar la fecha.\n\n_Climatización Zapata_`
            : `Hi ${nombre}, we confirm your maintenance visit${tec} on *${fmtL(woCreado.fecha)}*.\n\nPlease be available at home or let us know in advance if you need to change the date.\n\n_Climatización Zapata_`;
          return (
            <div className="px-5 py-3 bg-green-50 border-b border-green-100 space-y-2 flex-shrink-0">
              <p className="text-xs font-bold text-green-700">✓ Visita creada — {fmtL(woCreado.fecha)}</p>
              {item.clienteTelefono && (
                <button onClick={() => window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msgWA)}`, '_blank', 'noopener,noreferrer')}
                  className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-xl">
                  <Icons.MessageCircle className="w-4 h-4" />Confirmar cita por WhatsApp
                </button>
              )}
              <button onClick={() => { setWoCreado(null); setAccion(null); }}
                className="w-full text-xs text-green-600 hover:text-green-800 font-semibold py-0.5">
                Cerrar
              </button>
            </div>
          );
        })()}
        {accion === 'renovar' && (
          <div className="px-5 py-3 bg-purple-50 border-b border-purple-100 flex-shrink-0 space-y-2">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs font-bold text-purple-700 mb-1">Nueva fecha fin contrato</label>
                <input type="date" value={nuevaFin} onChange={e => setNuevaFin(e.target.value)} min={hoy()}
                  className="w-full border border-purple-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none" />
              </div>
              <button onClick={renovarContrato} disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-xl">
                {loading ? <Spinner /> : null}Renovar
              </button>
            </div>
            {item.modoCobro === 'visita' && (
              <label className="flex items-center gap-2 text-xs font-semibold text-purple-800 cursor-pointer">
                <input type="checkbox" checked={domiciliarRenovacion} onChange={e => setDomiciliarRenovacion(e.target.checked)} />
                El cliente quiere domiciliar el cobro (pasar a SEPA) a partir de esta renovación
              </label>
            )}
          </div>
        )}
        {accion === 'wa' && (
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 space-y-2 flex-shrink-0">
            <div className="flex gap-2">
              {['vencimiento', 'captacion'].map(t => (
                <button key={t} onClick={() => setTipoMsg(t)}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors ${tipoMsg === t ? 'bg-green-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>
                  {t === 'vencimiento' ? 'Recordatorio' : 'Oferta'}
                </button>
              ))}
            </div>
            <pre className="whitespace-pre-wrap font-sans text-xs text-gray-700 bg-white border rounded-xl p-3 max-h-32 overflow-y-auto leading-relaxed">
              {generarMensaje(item, tipoMsg)}
            </pre>
            <div className="flex gap-2">
              <button onClick={copiarWA} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border hover:bg-gray-50 rounded-xl">
                <Icons.Copy className="w-3.5 h-3.5" />{waCopiado ? '¡Copiado!' : 'Copiar'}
              </button>
              <button onClick={abrirWA} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-xl">
                <Icons.MessageCircle className="w-3.5 h-3.5" />Abrir WA
              </button>
            </div>
          </div>
        )}
        {accion === 'pack' && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex gap-2 items-end flex-shrink-0">
            <div className="flex-1">
              <label className="block text-xs font-bold text-amber-700 mb-1">Nuevo pack</label>
              <select value={nuevoPack} onChange={e => setNuevoPack(e.target.value)}
                className="w-full border border-amber-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none">
                {Object.entries(PACKS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <button onClick={cambiarPack} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-xl">
              {loading ? <Spinner /> : null}Cambiar
            </button>
          </div>
        )}

        {/* Tabs detalles / equipos / historial */}
        <div className="border-b border-gray-100 flex flex-shrink-0">
          {[['detalles', 'Detalles'], ['equipos', `Equipos${equipos?.length ? ` (${equipos.length})` : ''}`], ['historial', 'Historial']].map(([k, l]) => (
            <button key={k} onClick={() => setPanelTab(k)}
              className={`flex-1 py-2.5 text-sm font-bold transition-colors ${panelTab === k ? 'text-red-600 border-b-2 border-red-500' : 'text-gray-500 hover:text-gray-700'}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {panelTab === 'detalles' && (
            <>
              <Seccion title="Cliente">
                <Field label="Nombre"    value={item.clienteNombre} />
                <Field label="DNI / NIE" value={item.clienteDni} />
                <Field label="Teléfono"  value={item.clienteTelefono} />
                <Field label="Email"     value={item.clienteEmail} />
                <Field label="Dirección" value={item.direccion} />
                <Field label="Municipio" value={item.municipio} />
              </Seccion>
              <Seccion title="Garantía y telemetría">
                <Field label="Tipo"          value={TIPOS_EQUIPO[item.tipoEquipo] || item.tipoEquipo} />
                <Field label="Instalación"   value={fmt(item.fechaInstalacion)} />
                <Field label="Telemetría"    value={item.telemetria ? 'Activa' : undefined} />
                <Field label="Ext. garantía" value={item.extensionGarantia ? '3 → 5 años' : undefined} />
                {item.extensionGarantia && (
                  <div className="flex gap-2 items-center text-xs">
                    <span className="text-gray-400 w-28 flex-shrink-0">ID contrato Ariston</span>
                    {aristonEdit ? (
                      <div className="flex gap-1 flex-1">
                        <input
                          value={aristonId}
                          onChange={e => setAristonId(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') guardarAriston(); if (e.key === 'Escape') setAristonEdit(false); }}
                          autoFocus
                          placeholder="Ej: AR-2026-XXXXX"
                          className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-red-400"
                        />
                        <button onClick={guardarAriston} disabled={loading} className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">✓</button>
                        <button onClick={() => setAristonEdit(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">✕</button>
                      </div>
                    ) : (
                      <div className="flex gap-1.5 items-center flex-1">
                        {item.idContratoAriston
                          ? <span className="font-semibold text-gray-700">{item.idContratoAriston}</span>
                          : <span className="font-bold text-amber-600">⚠ Pendiente</span>}
                        <button onClick={() => { setAristonEdit(true); setAristonId(item.idContratoAriston || ''); }}
                          className="text-gray-300 hover:text-gray-500 text-[10px] leading-none">✏</button>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-gray-400 pt-1">
                  Marca, nº de serie y garantía de cada equipo, en la pestaña <b>Equipos</b>.
                </p>
              </Seccion>
              <Seccion title="Contrato">
                <Field label="Pack"          value={PACKS[item.pack]?.label} />
                {item.tieneContrato && (
                  <div className="flex text-sm gap-2 items-center">
                    <span className="text-gray-400 w-28 flex-shrink-0 leading-snug">Cobro</span>
                    {isAdmin && cobroEdit ? (
                      <div className="flex gap-1 flex-1">
                        <select value={cobroVal} onChange={e => setCobroVal(e.target.value)} autoFocus
                          className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-red-400">
                          <option value="visita">En visita (técnico)</option>
                          <option value="sepa">Domiciliado (SEPA)</option>
                        </select>
                        <button onClick={guardarCobro} disabled={loading} className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">✓</button>
                        <button onClick={() => { setCobroEdit(false); setCobroVal(item.modoCobro === 'sepa' ? 'sepa' : 'visita'); }} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">✕</button>
                      </div>
                    ) : (
                      <span className="text-gray-800 font-medium flex-1 leading-snug flex items-center gap-1.5">
                        {item.modoCobro === 'visita' ? 'En visita (técnico)' : 'Domiciliado (SEPA)'}
                        {isAdmin && <button onClick={() => setCobroEdit(true)} className="text-gray-300 hover:text-gray-500 text-[10px] leading-none">✏</button>}
                      </span>
                    )}
                  </div>
                )}
                {canEditPrecios && <Field label="Precio/año"  value={item.precioAnual ? `${Number(item.precioAnual).toFixed(2).replace('.', ',')}€` : undefined} />}
                <Field label="Técnico"       value={item.tecnicoAsignado} />
                <Field label="Zona"          value={item.zona} />
                <Field label="Inicio"        value={fmt(item.fechaInicioContrato)} />
                <div className="flex text-sm gap-2 items-center">
                  <span className="text-gray-400 w-28 flex-shrink-0 leading-snug">Vence</span>
                  {isAdmin && vcEdit ? (
                    <div className="flex gap-1 flex-1">
                      <input type="date" value={vcVal} onChange={e => setVcVal(e.target.value)} autoFocus
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-red-400" />
                      <button onClick={guardarVencimiento} disabled={loading} className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">✓</button>
                      <button onClick={() => { setVcEdit(false); setVcVal(item.fechaVencimientoContrato || ''); }} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">✕</button>
                    </div>
                  ) : (
                    <span className="text-gray-800 font-medium flex-1 leading-snug flex items-center gap-1.5">
                      {fmt(item.fechaVencimientoContrato) || '—'}
                      {isAdmin && <button onClick={() => { setVcEdit(true); setVcVal(item.fechaVencimientoContrato || ''); }} className="text-gray-300 hover:text-gray-500 text-[10px] leading-none">✏</button>}
                    </span>
                  )}
                </div>
                <Field label="Último mant."  value={fmt(item.fechaUltimoMantenimiento)} />
                <Field label="Inicio garantía" value={fmt(item.fechaInicioGarantia)} />
                {esClientePotencial(item) && <Field label="Contacto sugerido" value={fmt(fechaContactoSugerida(item))} />}
                <div className="flex text-sm gap-2 items-center">
                  <span className="text-gray-400 w-28 flex-shrink-0 leading-snug">Próximo mant.</span>
                  {isAdmin && fmEdit && !item.woMantenimientoId ? (
                    <div className="flex gap-1 flex-1">
                      <input type="date" value={fmVal} onChange={e => setFmVal(e.target.value)} autoFocus
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-red-400" />
                      <button onClick={guardarFechaMant} disabled={loading} className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">✓</button>
                      <button onClick={() => { setFmEdit(false); setFmVal(item.fechaProximoMantenimiento || ''); }} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">✕</button>
                    </div>
                  ) : (
                    <span className="text-gray-800 font-medium flex-1 leading-snug flex items-center gap-1.5">
                      {fmt(item.fechaProximoMantenimiento) || '—'}
                      {item.woMantenimientoId ? (
                        <span className="text-[10px] text-gray-400">· la marca la orden {item.woMantenimientoRef || ''}</span>
                      ) : (
                        isAdmin && <button onClick={() => { setFmEdit(true); setFmVal(item.fechaProximoMantenimiento || ''); }} className="text-gray-300 hover:text-gray-500 text-[10px] leading-none">✏</button>
                      )}
                    </span>
                  )}
                </div>
                <Field label="Canal"         value={CANALES[item.canalCaptacion] || item.canalCaptacion} />
                {item.instaladorReferido   && <Field label="Instalador"    value={item.instaladorReferido} />}
                {item.administradorFincas  && <Field label="Admin. fincas" value={item.administradorFincas} />}
                {item.codigoComunidad && <Field label="Comunidad" value={item.codigoComunidad} />}
                {item.socioNombre && <Field label="Socio referidor" value={`${item.socioNombre}${item.codigoSocio ? ` (${item.codigoSocio})` : ''}`} />}
                <Field label="Ref. contrato" value={item.referenciaContrato} />
                <Field label="Fecha firma"   value={fmt(item.fechaFirma)} />
                <Field label="Ref. mandato"  value={item.referenciaMandatoSepa} />
                <Field label="Titular IBAN"  value={item.titularCuenta} />
                <div className="flex gap-2 items-center text-sm">
                  <span className="text-gray-400 w-28 flex-shrink-0 leading-snug">WO mantenimiento</span>
                  {woEdit ? (
                    <div className="flex gap-1 flex-1">
                      <input
                        value={woIdInput}
                        onChange={e => setWoIdInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') vincularOrden(); if (e.key === 'Escape') setWoEdit(false); }}
                        autoFocus
                        placeholder="Ej: AV26/2634 o el ID numérico"
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-red-400"
                      />
                      <button onClick={vincularOrden} disabled={woVinculando} className="px-2 py-1 text-xs font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">{woVinculando ? '…' : '✓'}</button>
                      <button onClick={() => setWoEdit(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">✕</button>
                    </div>
                  ) : (
                    <div className="flex gap-1.5 items-center flex-1">
                      <span className="text-gray-800 font-medium">{item.woMantenimientoRef || '—'}</span>
                      <button onClick={() => { setWoEdit(true); setWoIdInput(''); }} title="Vincular a otra orden de StelOrder"
                        className="text-gray-300 hover:text-gray-500 text-[10px] leading-none">✏</button>
                    </div>
                  )}
                </div>
                {item.facturaRef && <Field label="Ref. factura" value={item.facturaRef} />}
                {item.facturaError && (
                  <div className="mt-1 bg-red-50 border border-red-100 rounded-xl p-2.5">
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wide">Error al facturar</p>
                    <p className="text-xs text-red-700 mt-0.5">{item.facturaError}</p>
                  </div>
                )}
                {item.estadoPago === 'devuelto' && (
                  <div className="mt-2 bg-red-50 border border-red-100 rounded-xl p-2.5 space-y-1">
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wide">Pago devuelto</p>
                    <div className="flex text-xs gap-2">
                      <span className="text-red-400 w-28 flex-shrink-0">Motivo</span>
                      <span className="text-red-800 font-bold">{item.motivoDevolucion || '—'}</span>
                    </div>
                    {item.infoDevolucion && (
                      <div className="flex text-xs gap-2">
                        <span className="text-red-400 w-28 flex-shrink-0">Info</span>
                        <span className="text-red-700">{item.infoDevolucion}</span>
                      </div>
                    )}
                    <div className="flex text-xs gap-2">
                      <span className="text-red-400 w-28 flex-shrink-0">Fecha</span>
                      <span className="text-red-700">{fmt(item.fechaDevolucion)}</span>
                    </div>
                  </div>
                )}
              </Seccion>

              {/* Contrato PDF */}
              {(['firmado_activo', 'activo', 'vence_pronto', 'vencido', 'sepa_generado', 'pendiente_factura', 'pendiente_agendar', 'mantenimiento_agendado', 'pendiente_renovar', 'pendiente_reagendar', 'sepa_devuelto'].includes(item.estado)) && (
                <div className="flex gap-2">
                  <button
                    onClick={() => abrirContratoPDF('ver')}
                    disabled={!!pdfLoading}
                    className="flex items-center gap-2 flex-1 justify-center px-3 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    {pdfLoading === 'ver' ? 'Abriendo…' : 'Ver contrato PDF'}
                  </button>
                  <button
                    onClick={() => abrirContratoPDF('descargar')}
                    disabled={!!pdfLoading}
                    className="flex items-center gap-1.5 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {pdfLoading === 'descargar' ? 'Descargando…' : 'Descargar'}
                  </button>
                </div>
              )}
              {item.notasInternas && (
                <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Notas</p>
                  {item.notasInternas}
                </div>
              )}
              {(item.clienteId || item.assetId) && (
                <div className="text-xs text-gray-300 space-y-0.5 border-t pt-3">
                  {item.clienteId && <p>StelOrder cliente: {item.clienteId}</p>}
                  {item.assetId   && <p>StelOrder activo:  {item.assetId}</p>}
                </div>
              )}
            </>
          )}
          {panelTab === 'equipos' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Equipos {coveredIds.size > 0 ? `del cliente · ${coveredIds.size} en el contrato` : 'del cliente'}
                </p>
              </div>

              {equiposLoading && !equipos && (
                <p className="text-sm text-gray-400 text-center py-4 animate-pulse">Cargando equipos…</p>
              )}
              {equiposError && (
                <p className="text-sm text-red-500 text-center py-3">{equiposError}</p>
              )}
              {!equiposError && equiposOrden && equiposOrden.length === 0 && !equiposLoading && (
                <div className="text-sm text-gray-400 text-center py-4 space-y-1">
                  <p>Sin equipos registrados para este cliente</p>
                  {!item.clienteId && <p className="text-xs">El contrato no tiene ID de cliente StelOrder.</p>}
                </div>
              )}
              {equiposOrden && equiposOrden.length > 0 && (
                <div className="space-y-2">
                  {equiposOrden.map(eq => (
                    <EquipoCard key={eq.id} eq={eq}
                      cubierto={coveredIds.has(eq.id)}
                      mostrarCobertura={coveredIds.size > 0} />
                  ))}
                </div>
              )}

              {equiposOrden && equiposOrden.length > 0 && coveredIds.size === 0 && (
                <p className="text-[11px] text-gray-400 pt-1">
                  Este contrato no tiene equipos vinculados de forma explícita — se muestran todos los del cliente.
                </p>
              )}
            </div>
          )}
          {panelTab === 'historial' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Cronología</p>
                <button onClick={() => loadWorkorders(true)} disabled={woLoading}
                  className="text-xs text-blue-500 hover:underline disabled:opacity-40">
                  {woLoading ? 'Cargando…' : 'Recargar'}
                </button>
              </div>

              {woLoading && eventos.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4 animate-pulse">Cargando historial…</p>
              )}
              {!woLoading && eventos.length === 0 && (
                <div className="text-sm text-gray-400 text-center py-4 space-y-1">
                  <p>Sin historial registrado</p>
                  {woDebug && (
                    <details className="text-left">
                      <summary className="cursor-pointer text-xs text-gray-300 hover:text-gray-500">Info debug</summary>
                      <pre className="text-xs text-gray-400 bg-gray-50 rounded p-2 mt-1 whitespace-pre-wrap break-all">{JSON.stringify(woDebug, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}
              {eventos.length > 0 && <Timeline eventos={eventos} onWo={setWoDetalle} />}
            </div>
          )}
        </div>
      </div>

      {/* Modal: motivo de baja */}
      {showBajaModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowBajaModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900">Dar de baja contrato</h3>
            <p className="text-sm text-gray-500">El contrato de <strong>{item.clienteNombre}</strong> quedará en estado "Baja". El registro se conserva.</p>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Motivo de baja</label>
              <textarea rows={3} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 resize-none"
                value={motivoBaja} onChange={e => setMotivoBaja(e.target.value)}
                placeholder="Ej: cliente no renueva, precio, otro servicio…" autoFocus />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowBajaModal(false)} className="px-4 py-2.5 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl">Cancelar</button>
              <button onClick={confirmarBaja} className="px-4 py-2.5 text-sm font-bold text-white bg-gray-700 hover:bg-gray-800 rounded-xl">Confirmar baja</button>
            </div>
          </div>
        </div>
      )}
      {showAmpliacion && (
        <AmpliacionContratoModal
          item={item}
          onClose={() => setShowAmpliacion(false)}
          onSaved={() => { setShowAmpliacion(false); setMsg({ type: 'ok', text: 'Contrato ampliado correctamente' }); onRefresh(); }}
        />
      )}
      {woDetalle && (
        <WorkorderDetalleModal wo={woDetalle} onClose={() => setWoDetalle(null)} />
      )}
    </>
  );
}

// ─── Helpers para el wizard de contrato ───────────────────────────────────────

// tipoSplit es un array — puede llevar 'split', 'conductos' o los dos a la
// vez (un mismo contrato puede cubrir ambos tipos de equipo, p.ej. split en
// unas habitaciones y conductos en el salón). Las unidades exteriores son un
// único contador compartido (numUnidadesExterior): cuestan 50€/unidad igual
// da si van con una máquina split o de conductos, así que no hace falta
// distinguirlas — pedido explícito del usuario.
function calcularPrecioServicio({ tiposServicio, tipoAerotermia, tipoSplit, numSplitsInterior, numConductosInterior, numUnidadesExterior }, zona, numEquiposAerotermia = 1) {
  let precio = 0;
  if (tiposServicio.includes('aerotermia')) {
    precio += (tipoAerotermia === 'suelo' ? 125 : 105) * numEquiposAerotermia;
  }
  if (tiposServicio.includes('airAcondicionado')) {
    const tipos = tipoSplit || [];
    if (tipos.includes('split'))     precio += (parseInt(numSplitsInterior) || 1) * 25;
    if (tipos.includes('conductos')) precio += (parseInt(numConductosInterior) || 1) * 40;
    precio += (parseInt(numUnidadesExterior) || 0) * 50;
  }
  if (tiposServicio.includes('caldera')) {
    precio += 120;
  }
  if (tiposServicio.includes('aerotermiaMultitarea')) {
    precio += 220;
  }
  precio += RECARGO_ZONA[zona] || 0;
  return Math.round(precio * 100) / 100;
}

function calcularDesglosePrecio({ tiposServicio, tipoAerotermia, tipoSplit, numSplitsInterior, numConductosInterior, numUnidadesExterior }, zona, numEquiposAerotermia = 1) {
  const lineas = [];
  if (tiposServicio.includes('aerotermia')) {
    const tipo      = tipoAerotermia === 'suelo' ? 'suelo' : 'pared';
    const precioUnd = tipoAerotermia === 'suelo' ? 125 : 105;
    if (numEquiposAerotermia > 1) {
      for (let i = 1; i <= numEquiposAerotermia; i++) {
        lineas.push({ label: `Aerotermia ${i} (${tipo})`, importe: precioUnd });
      }
    } else {
      lineas.push({ label: `Mantenimiento aerotermia (${tipo})`, importe: precioUnd });
    }
  }
  if (tiposServicio.includes('airAcondicionado')) {
    const tipos = tipoSplit || [];
    if (tipos.includes('split')) {
      const n = parseInt(numSplitsInterior) || 1;
      lineas.push({ label: `Mantenimiento ${n} unidad${n > 1 ? 'es' : ''} interior${n > 1 ? 'es' : ''} split`, importe: n * 25 });
    }
    if (tipos.includes('conductos')) {
      const nInt = parseInt(numConductosInterior) || 1;
      lineas.push({ label: `Mantenimiento ${nInt} unidad${nInt > 1 ? 'es' : ''} interior${nInt > 1 ? 'es' : ''} conductos`, importe: nInt * 40 });
    }
    const nExt = parseInt(numUnidadesExterior) || 0;
    if (nExt > 0) lineas.push({ label: `Mantenimiento ${nExt} unidad${nExt > 1 ? 'es' : ''} exterior${nExt > 1 ? 'es' : ''}`, importe: nExt * 50 });
  }
  if (tiposServicio.includes('caldera')) {
    lineas.push({ label: 'Mantenimiento caldera', importe: 120 });
  }
  if (tiposServicio.includes('aerotermiaMultitarea')) {
    lineas.push({ label: 'Mantenimiento aerotermia multitarea', importe: 220 });
  }
  const recargo = RECARGO_ZONA[zona] || 0;
  if (recargo > 0) lineas.push({ label: `Suplemento desplazamiento ${zona}`, importe: recargo });
  return lineas;
}

function derivarServicios(cfg, zona, numEquiposAerotermia = 1) {
  const s = [];
  if (cfg.tiposServicio.includes('aerotermia')) {
    const tipo = cfg.tipoAerotermia === 'suelo' ? 'suelo' : 'pared';
    s.push(numEquiposAerotermia > 1
      ? `Mantenimiento ${numEquiposAerotermia} aerotermias (equipo de ${tipo})`
      : `Mantenimiento aerotermia (equipo de ${tipo})`);
    if (cfg.esNuos) s.push('Extensión de garantía 3→5 años (NUOS Ariston)');
  }
  if (cfg.tiposServicio.includes('airAcondicionado')) {
    const tipos = cfg.tipoSplit || [];
    if (tipos.includes('split')) {
      const n = parseInt(cfg.numSplitsInterior) || 1;
      s.push(`Mantenimiento ${n} unidad${n > 1 ? 'es' : ''} interior${n > 1 ? 'es' : ''} split`);
    }
    if (tipos.includes('conductos')) {
      const nInt = parseInt(cfg.numConductosInterior) || 1;
      s.push(`Mantenimiento ${nInt} unidad${nInt > 1 ? 'es' : ''} interior${nInt > 1 ? 'es' : ''} conductos`);
    }
    const nExt = parseInt(cfg.numUnidadesExterior) || 0;
    if (nExt > 0) s.push(`Mantenimiento ${nExt} unidad${nExt > 1 ? 'es' : ''} exterior${nExt > 1 ? 'es' : ''}`);
  }
  if (cfg.tiposServicio.includes('caldera')) {
    s.push('Mantenimiento caldera');
  }
  if (cfg.tiposServicio.includes('aerotermiaMultitarea')) {
    s.push('Mantenimiento aerotermia multitarea');
  }
  const recargo = RECARGO_ZONA[zona] || 0;
  if (recargo > 0) s.push(`Suplemento desplazamiento ${zona} (+${recargo}€)`);
  return s;
}

function derivarPack({ tiposServicio, tipoSplit, numSplitsInterior }) {
  // La aerotermia multitarea es de la familia aerotermia para efectos de pack.
  const aerotermia = tiposServicio.includes('aerotermia') || tiposServicio.includes('aerotermiaMultitarea');
  const ac         = tiposServicio.includes('airAcondicionado');
  const caldera    = tiposServicio.includes('caldera');
  // Solo caldera → su propio pack. Con aerotermia/aire, la caldera es un
  // añadido y el pack lo marca el equipo principal.
  if (!aerotermia && !ac && caldera) return 'caldera';
  if (!aerotermia) return 'ac_solo';
  if (!ac)         return 'basico';
  // Conductos (solo o combinado con split) siempre es gold — mismo criterio
  // que antes, ahora también cubre el caso combinado.
  if ((tipoSplit || []).includes('conductos')) return 'gold';
  const n = parseInt(numSplitsInterior) || 1;
  return n >= 3 ? 'gold' : 'silver';
}

// Clasifica un contrato por lo que cubre: 'aerotermia' (solo), 'aire' (solo aire
// acondicionado), 'ambas' o 'otro'. tiposServicio no se guarda en el documento,
// así que se deduce de serviciosIncluidos (texto) con el pack y el tipo de
// equipo como respaldo para contratos antiguos.
const CLASE_SERVICIO_LABEL = { aerotermia: 'Solo aerotermia', aire: 'Solo aire acondicionado', ambas: 'Aerotermia + aire', caldera: 'Solo caldera', otro: 'Sin clasificar' };
const CLASES_SERVICIO = ['aerotermia', 'aire', 'ambas', 'caldera', 'otro'];
function claseServicioContrato(m) {
  const txt = (m.serviciosIncluidos || []).join(' · ').toLowerCase();
  let aero = /aerotermia|bomba de calor|nuos|acumulador|termo/.test(txt);
  let ac   = /split|conductos|aire acondicionado|unidad(?:es)? (?:interior|exterior)/.test(txt);
  const cal = /mantenimiento caldera/.test(txt) || m.pack === 'caldera';
  if (!aero && !ac && !cal) {
    if (m.pack === 'ac_solo') ac = true;
    else if (m.pack === 'basico') aero = true;
    else if (m.pack === 'silver' || m.pack === 'gold') { aero = true; ac = true; }
    else if (['nuos_ariston', 'aerotermia_otra', 'acumulador'].includes(m.tipoEquipo)) aero = true;
    else if (m.tipoEquipo === 'caldera') return 'caldera';
    else if (['split', 'conductos'].includes(m.tipoEquipo)) ac = true;
  }
  if (aero && ac) return 'ambas';
  if (aero) return 'aerotermia';
  if (ac) return 'aire';
  if (cal) return 'caldera';
  return 'otro';
}

// ─── Modal Nuevo Contrato — wizard 4 pasos ─────────────────────────────────────

const ESTADOS_BLOQUEO_CONTRATO = ['firmado_activo', 'sepa_generado', 'pendiente_factura', 'pendiente_agendar', 'mantenimiento_agendado', 'pendiente_renovar', 'pendiente_reagendar', 'contrato_enviado', 'sepa_devuelto'];

// El bloqueo de arriba solo mira clienteId exacto — si el cliente se crea
// "nuevo" en StelOrder para alguien que ya tenía ficha (typo en la búsqueda,
// teléfono distinto en el sistema...), el ID nunca coincide y no se detecta
// nada. Esto es un AVISO adicional, no un bloqueo: compara por teléfono y
// por nombre normalizado contra el resto de registros, para que quien crea
// el contrato pueda comprobar a mano si es la misma persona antes de seguir.
function normNombre(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
}
function normTelefonoCorto(s) {
  return (s || '').replace(/\D/g, '').slice(-9); // últimos 9 dígitos, sin prefijo de país
}
function buscarPosibleDuplicado(c, existingData) {
  const telNuevo    = normTelefonoCorto(c.phone);
  const nombreNuevo = normNombre(c.clienteNombre);
  if (!telNuevo && !nombreNuevo) return null;
  return (existingData || []).find(m => {
    if (!m.clienteId || String(m.clienteId) === String(c.id)) return false; // es el mismo cliente, no un duplicado
    if (m.estado === 'baja') return false;
    const telCoincide    = telNuevo && normTelefonoCorto(m.clienteTelefono) === telNuevo;
    const nombreCoincide = nombreNuevo && normNombre(m.clienteNombre) === nombreNuevo;
    return telCoincide || nombreCoincide;
  }) || null;
}

function NuevoContratoModal({ onClose, onSaved, clienteInicial, captadorData, existingData }) {
  const { user }  = useAuth();
  const inputCls  = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white';
  const lblCls       = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5';

  const [step,    setStep]   = useState(clienteInicial ? 1 : 0);
  const [error,   setError]  = useState('');
  const [saving,  setSaving] = useState(false);
  const [posibleDuplicado, setPosibleDuplicado] = useState(null); // aviso, no bloqueo

  // Paso 0 — búsqueda
  const [query,     setQuery]     = useState(clienteInicial?.clienteNombre || '');
  const [buscando,  setBuscando]  = useState(false);
  const [resultados,setResultados]= useState([]);
  const searchTimer               = React.useRef(null);

  // Paso 1 — cliente + equipo
  const [clienteSel,  setClienteSel]  = useState(clienteInicial ? {
    id:            clienteInicial.clienteId   || '',
    clienteNombre: clienteInicial.clienteNombre || '',
    phone:         clienteInicial.clienteTelefono || '',
    email:         clienteInicial.clienteEmail    || '',
    municipio:     clienteInicial.municipio       || '',
    direccion:     clienteInicial.direccion       || '',
    assets:        [],
  } : null);
  const [assetsSel,   setAssetsSel]   = useState([]);

  // Crear cliente nuevo
  const [creandoCliente,   setCreandoCliente]   = useState(false);
  const [guardandoCliente, setGuardandoCliente] = useState(false);
  const [nuevoCliente,     setNuevoCliente]     = useState({ nombre: '', telefono: '', email: '', direccion: '', codigoPostal: '', localidad: '', provincia: '' });
  const setNC = (k, v) => setNuevoCliente(c => ({ ...c, [k]: v }));

  // Crear asset nuevo
  const [creandoAsset,   setCreandoAsset]   = useState(false);
  const [guardandoAsset, setGuardandoAsset] = useState(false);
  const [nuevoAsset,     setNuevoAsset]     = useState({ marca: 'Ariston', modelo: '', tipoEquipo: 'BOMBA DE CALOR', numeroSerie: '', fechaInstalacion: '' });
  const setNA = (k, v) => setNuevoAsset(a => ({ ...a, [k]: v }));
  const [equipoManual,setEquipoManual]= useState(clienteInicial ? {
    marca:           clienteInicial.marca           || 'Ariston',
    modelo:          clienteInicial.modelo          || '',
    numeroSerie:     clienteInicial.numeroSerie     || '',
    tipoEquipo:      clienteInicial.tipoEquipo      || 'nuos_ariston',
    fechaInstalacion:clienteInicial.fechaInstalacion|| '',
  } : { marca: 'Ariston', modelo: '', numeroSerie: '', tipoEquipo: 'nuos_ariston', fechaInstalacion: '' });

  // Paso 2 — configuración del servicio
  const [cfg, setCfg] = useState({
    tiposServicio:       [],
    esNuos:              true,
    tipoAerotermia:      'pared',
    tipoSplit:           ['split'], // array — puede llevar 'split' y 'conductos' a la vez
    numSplitsInterior:   1,
    numConductosInterior: 1,
    numUnidadesExterior: 0, // compartido entre split y conductos (mismo coste, 50€/u)
  });
  const setCfgF = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const toggleServicio = (tipo) => setCfg(c => ({
    ...c,
    tiposServicio: c.tiposServicio.includes(tipo)
      ? c.tiposServicio.filter(t => t !== tipo)
      : [...c.tiposServicio, tipo],
  }));
  // Split/conductos no son mutuamente excluyentes — un contrato puede cubrir
  // los dos tipos de equipo a la vez. No se permite dejarlo vacío mientras
  // "Aire acondicionado" siga marcado.
  const toggleTipoSplit = (v) => setCfg(c => ({
    ...c,
    tipoSplit: c.tipoSplit.includes(v)
      ? (c.tipoSplit.length > 1 ? c.tipoSplit.filter(t => t !== v) : c.tipoSplit)
      : [...c.tipoSplit, v],
  }));

  // DNI/NIE (opcional, pre-cargado desde StelOrder si existe)
  const [clienteDni, setClienteDni] = useState('');

  // Paso 3 — enlace firma
  const [linkGenerado, setLinkGenerado] = useState(null); // {url, contratoId}

  // Config extra (técnico / zona / canal / cobro)
  const [extras, setExtras] = useState({
    tecnicoAsignado: '', zona: '', canalCaptacion: 'puesta_en_marcha',
    instaladorReferido: '', administradorFincas: '', notasInternas: '',
    modoCobro: 'visita', // por defecto en visita — igual que createContratoToken.js
    // El vencimiento del contrato es inicio + 1 año, SIEMPRE independiente de
    // la fecha de la visita de mantenimiento (que puede ser en 2 días si es
    // urgente). Antes se dejaba vacío y createContratoToken.js lo rellenaba con
    // la fecha del próximo mantenimiento → contratos que "duraban 2 días".
    fechaInicioContrato: hoy(), fechaVencimientoContrato: en1Ano(), fechaProximoMantenimiento: en1Ano(),
  });

  // Número de equipos seleccionados (mín. 1 para precio correcto si se escribe manualmente)
  const numEquiposAerotermia = Math.max(1, assetsSel.length);

  // Valores calculados (dependen de cfg y extras.zona — deben ir después de ambos)
  const precioCalculado   = calcularPrecioServicio(cfg, extras.zona, numEquiposAerotermia);
  const desglosePrecio    = calcularDesglosePrecio(cfg, extras.zona, numEquiposAerotermia);
  const serviciosDesglose = derivarServicios(cfg, extras.zona, numEquiposAerotermia);
  const packDerivado      = derivarPack(cfg);
  const setExt = (k, v) => setExtras(e => ({ ...e, [k]: v }));
  const tipoServicio = (extras.fechaProximoMantenimiento || '').startsWith(new Date().getFullYear().toString()) ? 'este_anio' : 'siguiente_anio';

  // Disponibilidad del técnico en la fecha seleccionada
  const [disponData, setDisponData] = useState(null);
  useEffect(() => {
    const cp = clienteSel?.codigoPostal;
    if (!cp) { setDisponData(null); return; }
    fetch(`/.netlify/functions/getDisponibilidad?cp=${encodeURIComponent(cp)}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => setDisponData(d))
      .catch(() => {});
  }, [clienteSel?.codigoPostal]);

  // Ausencias de la plantilla (baja/vacaciones). getDisponibilidad solo cubre
  // los próximos 5 días laborables; la fecha de próximo mantenimiento casi
  // siempre está a meses vista, así que hay que comprobar las ausencias aparte
  // para cualquier fecha. Sin `mes` el endpoint devuelve desde hace 6 meses en
  // adelante, lo que incluye todas las ausencias futuras.
  const [ausenciasPlantilla, setAusenciasPlantilla] = useState([]);
  useEffect(() => {
    fetch('/.netlify/functions/manageAusencias', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => { if (d.success) setAusenciasPlantilla(d.ausencias || []); })
      .catch(() => {});
  }, []);

  const disponCheck = useMemo(() => {
    const fecha  = extras.fechaProximoMantenimiento;
    const zona   = extras.zona;
    const tec    = extras.tecnicoAsignado;
    if (!fecha || !zona || !tec) return null;

    // Ausencia del técnico asignado en esa fecha — tiene prioridad sobre todo
    // lo demás. Match por id de StelOrder (fiable); si el registro es viejo y
    // no lo tiene, por nombre (el corto como palabra dentro del guardado).
    const tecIdBuscado = TECNICOS_IDS[tec] != null ? String(TECNICOS_IDS[tec]) : null;
    const tecLc = tec.toLowerCase();
    const aus = (ausenciasPlantilla || []).find(a => {
      if (!(a.fechaInicio && a.fechaInicio <= fecha && a.fechaFin && a.fechaFin >= fecha)) return false;
      const idAus = a.tecnicoId != null && a.tecnicoId !== '' ? String(a.tecnicoId) : null;
      if (tecIdBuscado && idAus) return tecIdBuscado === idAus;
      const nAus = (a.tecnicoNombre || '').trim().toLowerCase();
      return nAus === tecLc || nAus.split(/\s+/).includes(tecLc);
    });
    if (aus) {
      const motivo = aus.tipoLabel || aus.tipo || 'ausencia';
      return { nivel: 'rojo', msg: `${tec} no disponible — ${motivo} del ${aus.fechaInicio} al ${aus.fechaFin}` };
    }

    // Para fechas próximas: usar datos reales de StelOrder si los tenemos.
    // Ojo: la cita tiene que ser DEL técnico asignado — getDisponibilidad
    // también devuelve citas de apoyos cuando el principal está ausente/lleno,
    // y antes se cogía la primera del día sin mirar de quién era (mostraba
    // "N huecos" de otro técnico como si fueran del asignado).
    if (disponData?.citas) {
      const cita = disponData.citas.find(c => c.fechaISO === fecha && c.tecnico === tec);
      if (cita) {
        if (cita.huecos <= 0) return { nivel: 'rojo',   msg: `Sin huecos — ${tec} completo ese día` };
        if (cita.huecos <= 2) return { nivel: 'ambar',  msg: `Poco espacio — ${cita.huecos} hueco${cita.huecos > 1 ? 's' : ''} disponible${cita.huecos > 1 ? 's' : ''}` };
        return { nivel: 'verde', msg: `${cita.huecos} huecos disponibles` };
      }
    }

    // Para cualquier fecha: regla de calendario (día de semana × técnico × zona)
    const [y, m, d] = fecha.split('-').map(Number);
    const diaNombre = DIAS_SEMANA_ES[new Date(y, m - 1, d).getDay()];
    if (diaNombre === 'Sabado' || diaNombre === 'Domingo')
      return { nivel: 'rojo', msg: 'Fin de semana — no hay servicio' };
    const zonasTec = CALENDARIO_MANTENIMIENTO[diaNombre]?.[tec] || [];
    if (zonasTec.includes(zona))
      return { nivel: 'verde', msg: `${tec} trabaja en ${zona} los ${diaNombre}s` };
    const diasSi = Object.entries(CALENDARIO_MANTENIMIENTO)
      .filter(([, t]) => (t[tec] || []).includes(zona))
      .map(([dia]) => dia);
    return {
      nivel: 'ambar',
      msg: `${tec} no cubre ${zona} los ${diaNombre}s${diasSi.length ? ` · días habituales: ${diasSi.join(', ')}` : ''}`,
    };
  }, [extras.fechaProximoMantenimiento, extras.zona, extras.tecnicoAsignado, disponData, ausenciasPlantilla]);

  // Auto-fetch assets cuando clienteInicial proporciona un clienteId (ej. desde orden PM)
  useEffect(() => {
    if (!clienteInicial?.clienteId) return;
    fetch(`/.netlify/functions/searchClienteStelOrder?clientId=${encodeURIComponent(clienteInicial.clienteId)}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => {
        if (!d.success || !d.results?.[0]) return;
        const c = d.results[0];
        setClienteSel(prev => prev ? { ...prev, assets: c.assets || [], addressId: c.addressId || prev.addressId, codigoPostal: c.codigoPostal || prev.codigoPostal } : prev);
        if (c.dni) setClienteDni(c.dni);
        if (c.codigoPostal) {
          const zt = getZonaTecnicoFromCP(c.codigoPostal);
          if (zt) setExtras(e => ({ ...e, zona: e.zona || zt.zona, tecnicoAsignado: e.tecnicoAsignado || zt.tecnico }));
        }
        const nuos = (c.assets || []).filter(a =>
          a.brand?.toLowerCase().includes('ariston') || a.model?.toLowerCase().includes('nuos')
        );
        if (nuos.length === 1) {
          setAssetsSel([nuos[0]]);
          setEquipoManual(m => ({ ...m, marca: nuos[0].brand || 'Ariston', modelo: nuos[0].model || '', numeroSerie: nuos[0].serialNumber || '' }));
        }
      })
      .catch(() => {});
  }, []); // solo al montar — clienteInicial es estático

  // Auto-detectar zona/técnico cuando se escribe CP en formulario de nuevo cliente
  useEffect(() => {
    if (nuevoCliente.codigoPostal.length === 5) {
      const zt = getZonaTecnicoFromCP(nuevoCliente.codigoPostal);
      if (zt) setExtras(e => ({ ...e, zona: zt.zona, tecnicoAsignado: zt.tecnico }));
    }
  }, [nuevoCliente.codigoPostal]);

  // ── Paso 0: búsqueda ──────────────────────────────────────────────────────
  const buscarClientes = React.useCallback(async (q) => {
    if (q.trim().length < 2) { setResultados([]); return; }
    setBuscando(true);
    try {
      const r = await fetch(`/.netlify/functions/searchClienteStelOrder?q=${encodeURIComponent(q.trim())}`, { headers: { ...authService.authHeader() } });
      const d = await r.json();
      setResultados(d.success ? d.results : []);
    } catch { setResultados([]); }
    finally   { setBuscando(false); }
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => buscarClientes(val), 500);
  };

  const seleccionarCliente = (c) => {
    // Bloquear si ya tiene contrato activo
    const yaExiste = (existingData || []).find(
      m => m.clienteId && String(m.clienteId) === String(c.id) && ESTADOS_BLOQUEO_CONTRATO.includes(m.estado)
    );
    if (yaExiste) {
      const estadoLabel = ESTADO_CFG[yaExiste.estado]?.label || yaExiste.estado;
      const ref = yaExiste.referenciaContrato ? ` · Ref: ${yaExiste.referenciaContrato}` : '';
      setError(`⚠ Este cliente ya tiene un contrato activo (${estadoLabel}${ref}). No se puede crear otro.`);
      return;
    }
    setError('');
    setPosibleDuplicado(buscarPosibleDuplicado(c, existingData));
    setClienteSel(c);
    setClienteDni(c.dni || '');
    setQuery(c.clienteNombre);
    setResultados([]);
    if (c.codigoPostal) {
      const zt = getZonaTecnicoFromCP(c.codigoPostal);
      if (zt) setExtras(e => ({ ...e, zona: zt.zona, tecnicoAsignado: zt.tecnico }));
    }
    // Auto-select if only one Ariston/NUOS asset
    const nuos = (c.assets || []).filter(a =>
      a.brand?.toLowerCase().includes('ariston') || a.model?.toLowerCase().includes('nuos')
    );
    if (nuos.length === 1) {
      setAssetsSel([nuos[0]]);
      setEquipoManual(m => ({ ...m, marca: nuos[0].brand || 'Ariston', modelo: nuos[0].model || '', numeroSerie: nuos[0].serialNumber || '' }));
    } else {
      setAssetsSel([]);
    }
    setStep(1);
  };

  // ── Crear cliente en StelOrder ─────────────────────────────────────────────
  const handleCrearCliente = async () => {
    setGuardandoCliente(true); setError('');
    try {
      const r = await fetch('/.netlify/functions/createClient', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          nombreCompleto: nuevoCliente.nombre, telefono: nuevoCliente.telefono,
          email: nuevoCliente.email, direccion: nuevoCliente.direccion,
          codigoPostal: nuevoCliente.codigoPostal, localidad: nuevoCliente.localidad,
          provincia: nuevoCliente.provincia,
        }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error);
      const c = d.client;
      seleccionarCliente({
        id: c.id, clienteNombre: c.legalName || c.name || '',
        phone: c.phone || '', email: c.email || '',
        direccion: c.mainAddress?.['address-data'] || nuevoCliente.direccion,
        municipio: c.mainAddress?.['city-town'] || nuevoCliente.localidad,
        codigoPostal: c.mainAddress?.['postal-code'] || nuevoCliente.codigoPostal,
        addressId: c.mainAddressId || '', dni: '', assets: [],
      });
      setCreandoCliente(false);
      setNuevoCliente({ nombre: '', telefono: '', email: '', direccion: '', codigoPostal: '', localidad: '', provincia: '' });
    } catch (e) { setError(e.message); }
    finally { setGuardandoCliente(false); }
  };

  // ── Crear asset en StelOrder ───────────────────────────────────────────────
  const handleCrearAsset = async () => {
    setGuardandoAsset(true); setError('');
    try {
      const r = await fetch('/.netlify/functions/createAsset', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          clienteId: clienteSel.id, addressId: clienteSel.addressId,
          marca: nuevoAsset.marca, modelo: nuevoAsset.modelo,
          tipoEquipo: nuevoAsset.tipoEquipo, numeroSerie: nuevoAsset.numeroSerie,
          fechaInstalacion: nuevoAsset.fechaInstalacion,
        }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error);
      const a = d.asset;
      const assetObj = {
        id: a.id, brand: a.brand || nuevoAsset.marca,
        model: a.model || nuevoAsset.modelo, serialNumber: a.serialNumber || nuevoAsset.numeroSerie,
        installationDate: a.warrantyStartDate || '',
      };
      if (assetsSel.length === 0) setEquipoManual(m => ({ ...m, marca: assetObj.brand, modelo: assetObj.model, numeroSerie: assetObj.serialNumber }));
      setClienteSel(c => ({ ...c, assets: [...(c.assets || []), assetObj] }));
      setAssetsSel(prev => [...prev, assetObj]);
      setCreandoAsset(false);
      setNuevoAsset({ marca: 'Ariston', modelo: '', tipoEquipo: 'BOMBA DE CALOR', numeroSerie: '', fechaInstalacion: '' });
    } catch (e) { setError(e.message); }
    finally { setGuardandoAsset(false); }
  };

  // ── Build payload ──────────────────────────────────────────────────────────
  const buildPayload = () => {
    const primaryAsset = assetsSel[0] || null;
    const equipo = primaryAsset
      ? { marca: primaryAsset.brand || '', modelo: primaryAsset.model || '', numeroSerie: primaryAsset.serialNumber || '',
          assetId:  String(primaryAsset.id || ''),
          assetIds: assetsSel.map(a => String(a.id)),
          tipoEquipo: equipoManual.tipoEquipo,
          fechaInstalacion: primaryAsset.installationDate || equipoManual.fechaInstalacion || '' }
      : { ...equipoManual, assetId: '', assetIds: [] };

    return {
      clienteId:        String(clienteSel?.id || ''),
      addressId:        String(clienteSel?.addressId || ''),
      clienteNombre:    clienteSel?.clienteNombre || '',
      clienteTelefono:  clienteSel?.phone || '',
      clienteEmail:     clienteSel?.email || '',
      clienteDni:       clienteDni.trim().toUpperCase(),
      direccion:        clienteSel?.direccion || '',
      municipio:        clienteSel?.municipio || '',
      ...equipo,
      serviciosIncluidos: serviciosDesglose,
      pack:             packDerivado,
      precioAnual:      precioCalculado,
      numEquipos:       numEquiposAerotermia,
      extensionGarantia:cfg.esNuos && cfg.tiposServicio.includes('aerotermia'),
      tieneContrato:    true,
      estado:           'firmado_activo',
      tipoServicio,
      creadoPor:        user?.uid || user?.email || '',
      ...extras,
      ...(captadorData ? {
        captadoPorTecnicoId:     String(captadorData.tecnicoId     || ''),
        captadoPorTecnicoNombre: captadorData.tecnicoNombre        || '',
        comisionCaptacion:       Number(captadorData.comision)     || 0,
        workorderOrigenRef:      captadorData.workorderRef         || '',
      } : {}),
    };
  };

  // ── Paso 3: generar enlace firma ───────────────────────────────────────────
  const handleGenerarLink = async () => {
    setSaving(true); setError('');
    try {
      const payload = buildPayload();
      const r = await fetch('/.netlify/functions/createContratoToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ contratoData: payload }),
      });
      const d = await r.json();
      if (r.status === 409) throw new Error(`Este cliente ya tiene un contrato activo. ${d.error || ''}`);
      if (!d.success) throw new Error(d.error);
      setLinkGenerado({ url: d.url, contratoId: d.contratoId });
    } catch (e) { setError(e.message); }
    finally    { setSaving(false); }
  };

  const copiarLink = () => { if (linkGenerado) navigator.clipboard.writeText(linkGenerado.url).catch(() => {}); };
  const waLink = () => {
    if (!linkGenerado) return;
    const nombre  = (clienteSel?.clienteNombre || '').split(' ')[0];
    const equipo  = `${equipoManual.marca} ${equipoManual.modelo}`.trim() || 'su equipo';
    const precio  = precioCalculado.toFixed(2).replace('.', ',');
    const msg     = encodeURIComponent(
      `Hola ${nombre}, le enviamos el contrato de mantenimiento de su equipo *${equipo}*.\n\n` +
      `✅ Servicios: ${serviciosDesglose.join(', ')}\n💶 Precio anual: ${precio}€ + IVA (se abona en la propia visita)\n\n` +
      `Para firmar pulse aquí:\n${linkGenerado.url}\n\nEnlace válido 7 días. Cualquier duda llámenos.\n_Climatización Zapata_`
    );
    window.open(`https://wa.me/${normTel(clienteSel?.phone || '')}?text=${msg}`, '_blank');
  };

  const STEPS = ['Cliente', 'Equipo', 'Servicio', 'Enviar'];

  const aristonAssets = (clienteSel?.assets || []).filter(a =>
    a.brand?.toLowerCase().includes('ariston') || a.model?.toLowerCase().includes('nuos')
  );
  const otrosAssets = (clienteSel?.assets || []).filter(a => !aristonAssets.includes(a));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] flex flex-col" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
          <div className="w-9 h-9 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icons.Wrench className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">Nuevo contrato de mantenimiento</h3>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              {STEPS.map((s, i) => (
                <React.Fragment key={i}>
                  <span className={`text-xs font-semibold ${i === step ? 'text-red-600' : i < step ? 'text-green-600' : 'text-gray-300'}`}>
                    {i < step ? '✓' : `${i + 1}.`} {s}
                  </span>
                  {i < STEPS.length - 1 && <span className="text-gray-200 text-xs">›</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-2">✕</button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          {posibleDuplicado && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-3 py-2 text-sm">
              ⚠ Posible duplicado: ya existe un registro de <strong>{posibleDuplicado.clienteNombre}</strong> con el mismo teléfono o nombre
              {posibleDuplicado.estado ? ` (estado: ${ESTADO_CFG[posibleDuplicado.estado]?.label || posibleDuplicado.estado})` : ''}.
              Comprueba que no sea la misma persona antes de seguir.
            </div>
          )}

          {/* ── PASO 0: búsqueda de cliente ─────────────────────────────── */}
          {step === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Busca por nombre, teléfono o dirección en StelOrder.</p>
              <div className="relative">
                <input
                  className={inputCls + ' pr-10'}
                  value={query}
                  onChange={handleQueryChange}
                  placeholder="Nombre, 6XX XXX XXX, Calle…"
                  autoFocus
                />
                {buscando && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
                  </div>
                )}
              </div>
              {resultados.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                  {resultados.map((r, i) => (
                    <button key={i} onClick={() => seleccionarCliente(r)}
                      className="w-full text-left px-4 py-4 hover:bg-gray-50 active:bg-gray-100 transition-colors">
                      <p className="font-semibold text-base text-gray-900">{r.clienteNombre}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{r.phone}{r.municipio ? ` · ${r.municipio}` : ''}</p>
                      {r.assets?.length > 0 && (
                        <p className="text-xs text-blue-500 mt-1">{r.assets.length} equipo{r.assets.length > 1 ? 's' : ''} registrado{r.assets.length > 1 ? 's' : ''}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {query.length >= 2 && !buscando && resultados.length === 0 && !creandoCliente && (
                <div className="text-center py-2 space-y-2">
                  <p className="text-sm text-gray-400">No encontrado. Prueba otro término.</p>
                  <button onClick={() => setCreandoCliente(true)} className="text-sm font-semibold text-red-600 hover:text-red-700">+ Crear nuevo cliente en StelOrder</button>
                </div>
              )}

              {creandoCliente && (
                <div className="border border-red-200 rounded-xl p-4 space-y-3 bg-red-50/30">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Nuevo cliente</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className={lblCls}>Nombre completo <span className="text-red-500">*</span></label>
                      <input className={inputCls} value={nuevoCliente.nombre} onChange={e => setNC('nombre', e.target.value)} placeholder="Nombre o razón social" />
                    </div>
                    <div>
                      <label className={lblCls}>Teléfono <span className="text-red-500">*</span></label>
                      <input className={inputCls} value={nuevoCliente.telefono} onChange={e => setNC('telefono', e.target.value)} placeholder="6XX XXX XXX" type="tel" />
                    </div>
                    <div>
                      <label className={lblCls}>Email</label>
                      <input className={inputCls} value={nuevoCliente.email} onChange={e => setNC('email', e.target.value)} placeholder="correo@ejemplo.com" type="email" />
                    </div>
                    <div className="col-span-2">
                      <label className={lblCls}>Dirección <span className="text-red-500">*</span></label>
                      <input className={inputCls} value={nuevoCliente.direccion} onChange={e => setNC('direccion', e.target.value)} placeholder="Calle, número..." />
                    </div>
                    <div>
                      <label className={lblCls}>Código postal <span className="text-red-500">*</span></label>
                      <input className={inputCls} value={nuevoCliente.codigoPostal} onChange={e => setNC('codigoPostal', e.target.value)} placeholder="03001" maxLength={5} />
                    </div>
                    <div>
                      <label className={lblCls}>Localidad <span className="text-red-500">*</span></label>
                      <input className={inputCls} value={nuevoCliente.localidad} onChange={e => setNC('localidad', e.target.value)} placeholder="Ciudad" />
                    </div>
                    <div>
                      <label className={lblCls}>Provincia <span className="text-red-500">*</span></label>
                      <input className={inputCls} value={nuevoCliente.provincia} onChange={e => setNC('provincia', e.target.value)} placeholder="Alicante" />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => setCreandoCliente(false)} className="flex-1 py-2 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50">Cancelar</button>
                    <button onClick={handleCrearCliente} disabled={guardandoCliente || !nuevoCliente.nombre || !nuevoCliente.telefono || !nuevoCliente.direccion || !nuevoCliente.codigoPostal || !nuevoCliente.localidad || !nuevoCliente.provincia}
                      className="flex-1 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-xl">
                      {guardandoCliente ? 'Creando...' : 'Crear cliente'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── PASO 1: cliente + equipo ────────────────────────────────── */}
          {step === 1 && clienteSel && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <p className="font-bold text-green-800 text-sm">{clienteSel.clienteNombre}</p>
                <p className="text-xs text-green-600">{clienteSel.phone} · {clienteSel.municipio}</p>
                {clienteSel.email && <p className="text-xs text-green-500">{clienteSel.email}</p>}
              </div>

              <div>
                <label className={lblCls}>DNI / NIE del titular</label>
                <input
                  className={inputCls}
                  placeholder="12345678A"
                  maxLength={9}
                  value={clienteDni}
                  onChange={e => setClienteDni(e.target.value.toUpperCase())}
                />
              </div>

              {aristonAssets.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Equipos Ariston / NUOS <span className="font-normal normal-case">(selecciona todos los que se van a mantener)</span></p>
                  {aristonAssets.map(a => {
                    const sel = assetsSel.some(s => s.id === a.id);
                    return (
                      <button key={a.id} onClick={() => {
                        if (sel) {
                          setAssetsSel(assetsSel.filter(s => s.id !== a.id));
                        } else {
                          if (assetsSel.length === 0) setEquipoManual(m => ({ ...m, marca: a.brand || 'Ariston', modelo: a.model || '', numeroSerie: a.serialNumber || '' }));
                          setAssetsSel([...assetsSel, a]);
                        }
                      }}
                        className={`w-full text-left px-4 py-2.5 rounded-xl border-2 transition-colors mb-2 ${sel ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center text-xs ${sel ? 'border-red-500 bg-red-500 text-white' : 'border-gray-300'}`}>{sel ? '✓' : ''}</span>
                          <div>
                            <p className="font-bold text-sm text-gray-800">{a.brand} {a.model}</p>
                            {a.serialNumber && <p className="text-xs text-gray-400">S/N: {a.serialNumber}</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {otrosAssets.length > 0 && (
                <details>
                  <summary className="text-xs text-gray-400 cursor-pointer select-none">Otros equipos ({otrosAssets.length})</summary>
                  <div className="mt-2">
                    {otrosAssets.map(a => {
                      const sel = assetsSel.some(s => s.id === a.id);
                      return (
                        <button key={a.id} onClick={() => {
                          if (sel) {
                            setAssetsSel(assetsSel.filter(s => s.id !== a.id));
                          } else {
                            if (assetsSel.length === 0) setEquipoManual(m => ({ ...m, marca: a.brand || '', modelo: a.model || '', numeroSerie: a.serialNumber || '' }));
                            setAssetsSel([...assetsSel, a]);
                          }
                        }}
                          className={`w-full text-left px-4 py-2.5 rounded-xl border-2 transition-colors mb-2 ${sel ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center text-xs ${sel ? 'border-red-500 bg-red-500 text-white' : 'border-gray-300'}`}>{sel ? '✓' : ''}</span>
                            <div>
                              <p className="font-bold text-sm text-gray-800">{a.brand} {a.model}</p>
                              {a.serialNumber && <p className="text-xs text-gray-400">S/N: {a.serialNumber}</p>}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </details>
              )}

              {/* Botón añadir nuevo equipo (siempre visible si hay cliente) */}
              {!creandoAsset && (
                <button onClick={() => setCreandoAsset(true)} className="w-full text-sm text-red-600 hover:text-red-700 font-semibold border border-dashed border-red-300 rounded-xl py-2 hover:border-red-400 transition-colors">
                  + Añadir nuevo equipo en StelOrder
                </button>
              )}

              {creandoAsset && (
                <div className="border border-red-200 rounded-xl p-4 space-y-3 bg-red-50/30">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Nuevo equipo</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lblCls}>Marca <span className="text-red-500">*</span></label>
                      <input className={inputCls} value={nuevoAsset.marca} onChange={e => setNA('marca', e.target.value)} placeholder="Ariston, Daikin…" />
                    </div>
                    <div>
                      <label className={lblCls}>Modelo <span className="text-red-500">*</span></label>
                      <input className={inputCls} value={nuevoAsset.modelo} onChange={e => setNA('modelo', e.target.value)} placeholder="NUOS R 80 V…" />
                    </div>
                    <div>
                      <label className={lblCls}>Tipo <span className="text-red-500">*</span></label>
                      <select className={inputCls} value={nuevoAsset.tipoEquipo} onChange={e => setNA('tipoEquipo', e.target.value)}>
                        {TIPOS_ASSET.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={lblCls}>Nº serie</label>
                      <input className={inputCls} value={nuevoAsset.numeroSerie} onChange={e => setNA('numeroSerie', e.target.value)} placeholder="SN123456" />
                    </div>
                    <div>
                      <label className={lblCls}>Fecha instalación</label>
                      <input type="date" className={inputCls} value={nuevoAsset.fechaInstalacion} onChange={e => setNA('fechaInstalacion', e.target.value)} />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => setCreandoAsset(false)} className="flex-1 py-2 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50">Cancelar</button>
                    <button onClick={handleCrearAsset} disabled={guardandoAsset || !nuevoAsset.marca || !nuevoAsset.modelo}
                      className="flex-1 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-xl">
                      {guardandoAsset ? 'Creando...' : 'Crear equipo'}
                    </button>
                  </div>
                </div>
              )}

              {(clienteSel.assets || []).length === 0 && !creandoAsset && (
                <div className="space-y-3">
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">Sin equipos en StelOrder. Introduce los datos del equipo o créalo arriba.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {['marca', 'modelo', 'numeroSerie'].map(k => (
                      <div key={k}>
                        <label className={lblCls}>{k === 'numeroSerie' ? 'Nº serie' : k.charAt(0).toUpperCase() + k.slice(1)}</label>
                        <input className={inputCls} value={equipoManual[k]} onChange={e => setEquipoManual(m => ({ ...m, [k]: e.target.value }))} />
                      </div>
                    ))}
                    <div>
                      <label className={lblCls}>Tipo de equipo</label>
                      <select className={inputCls} value={equipoManual.tipoEquipo} onChange={e => setEquipoManual(m => ({ ...m, tipoEquipo: e.target.value }))}>
                        {Object.entries(TIPOS_EQUIPO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Extra: técnico, zona, canal */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                <div>
                  <label className={lblCls}>Técnico asignado</label>
                  <select className={inputCls} value={extras.tecnicoAsignado} onChange={e => setExt('tecnicoAsignado', e.target.value)}>
                    <option value="">Sin asignar</option>
                    {TECNICOS_NOMBRES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lblCls}>Zona</label>
                  <select className={inputCls} value={extras.zona} onChange={e => setExt('zona', e.target.value)}>
                    <option value="">—</option>
                    {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lblCls}>Canal captación</label>
                  <select className={inputCls} value={extras.canalCaptacion} onChange={e => setExt('canalCaptacion', e.target.value)}>
                    {Object.entries(CANALES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lblCls}>Modo de cobro</label>
                  <select className={inputCls} value={extras.modoCobro} onChange={e => setExt('modoCobro', e.target.value)}>
                    <option value="visita">En visita (cobra el técnico)</option>
                    <option value="sepa">Domiciliado (SEPA)</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Por defecto en visita — se puede domiciliar más adelante al renovar.</p>
                </div>
                <div>
                  <label className={lblCls}>Próx. mantenimiento</label>
                  <input type="date" className={inputCls} min={hoy()} value={extras.fechaProximoMantenimiento} onChange={e => setExt('fechaProximoMantenimiento', e.target.value)} />
                  <p className="text-xs text-gray-400 mt-1">
                    {tipoServicio === 'este_anio'
                      ? '→ Este año · la visita se agenda automáticamente al firmar'
                      : '→ Próximo año · la visita se agenda más adelante, a mano'}
                  </p>
                  {disponCheck && (
                    <p className={`text-xs mt-0.5 font-medium ${
                      disponCheck.nivel === 'verde' ? 'text-green-600' :
                      disponCheck.nivel === 'ambar' ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {disponCheck.nivel === 'verde' ? '✓' : disponCheck.nivel === 'ambar' ? '⚠' : '✕'} {disponCheck.msg}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-between pt-1">
                <button onClick={() => { setClienteSel(null); setStep(0); }} className="text-sm text-gray-500 hover:text-gray-700">← Volver</button>
                <button onClick={() => { setError(''); setStep(2); }} disabled={aristonAssets.length > 0 && assetsSel.length === 0}
                  className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-xl">
                  Continuar →
                </button>
              </div>
            </div>
          )}

          {/* ── PASO 2: configuración del servicio ──────────────────────── */}
          {step === 2 && (
            <div className="space-y-5">

              {/* Q1: servicios */}
              <div>
                <p className="text-sm font-bold text-gray-800 mb-2">¿Qué servicios incluye el contrato?</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'aerotermia',           label: 'Aerotermia',           icon: '💧', sub: 'pared 105€ / suelo 125€' },
                    { key: 'aerotermiaMultitarea', label: 'Aerotermia multitarea', icon: '🌡️', sub: '220€' },
                    { key: 'airAcondicionado',     label: 'Aire acondicionado',   icon: '❄️', sub: 'split / conductos' },
                    { key: 'caldera',              label: 'Caldera',              icon: '🔥', sub: '120€' },
                  ].map(({ key, label, icon, sub }) => (
                    <button key={key} onClick={() => toggleServicio(key)}
                      className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl border-2 transition-colors text-left ${cfg.tiposServicio.includes(key) ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <span className="text-lg leading-none">{icon}</span>
                      <span className="text-sm font-semibold text-gray-800 mt-1">{label}</span>
                      <span className="text-xs text-gray-400">{sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Q2: pared o suelo */}
              {cfg.tiposServicio.includes('aerotermia') && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-2">¿El equipo de aerotermia es de pared o de suelo?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'pared', label: 'Equipo de pared',  sub: '80 / 110 / 150 L monoblock', precio: '105€' },
                      { v: 'suelo', label: 'Equipo de suelo',  sub: 'NUOS Split, suelo…',          precio: '125€' },
                    ].map(({ v, label, sub, precio }) => (
                      <button key={v} onClick={() => setCfgF('tipoAerotermia', v)}
                        className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${cfg.tipoAerotermia === v ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <p className="text-sm font-semibold text-gray-800">{label}</p>
                        <p className="text-xs text-gray-400">{sub}</p>
                        <p className="text-xs font-bold text-red-600 mt-0.5">{precio}/año</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Q3: ¿NUOS Ariston? */}
              {cfg.tiposServicio.includes('aerotermia') && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-2">¿Es Ariston NUOS?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: true,  label: 'Sí, NUOS Ariston', sub: '+ ext. garantía 3→5 años' },
                      { v: false, label: 'No, otra marca',    sub: 'Sin extensión de garantía' },
                    ].map(({ v, label, sub }) => (
                      <button key={String(v)} onClick={() => setCfgF('esNuos', v)}
                        className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${cfg.esNuos === v ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <p className="text-sm font-semibold text-gray-800">{label}</p>
                        <p className="text-xs text-gray-400">{sub}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Q4: split y/o conductos — no son excluyentes, se pueden marcar los dos */}
              {cfg.tiposServicio.includes('airAcondicionado') && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-2">
                    ¿Tipo de equipo de aire acondicionado? <span className="font-normal text-gray-400">(puedes marcar los dos)</span>
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: 'split',    label: 'Split',    sub: '25€/unidad interior' },
                      { v: 'conductos',label: 'Conductos', sub: '40€/unidad interior' },
                    ].map(({ v, label, sub }) => (
                      <button key={v} onClick={() => toggleTipoSplit(v)}
                        className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${cfg.tipoSplit.includes(v) ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <p className="text-sm font-semibold text-gray-800">{label}{cfg.tipoSplit.includes(v) ? ' ✓' : ''}</p>
                        <p className="text-xs text-gray-400">{sub}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Q5: número de unidades interiores split */}
              {cfg.tiposServicio.includes('airAcondicionado') && cfg.tipoSplit.includes('split') && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-2">¿Cuántas unidades interiores split?</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map(n => (
                      <button key={n} onClick={() => setCfgF('numSplitsInterior', n)}
                        className={`text-center py-2.5 rounded-xl border-2 transition-colors ${cfg.numSplitsInterior === n ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <p className="text-sm font-semibold text-gray-800">{n === 4 ? '4+' : n}</p>
                        <p className="text-xs text-gray-400">{(n * 25)}€</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Q6: número de unidades interiores conductos */}
              {cfg.tiposServicio.includes('airAcondicionado') && cfg.tipoSplit.includes('conductos') && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-2">¿Cuántas máquinas de conductos (unidad interior)?</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map(n => (
                      <button key={n} onClick={() => setCfgF('numConductosInterior', n)}
                        className={`text-center py-2.5 rounded-xl border-2 transition-colors ${(cfg.numConductosInterior || 1) === n ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <p className="text-sm font-semibold text-gray-800">{n === 4 ? '4+' : n}</p>
                        <p className="text-xs text-gray-400">{n * 40}€</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Q7: unidades exteriores — un único contador, compartido entre
                  split y conductos (mismo coste, 50€/u, da igual con qué
                  máquina interior vayan). */}
              {cfg.tiposServicio.includes('airAcondicionado') && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-1.5">
                    Unidades exteriores <span className="font-normal text-gray-400">(+50€ c/u)</span>
                  </p>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setCfgF('numUnidadesExterior', Math.max(0, (cfg.numUnidadesExterior || 0) - 1))}
                      className="w-10 h-10 rounded-xl border-2 border-gray-200 hover:border-red-400 text-xl font-bold text-gray-600 hover:text-red-600 transition-colors flex items-center justify-center">−</button>
                    <span className="w-10 text-center text-lg font-bold text-gray-900">{cfg.numUnidadesExterior || 0}</span>
                    <button onClick={() => setCfgF('numUnidadesExterior', (cfg.numUnidadesExterior || 0) + 1)}
                      className="w-10 h-10 rounded-xl border-2 border-gray-200 hover:border-red-400 text-xl font-bold text-gray-600 hover:text-red-600 transition-colors flex items-center justify-center">+</button>
                    {(cfg.numUnidadesExterior || 0) === 0
                      ? <span className="text-sm text-gray-400">Sin exterior</span>
                      : <span className="text-sm font-semibold text-red-600">+{(cfg.numUnidadesExterior || 0) * 50}€</span>
                    }
                  </div>
                </div>
              )}

              {/* Resumen pack + precio desglosado */}
              {cfg.tiposServicio.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PACKS[packDerivado]?.cls || 'bg-gray-100 text-gray-600'}`}>
                        {PACKS[packDerivado]?.label || packDerivado}
                      </span>
                      {cfg.tiposServicio.includes('aerotermia') && numEquiposAerotermia > 1 && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                          {numEquiposAerotermia} equipos
                        </span>
                      )}
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{precioCalculado.toFixed(2).replace('.', ',')}€<span className="text-sm font-normal text-gray-400">/año</span></p>
                  </div>
                  <div className="border-t border-red-200 pt-2 space-y-1">
                    {desglosePrecio.map((l, i) => (
                      <div key={i} className="flex justify-between text-xs text-gray-600">
                        <span>✓ {l.label}</span>
                        {l.importe != null && <span className="font-semibold">{l.importe}€</span>}
                      </div>
                    ))}
                    {cfg.esNuos && cfg.tiposServicio.includes('aerotermia') && (
                      <div className="text-xs text-gray-600">✓ Extensión de garantía 3→5 años (NUOS)</div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 pt-1">+ IVA 21% → <strong>{(precioCalculado * 1.21).toFixed(2).replace('.', ',')}€ con IVA</strong></p>
                </div>
              )}

              <div className="flex justify-between pt-1">
                <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700">← Volver</button>
                <button onClick={() => { setError(''); setStep(3); }}
                  disabled={cfg.tiposServicio.length === 0}
                  className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-xl">
                  Continuar →
                </button>
              </div>
            </div>
          )}

          {/* ── PASO 3: generar enlace de firma ─────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4">
              {!linkGenerado ? (
                <>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm">
                    <p className="font-bold text-gray-800 mb-1">{clienteSel?.clienteNombre}</p>
                    <p className="text-gray-500">{serviciosDesglose.join(' · ')}</p>
                    <p className="font-bold text-gray-900 mt-1">{precioCalculado.toFixed(2).replace('.', ',')}€/año <span className="text-xs font-normal text-gray-400">+ IVA 21% = {(precioCalculado * 1.21).toFixed(2).replace('.', ',')}€</span></p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700">
                    <p className="font-semibold mb-1">El cliente introducirá su IBAN al firmar</p>
                    <p className="text-xs text-blue-600">Se enviará un enlace de firma seguro válido 7 días.</p>
                  </div>
                  <div className="flex justify-between pt-1">
                    <button onClick={() => setStep(2)} className="text-sm text-gray-500 hover:text-gray-700">← Volver</button>
                    <button onClick={handleGenerarLink} disabled={saving}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-xl">
                      {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                      Generar enlace de firma
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                    <p className="font-bold text-green-800 text-sm mb-1">✓ Contrato creado — enlace listo</p>
                    <p className="text-xs text-green-600 font-mono break-all">{linkGenerado.url}</p>
                    <p className="text-xs text-green-500 mt-1">Válido 7 días · Un solo uso</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {clienteSel?.phone && (
                      <button onClick={waLink}
                        className="flex flex-col items-center gap-1.5 px-3 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs font-bold">
                        <span className="text-xl">💬</span> WhatsApp
                      </button>
                    )}
                    {clienteSel?.email && (
                      <a href={`mailto:${clienteSel.email}?subject=Contrato mantenimiento&body=Estimado ${clienteSel.clienteNombre},%0A%0ALe enviamos el enlace para firmar su contrato de mantenimiento:%0A${encodeURIComponent(linkGenerado.url)}%0A%0AGracias,%0AClimatización Zapata`}
                        className="flex flex-col items-center gap-1.5 px-3 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold">
                        <span className="text-xl">✉️</span> Email
                      </a>
                    )}
                    <button onClick={copiarLink}
                      className="flex flex-col items-center gap-1.5 px-3 py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-xl text-xs font-bold">
                      <span className="text-xl">📋</span> Copiar
                    </button>
                  </div>
                  <button onClick={() => onSaved(linkGenerado.contratoId)}
                    className="w-full py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl">
                    Hecho — cerrar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal Registrar Llamada (Captación) ──────────────────────────────────────

function RegistrarLlamadaModal({ item, onClose, onSaved }) {
  const [resultado,      setResultado]      = useState('interesado');
  const [nota,           setNota]           = useState('');
  const [proximoContacto,setProximoContacto]= useState('');
  const [modoCobro,      setModoCobro]      = useState('visita'); // por defecto en visita
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const entrada = { fecha: hoy(), resultado, nota, proximoContacto };
      await saveApi({
        id: item.id,
        historialContactos:  [...(item.historialContactos || []), entrada],
        ultimoContacto:      hoy(),
        resultadoUltimoContacto: resultado,
        proximoContacto,
        // modoCobro explícito: por defecto todo contrato nuevo es "en visita"
        // (igual que createContratoToken.js) — sin esto, el campo se quedaba
        // sin definir y reenviarEnlaceContrato.js lo trataba como SEPA al
        // generar el enlace de firma (esa función, al no saber el modo,
        // asume lo contrario: SEPA, por seguridad de pedir IBAN de más).
        ...(resultado === 'firmado' ? { estado: 'contrato_enviado', tieneContrato: true, modoCobro, fechaInicioContrato: hoy(), fechaVencimientoContrato: en1Ano(), fechaProximoMantenimiento: en1Ano() } : {}),
        ...(resultado === 'no_interesado' ? { estado: 'no_interesado' } : {}),
        // "Interesado" tiene su propia columna en el Kanban (getKanbanColId) —
        // antes solo "Firmado" y "No interesado" movían la tarjeta, así que
        // marcar una llamada como "Interesado" no la sacaba de "Contactado"
        // hasta que alguien la arrastrara a mano aparte.
        ...(resultado === 'interesado' ? { estado: 'interesado' } : {}),
      });
      onSaved();
    } catch (e) { setError(e.message); }
    finally    { setSaving(false); }
  };

  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Registrar llamada</h3>
            <p className="text-xs text-gray-400">{item.clienteNombre}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        <div className="px-6 py-5 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Resultado</label>
            <div className="grid grid-cols-2 gap-2">
              {RESULTADOS_LLAMADA.map(r => (
                <button key={r.value} onClick={() => setResultado(r.value)}
                  className={`px-3 py-2 text-sm font-semibold rounded-xl border-2 transition-colors ${resultado === r.value ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Nota</label>
            <textarea rows={2} className={inputCls} value={nota} onChange={e => setNota(e.target.value)} placeholder="Resumen de la llamada..." />
          </div>
          {resultado === 'vuelvo_a_llamar' && (
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Próximo contacto</label>
              <input type="date" className={inputCls} min={hoy()} value={proximoContacto} onChange={e => setProximoContacto(e.target.value)} />
            </div>
          )}
          {resultado === 'firmado' && (
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Modo de cobro</label>
              <select className={inputCls} value={modoCobro} onChange={e => setModoCobro(e.target.value)}>
                <option value="visita">En visita (cobra el técnico)</option>
                <option value="sepa">Domiciliado (SEPA)</option>
              </select>
            </div>
          )}
        </div>
        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl">
            {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Ampliar contrato ──────────────────────────────────────────────────

function AmpliacionContratoModal({ item, onClose, onSaved }) {
  const [cfg, setCfg] = useState({
    tiposServicio: [],
    tipoAerotermia: 'pared',
    tipoSplit: [], // qué tipo(s) de AC se van a AÑADIR — solo los que faltan (ver hasSplitAC/hasConductosAC)
    numSplitsInterior: 1,
    numConductosInterior: 1,
    numUnidadesExterior: 0, // compartido entre split y conductos (mismo coste, 50€/u)
    esNuos: false,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const serviciosTxt  = (item.serviciosIncluidos || []).join(' · ').toLowerCase();
  const hasAeroMulti  = /aerotermia multitarea/.test(serviciosTxt);
  const hasAerotermia = /mantenimiento aerotermia \(equipo/.test(serviciosTxt) || (!hasAeroMulti && ['basico', 'silver', 'gold'].includes(item.pack));
  // Split/conductos por separado — antes "hasAC" era todo-o-nada y ocultaba
  // el añadido de AC en cuanto el contrato ya tenía UNO de los dos tipos,
  // sin dejar añadir el que le faltaba (el caso real: un contrato con split
  // que también necesita conductos, o al revés).
  const hasSplitAC     = /interior(?:es)? split/.test(serviciosTxt);
  const hasConductosAC = /interior(?:es)? conductos/.test(serviciosTxt);
  const hasAC          = hasSplitAC || hasConductosAC || /aire acondicionado/.test(serviciosTxt) || ['ac_solo', 'silver', 'gold'].includes(item.pack);
  // Si el pack ya indica AC pero el texto no distingue tipo (contratos
  // antiguos con otro formato de texto), no se sabe cuál falta — se ofrecen
  // los dos en vez de esconder la opción entera.
  const tipoDesconocido   = hasAC && !hasSplitAC && !hasConductosAC;
  const faltaSplitAC      = tipoDesconocido || !hasSplitAC;
  const faltaConductosAC  = tipoDesconocido || !hasConductosAC;
  const hasCaldera    = item.pack === 'caldera' || /mantenimiento caldera/.test(serviciosTxt);
  // Cualquiera de las dos aerotermias ya cubre "aerotermia" a efectos de añadir
  const hasAlgunaAero = hasAerotermia || hasAeroMulti;

  const setCfgF = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const toggleServicio = (key) => setCfg(c => ({
    ...c,
    tiposServicio: c.tiposServicio.includes(key)
      ? c.tiposServicio.filter(s => s !== key)
      : [...c.tiposServicio, key],
  }));
  const toggleTipoSplit = (v) => setCfg(c => ({
    ...c,
    tipoSplit: c.tipoSplit.includes(v) ? c.tipoSplit.filter(t => t !== v) : [...c.tipoSplit, v],
  }));

  // Calculate additional price without zone surcharge (already charged in current precioAnual)
  const deltaPrecio    = calcularPrecioServicio(cfg, null, 1);
  const deltaDesglose  = calcularDesglosePrecio(cfg, null, 1);
  const newPrecioAnual = (item.precioAnual || 0) + deltaPrecio;

  // New pack: union of existing + new services
  const allTiposServicio = [
    ...(hasAerotermia ? ['aerotermia'] : []),
    ...(hasAeroMulti ? ['aerotermiaMultitarea'] : []),
    ...(hasAC ? ['airAcondicionado'] : []),
    ...(hasCaldera ? ['caldera'] : []),
    ...cfg.tiposServicio,
  ];
  // tipoSplit para el pack = lo que YA tenía el contrato + lo que se añade
  // ahora — sin esto, ampliar un contrato "gold" (conductos) con un servicio
  // sin relación (p.ej. caldera) recalculaba el pack ignorando que ya tenía
  // conductos y lo bajaba a "silver" por error.
  const tipoSplitParaPack = [
    ...(hasSplitAC ? ['split'] : []),
    ...(hasConductosAC ? ['conductos'] : []),
    ...cfg.tipoSplit,
  ];
  const newPack = derivarPack({
    tiposServicio: allTiposServicio,
    tipoSplit: tipoSplitParaPack,
    numSplitsInterior: cfg.numSplitsInterior,
  });

  const handleConfirm = async () => {
    if (cfg.tiposServicio.length === 0) return setError('Selecciona al menos un servicio a añadir.');
    setSaving(true); setError('');
    try {
      const newServicios = [
        ...(item.serviciosIncluidos || []),
        ...derivarServicios(cfg, null, 1),
      ];
      await saveApi({
        id: item.id,
        pack: newPack,
        precioAnual: newPrecioAnual,
        serviciosIncluidos: newServicios,
        updatedAt: hoy(),
      });
      // Contrato todavía sin firmar: el enlace ya enviado al cliente guarda su
      // PROPIA instantánea de los servicios (tokens_firma), independiente de
      // este documento — sin este paso, el cliente seguiría viendo la
      // versión vieja aunque acabemos de corregirla aquí. reenviarEnlaceContrato.js
      // ya sabe actualizar esa instantánea sin cambiar la URL si el token
      // sigue vigente. Best-effort: si falla, el contrato queda corregido
      // igual, solo falta reenviar el enlace a mano.
      if (item._storedEstado === 'contrato_enviado') {
        try {
          await fetch('/.netlify/functions/reenviarEnlaceContrato', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
            body: JSON.stringify({
              contratoId: item.id,
              contratoData: {
                clienteNombre: item.clienteNombre, clienteEmail: item.clienteEmail,
                clienteTelefono: item.clienteTelefono, clienteDni: item.clienteDni,
                direccion: item.direccion, municipio: item.municipio,
                marca: item.marca, modelo: item.modelo, numeroSerie: item.numeroSerie,
                serviciosIncluidos: newServicios,
                pack: newPack,
                precioAnual: newPrecioAnual,
                tipoServicio: item.tipoServicio || 'siguiente_anio',
                referenciaMandatoSepa: item.referenciaMandatoSepa || '',
              },
            }),
          });
        } catch { /* el contrato ya se guardó bien; el enlace se puede reenviar a mano después */ }
      }
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h3 className="font-bold text-gray-900">Ampliar contrato</h3>
            <p className="text-xs text-gray-400">{item.clienteNombre}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}

          {/* Servicios actuales */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Servicios actuales</p>
            <div className="flex flex-wrap gap-1.5">
              {(item.serviciosIncluidos || []).map((s, i) => (
                <span key={i} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">{s}</span>
              ))}
            </div>
            <p className="text-sm font-semibold text-gray-700 mt-2">{Number(item.precioAnual || 0).toFixed(2).replace('.', ',')}€/año (sin IVA)</p>
          </div>

          {/* Servicio a añadir */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">¿Qué servicio quieres añadir?</p>
            <div className="grid grid-cols-2 gap-2">
              {!hasAlgunaAero && (
                <button onClick={() => toggleServicio('aerotermia')}
                  className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl border-2 transition-colors text-left ${cfg.tiposServicio.includes('aerotermia') ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-lg leading-none">💧</span>
                  <span className="text-sm font-semibold text-gray-800 mt-1">Aerotermia</span>
                  <span className="text-xs text-gray-400">pared 105€ / suelo 125€</span>
                </button>
              )}
              {!hasAlgunaAero && (
                <button onClick={() => toggleServicio('aerotermiaMultitarea')}
                  className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl border-2 transition-colors text-left ${cfg.tiposServicio.includes('aerotermiaMultitarea') ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-lg leading-none">🌡️</span>
                  <span className="text-sm font-semibold text-gray-800 mt-1">Aerotermia multitarea</span>
                  <span className="text-xs text-gray-400">220€</span>
                </button>
              )}
              {(faltaSplitAC || faltaConductosAC) && (
                <button onClick={() => toggleServicio('airAcondicionado')}
                  className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl border-2 transition-colors text-left ${cfg.tiposServicio.includes('airAcondicionado') ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-lg leading-none">❄️</span>
                  <span className="text-sm font-semibold text-gray-800 mt-1">Aire acondicionado</span>
                  <span className="text-xs text-gray-400">
                    {hasSplitAC && !hasConductosAC ? 'añadir conductos' : hasConductosAC && !hasSplitAC ? 'añadir split' : 'split / conductos'}
                  </span>
                </button>
              )}
              {!hasCaldera && (
                <button onClick={() => toggleServicio('caldera')}
                  className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl border-2 transition-colors text-left ${cfg.tiposServicio.includes('caldera') ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-lg leading-none">🔥</span>
                  <span className="text-sm font-semibold text-gray-800 mt-1">Caldera</span>
                  <span className="text-xs text-gray-400">120€</span>
                </button>
              )}
            </div>
          </div>

          {/* Config nueva aerotermia */}
          {cfg.tiposServicio.includes('aerotermia') && (
            <div>
              <p className="text-sm font-bold text-gray-800 mb-2">¿El equipo es de pared o de suelo?</p>
              <div className="grid grid-cols-2 gap-2">
                {[{ v: 'pared', label: 'Equipo de pared', precio: '105€' }, { v: 'suelo', label: 'Equipo de suelo', precio: '125€' }].map(({ v, label, precio }) => (
                  <button key={v} onClick={() => setCfgF('tipoAerotermia', v)}
                    className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${cfg.tipoAerotermia === v ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <p className="text-sm font-semibold text-gray-800">{label}</p>
                    <p className="text-xs font-bold text-red-600 mt-0.5">{precio}/año</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Config nuevo AC — solo se ofrece el tipo que todavía no tenga el
              contrato (si ya tiene split, aquí solo se puede añadir
              conductos, y viceversa; si no tiene ninguno, se pueden marcar
              los dos). */}
          {cfg.tiposServicio.includes('airAcondicionado') && (
            <>
              <div>
                <p className="text-sm font-bold text-gray-800 mb-2">¿Tipo de equipo a añadir?</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: 'split', label: 'Split', sub: '25€/unidad interior', falta: faltaSplitAC },
                    { v: 'conductos', label: 'Conductos', sub: '40€/unidad interior', falta: faltaConductosAC },
                  ].filter(o => o.falta).map(({ v, label, sub }) => (
                    <button key={v} onClick={() => toggleTipoSplit(v)}
                      className={`text-left px-4 py-3 rounded-xl border-2 transition-colors ${cfg.tipoSplit.includes(v) ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <p className="text-sm font-semibold text-gray-800">{label}{cfg.tipoSplit.includes(v) ? ' ✓' : ''}</p>
                      <p className="text-xs text-gray-400">{sub}</p>
                    </button>
                  ))}
                </div>
              </div>
              {cfg.tipoSplit.includes('split') && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-2">¿Cuántas unidades interiores split?</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map(n => (
                      <button key={n} onClick={() => setCfgF('numSplitsInterior', n)}
                        className={`text-center py-2.5 rounded-xl border-2 transition-colors ${cfg.numSplitsInterior === n ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <p className="text-sm font-semibold text-gray-800">{n === 4 ? '4+' : n}</p>
                        <p className="text-xs text-gray-400">{n * 25}€</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {cfg.tipoSplit.includes('conductos') && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-2">¿Cuántas máquinas de conductos (unidad interior)?</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map(n => (
                      <button key={n} onClick={() => setCfgF('numConductosInterior', n)}
                        className={`text-center py-2.5 rounded-xl border-2 transition-colors ${(cfg.numConductosInterior || 1) === n ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <p className="text-sm font-semibold text-gray-800">{n === 4 ? '4+' : n}</p>
                        <p className="text-xs text-gray-400">{n * 40}€</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {cfg.tipoSplit.length > 0 && (
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-1.5">Unidades exteriores <span className="font-normal text-gray-400">(+50€ c/u)</span></p>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setCfgF('numUnidadesExterior', Math.max(0, (cfg.numUnidadesExterior || 0) - 1))}
                      className="w-10 h-10 rounded-xl border-2 border-gray-200 hover:border-red-400 text-xl font-bold text-gray-600 flex items-center justify-center">−</button>
                    <span className="w-10 text-center text-lg font-bold text-gray-900">{cfg.numUnidadesExterior || 0}</span>
                    <button onClick={() => setCfgF('numUnidadesExterior', (cfg.numUnidadesExterior || 0) + 1)}
                      className="w-10 h-10 rounded-xl border-2 border-gray-200 hover:border-red-400 text-xl font-bold text-gray-600 flex items-center justify-center">+</button>
                    {(cfg.numUnidadesExterior || 0) > 0 && <span className="text-sm font-semibold text-red-600">+{(cfg.numUnidadesExterior || 0) * 50}€</span>}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Resumen de precio */}
          {cfg.tiposServicio.length > 0 && deltaPrecio > 0 && (
            <div className="space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm space-y-1.5">
                <div className="flex justify-between text-gray-600">
                  <span>Precio actual</span>
                  <span className="font-semibold">{Number(item.precioAnual || 0).toFixed(2).replace('.', ',')}€/año</span>
                </div>
                {deltaDesglose.map((l, i) => (
                  <div key={i} className="flex justify-between text-xs text-gray-500">
                    <span>+ {l.label}</span>
                    <span>{l.importe}€</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold border-t border-gray-200 pt-1.5 text-gray-900">
                  <span>Nuevo precio anual</span>
                  <span className="text-red-600">{newPrecioAnual.toFixed(2).replace('.', ',')}€/año</span>
                </div>
                <p className="text-xs text-gray-400">Nuevo pack: <span className={`font-bold px-1.5 py-0.5 rounded-full ${PACKS[newPack]?.cls || 'bg-gray-100 text-gray-600'}`}>{PACKS[newPack]?.label || newPack}</span></p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <p className="text-xs font-bold text-amber-800 mb-1">Cobro inmediato al cliente</p>
                <p className="text-2xl font-bold text-amber-900">{deltaPrecio.toFixed(2).replace('.', ',')}€</p>
                <p className="text-xs text-amber-700 mt-1">
                  Coste del nuevo servicio — cóbralo ahora en efectivo o transferencia. {item.modoCobro === 'visita'
                    ? `El precio anual pasa a ${newPrecioAnual.toFixed(2).replace('.', ',')}€, a cobrar en la próxima visita.`
                    : `A partir de enero el cargo SEPA anual será de ${newPrecioAnual.toFixed(2).replace('.', ',')}€.`}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 flex gap-3 justify-end border-t border-gray-100 pt-4">
          <button onClick={onClose} disabled={saving} className="px-4 py-2.5 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl">Cancelar</button>
          <button onClick={handleConfirm} disabled={saving || cfg.tiposServicio.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl">
            {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Confirmar ampliación
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Contratos ───────────────────────────────────────────────────────────

function TabContratos({ data, onSelect, selected, onRefresh, isAdmin, autoFiltroEstado, onClearAutoFiltro, autoFiltroAriston, onClearAutoFiltroAriston, autoFiltroVisitaPendiente, onClearAutoFiltroVisitaPendiente, autoFiltroSinFechaVencimiento, onClearAutoFiltroSinFechaVencimiento }) {
  const [filters,         setFilters]         = useState({ search: '', estado: autoFiltroEstado || '', tipoEquipo: '', pack: '', tecnico: '', zona: '', canal: '', soloDevueltos: false, soloAristonSinId: autoFiltroAriston || false, soloVisitaPendiente: autoFiltroVisitaPendiente || false, soloSinFechaVencimiento: autoFiltroSinFechaVencimiento || false });
  const [pagina,          setPagina]          = useState(0);
  const [sortCol,         setSortCol]         = useState('prioridad');
  const [sortDir,         setSortDir]         = useState(1);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const setF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPagina(0); };
  const toggleSort = (col) => { if (sortCol === col) setSortDir(d => -d); else { setSortCol(col); setSortDir(1); } setPagina(0); };

  React.useEffect(() => {
    if (autoFiltroEstado !== undefined) { setFilters(f => ({ ...f, estado: autoFiltroEstado })); setPagina(0); }
  }, [autoFiltroEstado]);
  React.useEffect(() => {
    if (autoFiltroAriston !== undefined) { setFilters(f => ({ ...f, soloAristonSinId: !!autoFiltroAriston })); setPagina(0); }
  }, [autoFiltroAriston]);
  React.useEffect(() => {
    if (autoFiltroVisitaPendiente !== undefined) { setFilters(f => ({ ...f, soloVisitaPendiente: !!autoFiltroVisitaPendiente })); setPagina(0); }
  }, [autoFiltroVisitaPendiente]);
  React.useEffect(() => {
    if (autoFiltroSinFechaVencimiento !== undefined) { setFilters(f => ({ ...f, soloSinFechaVencimiento: !!autoFiltroSinFechaVencimiento })); setPagina(0); }
  }, [autoFiltroSinFechaVencimiento]);
  const selCls = 'border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';

  const filtrados = useMemo(() => {
    const s = filters.search.toLowerCase();
    return data.filter(m => {
      if (filters.soloDevueltos   && m.estadoPago !== 'devuelto') return false;
      if (filters.soloAristonSinId && !(m.extensionGarantia && !m.idContratoAriston)) return false;
      if (filters.soloVisitaPendiente && !necesitaAgendarVisita(m)) return false;
      if (filters.soloSinFechaVencimiento && !(m.tieneContrato && !m.fechaVencimientoContrato)) return false;
      if (filters.estado     && m.estado !== filters.estado) return false;
      if (filters.tipoEquipo && m.tipoEquipo !== filters.tipoEquipo) return false;
      if (filters.pack       && m.pack !== filters.pack) return false;
      if (filters.tecnico    && m.tecnicoAsignado !== filters.tecnico) return false;
      if (filters.zona       && m.zona !== filters.zona) return false;
      if (filters.canal      && m.canalCaptacion !== filters.canal) return false;
      if (s && !(m.clienteNombre?.toLowerCase().includes(s) || m.modelo?.toLowerCase().includes(s) || m.municipio?.toLowerCase().includes(s) || m.marca?.toLowerCase().includes(s) || m.referenciaContrato?.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [data, filters]);

  const SORT_FN = {
    cliente:  (a, b) => (a.clienteNombre || '').localeCompare(b.clienteNombre || ''),
    municipio:(a, b) => (a.municipio || '').localeCompare(b.municipio || ''),
    pack:     (a, b) => (a.pack || '').localeCompare(b.pack || ''),
    tecnico:  (a, b) => (a.tecnicoAsignado || '').localeCompare(b.tecnicoAsignado || ''),
    proximo:  (a, b) => (a.fechaProximoMantenimiento || '').localeCompare(b.fechaProximoMantenimiento || ''),
    estado:   (a, b) => (a.estado || '').localeCompare(b.estado || ''),
  };
  const sorted = useMemo(() => {
    if (sortCol && sortCol !== 'prioridad' && SORT_FN[sortCol])
      return [...filtrados].sort((a, b) => sortDir * SORT_FN[sortCol](a, b));
    // Orden por defecto: contratos con tareas arriba, de más a menos urgentes;
    // a igualdad, por próximo mantenimiento y luego por nombre.
    return [...filtrados].sort((a, b) => {
      const pa = prioridadContrato(a), pb = prioridadContrato(b);
      if (pa !== pb) return (pa - pb) * (sortCol === 'prioridad' ? sortDir : 1);
      const fa = a.fechaProximoMantenimiento || '9999-99-99';
      const fb = b.fechaProximoMantenimiento || '9999-99-99';
      if (fa !== fb) return fa.localeCompare(fb);
      return (a.clienteNombre || '').localeCompare(b.clienteNombre || '');
    });
  }, [filtrados, sortCol, sortDir]);

  const conTareas = useMemo(() => filtrados.filter(m => tareasDeContrato(m).length > 0).length, [filtrados]);

  const pagina_data = useMemo(() => sorted.slice(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA), [sorted, pagina]);
  const zonas = useMemo(() => [...new Set(data.map(m => m.zona).filter(Boolean))].sort(), [data]);
  const hayFiltros = Object.values(filters).some(Boolean);

  // Cálculo de renovaciones próximas
  const renovStats = useMemo(() => {
    const hoy = new Date().toISOString().split('T')[0];
    const en30 = new Date(); en30.setDate(en30.getDate() + 30);
    const en60 = new Date(); en60.setDate(en60.getDate() + 60);
    const en90 = new Date(); en90.setDate(en90.getDate() + 90);
    const str30 = en30.toISOString().split('T')[0];
    const str60 = en60.toISOString().split('T')[0];
    const str90 = en90.toISOString().split('T')[0];
    const ACTIVOS = new Set(['firmado_activo', 'sepa_generado', 'pendiente_factura', 'pendiente_agendar', 'mantenimiento_agendado', 'vence_pronto']);
    const activos = data.filter(m => ACTIVOS.has(m.estado));
    const arr = activos.reduce((s, m) => s + Number(m.precioAnual || 0), 0);
    const r30 = activos.filter(m => m.fechaProximoMantenimiento && m.fechaProximoMantenimiento >= hoy && m.fechaProximoMantenimiento <= str30);
    const r60 = activos.filter(m => m.fechaProximoMantenimiento && m.fechaProximoMantenimiento > str30 && m.fechaProximoMantenimiento <= str60);
    const r90 = activos.filter(m => m.fechaProximoMantenimiento && m.fechaProximoMantenimiento > str60 && m.fechaProximoMantenimiento <= str90);
    return { activos: activos.length, arr, r30, r60, r90 };
  }, [data]);
  const fmtEur = (n) => Number(n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

  return (
    <div className="space-y-4">
      {/* Dashboard de renovaciones */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Renovaciones próximas</h3>
          <div className="text-xs text-gray-400">{renovStats.activos} contratos activos · ARR {fmtEur(renovStats.arr)}</div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: '30 días', items: renovStats.r30, bg: 'bg-red-50 border-red-200', text: 'text-red-800', badge: 'bg-red-100 text-red-700' },
            { label: '60 días', items: renovStats.r60, bg: 'bg-orange-50 border-orange-200', text: 'text-orange-800', badge: 'bg-orange-100 text-orange-700' },
            { label: '90 días', items: renovStats.r90, bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-800', badge: 'bg-yellow-100 text-yellow-700' },
          ].map(b => (
            <button key={b.label}
              onClick={() => b.items.length > 0 && setF('estado', '')}
              className={`rounded-xl border p-3 text-left transition-all hover:shadow-sm ${b.bg}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-bold ${b.text}`}>Próx. {b.label}</span>
                <span className={`text-lg font-bold px-2 py-0.5 rounded-full ${b.badge}`}>{b.items.length}</span>
              </div>
              {b.items.length > 0 && (
                <p className={`text-[10px] opacity-70 ${b.text} truncate`}>
                  {b.items.slice(0, 2).map(m => m.clienteNombre).join(', ')}{b.items.length > 2 ? ` +${b.items.length - 2}` : ''}
                </p>
              )}
              <p className={`text-xs font-semibold ${b.text} mt-1`}>
                {fmtEur(b.items.reduce((s, m) => s + Number(m.precioAnual || 0), 0))}/año
              </p>
            </button>
          ))}
        </div>
      </div>

    <div className="bg-white rounded-2xl border shadow-lg overflow-hidden">
      {/* Filtros — fila principal (siempre visible) */}
      <div className="p-3 border-b border-gray-100 space-y-2">
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Icons.Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-red-400"
              placeholder="Buscar cliente, modelo, municipio..."
              value={filters.search} onChange={e => setF('search', e.target.value)} />
          </div>
          <select className={`${selCls} min-w-0`} value={filters.estado} onChange={e => setF('estado', e.target.value)}>
            <option value="">Todos estados</option>
            {ESTADOS_FILTRO.map(k => <option key={k} value={k}>{ESTADO_CFG[k]?.label || k}</option>)}
          </select>
          {/* Botón "más filtros" solo en móvil */}
          <button onClick={() => setShowMoreFilters(v => !v)}
            className={`sm:hidden flex-shrink-0 px-3 py-2 text-sm font-bold rounded-xl border transition-colors ${showMoreFilters ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-200'}`}>
            <Icons.Search className="w-4 h-4" />
          </button>
        </div>

        {/* Filtros secundarios — siempre visibles en escritorio, colapsables en móvil */}
        <div className={`${showMoreFilters ? 'flex' : 'hidden sm:flex'} flex-wrap gap-2 items-center`}>
          <select className={selCls} value={filters.tipoEquipo} onChange={e => setF('tipoEquipo', e.target.value)}>
            <option value="">Todos los equipos</option>
            {Object.entries(TIPOS_EQUIPO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className={selCls} value={filters.pack} onChange={e => setF('pack', e.target.value)}>
            <option value="">Todos los packs</option>
            {Object.entries(PACKS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className={selCls} value={filters.tecnico} onChange={e => setF('tecnico', e.target.value)}>
            <option value="">Todos los técnicos</option>
            {TECNICOS_NOMBRES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {zonas.length > 0 && (
            <select className={selCls} value={filters.zona} onChange={e => setF('zona', e.target.value)}>
              <option value="">Todas las zonas</option>
              {zonas.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
          )}
          <select className={selCls} value={filters.canal} onChange={e => setF('canal', e.target.value)}>
            <option value="">Todos los canales</option>
            {Object.entries(CANALES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button
            onClick={() => setF('soloDevueltos', !filters.soloDevueltos)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-xl transition-colors ${filters.soloDevueltos ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}>
            Devueltos{data.filter(m => m.estadoPago === 'devuelto').length > 0 && ` (${data.filter(m => m.estadoPago === 'devuelto').length})`}
          </button>
        </div>

        {/* Chips de filtros activos + contador */}
        {(filters.estado || filters.soloAristonSinId || filters.soloVisitaPendiente || filters.soloSinFechaVencimiento || hayFiltros) && (
          <div className="flex flex-wrap gap-1.5 items-center">
            {filters.estado && (
              <span className="flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 rounded-xl px-2.5 py-1 text-xs font-bold">
                {ESTADO_CFG[filters.estado]?.label || filters.estado}
                <button onClick={() => { setF('estado', ''); onClearAutoFiltro?.(); }} className="text-red-400 hover:text-red-700 ml-0.5 leading-none">✕</button>
              </span>
            )}
            {filters.soloAristonSinId && (
              <span className="flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-2.5 py-1 text-xs font-bold">
                ⚠ Ariston sin ID
                <button onClick={() => { setF('soloAristonSinId', false); onClearAutoFiltroAriston?.(); }} className="text-amber-400 hover:text-amber-700 ml-0.5 leading-none">✕</button>
              </span>
            )}
            {filters.soloVisitaPendiente && (
              <span className="flex items-center gap-1 bg-cyan-50 border border-cyan-200 text-cyan-700 rounded-xl px-2.5 py-1 text-xs font-bold">
                📅 Visita pendiente (en visita)
                <button onClick={() => { setF('soloVisitaPendiente', false); onClearAutoFiltroVisitaPendiente?.(); }} className="text-cyan-400 hover:text-cyan-700 ml-0.5 leading-none">✕</button>
              </span>
            )}
            {filters.soloSinFechaVencimiento && (
              <span className="flex items-center gap-1 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl px-2.5 py-1 text-xs font-bold">
                ⚠ Sin fecha de vencimiento
                <button onClick={() => { setF('soloSinFechaVencimiento', false); onClearAutoFiltroSinFechaVencimiento?.(); }} className="text-gray-400 hover:text-gray-700 ml-0.5 leading-none">✕</button>
              </span>
            )}
            {hayFiltros && (
              <button onClick={() => { setFilters({ search: '', estado: '', tipoEquipo: '', pack: '', tecnico: '', zona: '', canal: '', soloDevueltos: false, soloAristonSinId: false, soloVisitaPendiente: false, soloSinFechaVencimiento: false }); setPagina(0); onClearAutoFiltro?.(); onClearAutoFiltroAriston?.(); onClearAutoFiltroVisitaPendiente?.(); onClearAutoFiltroSinFechaVencimiento?.(); }}
                className="text-xs text-red-500 hover:text-red-700 font-bold">Limpiar todo</button>
            )}
            <span className="ml-auto text-xs text-gray-400">
              {filtrados.length} contratos{conTareas > 0 && <span className="text-amber-600 font-semibold"> · {conTareas} con tareas</span>}
            </span>
          </div>
        )}
        {!filters.estado && !filters.soloAristonSinId && !filters.soloVisitaPendiente && !filters.soloSinFechaVencimiento && !hayFiltros && (
          <div className="flex justify-end">
            <span className="text-xs text-gray-400">
              {filtrados.length} contratos{conTareas > 0 && <span className="text-amber-600 font-semibold"> · {conTareas} con tareas</span>}
            </span>
          </div>
        )}
      </div>

      {/* Lista de contratos */}
      {filtrados.length === 0 ? (
        <div className="py-16 text-center text-gray-400">
          <Icons.Wrench className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p className="font-medium">Sin resultados con estos filtros</p>
        </div>
      ) : (
        <>
          {/* ── Tarjetas móvil ──────────────────────────────────────────────── */}
          <div className="sm:hidden divide-y divide-gray-100">
            {pagina_data.map(m => {
              const tareas = tareasDeContrato(m);
              const rowTone = tareas[0] ? TAREA_ROW[tareas[0].tono] : '';
              return (
                <div key={m.id} onClick={() => onSelect(selected?.id === m.id ? null : m)}
                  className={`px-4 py-3 cursor-pointer active:bg-gray-50 transition-colors ${selected?.id === m.id ? 'bg-red-50' : rowTone || ''}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-sm leading-tight truncate">{m.clienteNombre}</p>
                      {m.referenciaContrato && <p className="text-[10px] text-gray-400 font-mono leading-none mt-0.5">{m.referenciaContrato}</p>}
                    </div>
                    <EstadoBadge estado={m.estado} />
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 mb-1.5">
                    {m.municipio && <span>{m.municipio}</span>}
                    {m.tecnicoAsignado && <span>· {m.tecnicoAsignado}</span>}
                    {(m.marca || m.modelo) && <span>· {[m.marca, m.modelo].filter(Boolean).join(' ')}</span>}
                    {m.fechaProximoMantenimiento && <span>· Mant: {fmt(m.fechaProximoMantenimiento)}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    {m.pack && <PackBadge pack={m.pack} />}
                  </div>
                  {tareas.length > 0 && (
                    <div className="flex flex-col gap-1 items-start mt-1.5">
                      {tareas.slice(0, 3).map((t, i) => <TareaChip key={i} tono={t.tono} label={t.label} />)}
                      {tareas.length > 3 && <span className="text-[10px] text-gray-400 pl-1">+{tareas.length - 3} más</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Tabla escritorio ────────────────────────────────────────────── */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {[
                    ['prioridad', 'Tareas'],
                    ['cliente',   'Cliente'],
                    ['municipio', 'Municipio'],
                    [null,        'Equipo'],
                    ['pack',      'Pack'],
                    ['tecnico',   'Técnico'],
                    ['proximo',   'Próximo mant.'],
                    ['estado',    'Estado'],
                  ].map(([col, label]) => (
                    <th key={label}
                      onClick={col ? () => toggleSort(col) : undefined}
                      className={`text-left py-3 px-4 text-xs font-bold uppercase tracking-wide select-none ${col ? 'cursor-pointer hover:text-gray-700' : ''} ${sortCol === col ? 'text-red-600' : 'text-gray-500'}`}>
                      {label}{col && sortCol === col ? (sortDir === 1 ? ' ↑' : ' ↓') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pagina_data.map(m => {
                  const tareas  = tareasDeContrato(m);
                  const rowTone = tareas[0] ? TAREA_ROW[tareas[0].tono] : '';
                  return (
                  <tr key={m.id} onClick={() => onSelect(selected?.id === m.id ? null : m)}
                    className={`cursor-pointer transition-colors ${selected?.id === m.id ? 'bg-red-50' : rowTone || 'hover:bg-gray-50'}`}>
                    <td className="py-3 px-4 align-top w-[1%] whitespace-nowrap">
                      <TareasCelda m={m} />
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-semibold text-gray-900 leading-tight">{m.clienteNombre}</p>
                      {m.referenciaContrato && <p className="text-[10px] text-gray-400 font-mono leading-none mt-0.5">{m.referenciaContrato}</p>}
                      {m.clienteTelefono && <p className="text-xs text-gray-400">{m.clienteTelefono}</p>}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{m.municipio || '—'}</td>
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-700">{m.marca} {m.modelo}</p>
                      {m.numeroSerie && <p className="text-xs text-gray-400">S/N {m.numeroSerie}</p>}
                    </td>
                    <td className="py-3 px-4"><PackBadge pack={m.pack} /></td>
                    <td className="py-3 px-4 text-gray-600">{m.tecnicoAsignado || '—'}</td>
                    <td className="py-3 px-4 text-gray-600 text-xs">{fmt(m.fechaProximoMantenimiento)}</td>
                    <td className="py-3 px-4"><EstadoBadge estado={m.estado} /></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Paginacion total={filtrados.length} pagina={pagina} onChange={setPagina} />
        </>
      )}
    </div>
    </div>
  );
}

// ─── Modal: Calcular grupos ────────────────────────────────────────────────────

function CalcularGruposModal({ onClose, onDone }) {
  const [estado, setEstado] = useState('idle'); // idle | running | done | error
  const [result, setResult] = useState(null);
  const [error,  setError]  = useState('');

  async function start() {
    setEstado('running'); setError(''); setResult(null);
    try {
      const resp = await fetch('/.netlify/functions/calcularGruposMantenimiento', { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error);
      setResult(data);
      setEstado('done');
      // no cerramos aquí — el usuario lee el resultado y cierra manualmente
    } catch (e) { setError(e.message); setEstado('error'); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <h2 className="text-lg font-bold text-gray-800">Calcular grupos de captación</h2>
        <p className="text-sm text-gray-500">
          Lee las workorders ya sincronizadas en Firebase (sin llamadas a StelOrder), construye el mapa en
          memoria y clasifica todos los leads sin contrato en grupos A / B / C.
        </p>

        {estado === 'running' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-sm text-gray-500 animate-pulse">Leyendo workorders y clasificando…</p>
          </div>
        )}

        {estado === 'done' && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              {[['A','bg-green-50','text-green-700','Mantenimiento/PM'],['B','bg-yellow-50','text-yellow-700','Con nombre y tel.'],['C','bg-gray-50','text-gray-500','Sin datos']].map(([g,bg,txt,lbl]) => (
                <div key={g} className={`${bg} rounded-xl p-3`}>
                  <div className={`text-2xl font-bold ${txt}`}>{result.stats?.[g] ?? 0}</div>
                  <div className={`text-xs font-bold ${txt}`}>Grupo {g}</div>
                  <div className="text-xs text-gray-400">{lbl}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 text-center">
              {result.nuevosClasificados} nuevos · {result.yaClasificados} ya tenían grupo · {result.conKeyword} con keyword · {result.total} total · {result.ordenesLeidas} workorders leídas
            </p>
          </div>
        )}

        {estado === 'error' && (
          <p className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{error}</p>
        )}

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={estado === 'done' ? onDone : onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
            {estado === 'done' ? 'Cerrar y actualizar' : 'Cancelar'}
          </button>
          {estado !== 'running' && (
            <button onClick={start} className="px-4 py-2 text-sm font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700">
              {estado === 'done' ? 'Recalcular' : 'Calcular ahora'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Kanban: Captación ───────────────────────────────────────────────────────

const GRUPO_ORDER = { A: 0, B: 1, C: 2 };
const GRUPO_CFG = {
  A: { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Grupo A', desc: 'Mantenimiento / PM' },
  B: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Grupo B', desc: 'Con nombre y tel.' },
  C: { bg: 'bg-gray-100',   text: 'text-gray-500',   label: 'Grupo C', desc: 'Sin datos completos' },
};


const KANBAN_COLS = [
  { id: 'potenciales',      label: 'Clientes potenciales', desc: 'Grupo A · ya mantenidos este año', dot: 'bg-amber-400', hdrBg: 'bg-amber-50', colBg: 'bg-amber-50/50', border: 'border-amber-200' },
  { id: 'sin_contrato',     label: 'Sin contacto',    desc: null,                              dot: 'bg-gray-400',   hdrBg: 'bg-gray-100',   colBg: 'bg-gray-50',    border: 'border-gray-200' },
  { id: 'contactado',       label: 'Contactado',       desc: null,                              dot: 'bg-indigo-400', hdrBg: 'bg-indigo-50',  colBg: 'bg-indigo-50/50', border: 'border-indigo-200' },
  { id: 'interesado',       label: 'Interesado',       desc: null,                              dot: 'bg-cyan-500',   hdrBg: 'bg-cyan-50',    colBg: 'bg-cyan-50/50', border: 'border-cyan-200' },
  { id: 'contrato_enviado', label: 'Contrato enviado', desc: 'Pendiente de firma',              dot: 'bg-purple-400', hdrBg: 'bg-purple-50',  colBg: 'bg-purple-50/50', border: 'border-purple-200' },
  { id: 'no_interesado',    label: 'No interesado',   desc: 'No responde, para sacarlo del flujo activo',    dot: 'bg-red-400',    hdrBg: 'bg-red-50',     colBg: 'bg-red-50/50',   border: 'border-red-200' },
];
const KANBAN_COL_ORDER = ['sin_contrato', 'contactado', 'interesado', 'contrato_enviado'];

function getKanbanColId(estado) {
  return estado === 'sin_respuesta' ? 'contactado' : estado;
}
function canKanbanDrop(fromEstado, toColId) {
  const from = getKanbanColId(fromEstado);
  // "No interesado" no significa rechazo — es "no responde, hay que sacarlo
  // del flujo activo para no ensuciar el Kanban". Se puede mandar ahí desde
  // cualquier punto del flujo, y desde ahí se puede reactivar hacia cualquier
  // columna normal (sigue siendo un lead recuperable, no uno cerrado).
  if (toColId === 'no_interesado') return from !== 'no_interesado';
  if (from === 'no_interesado')    return KANBAN_COL_ORDER.includes(toColId);

  const fromIdx = KANBAN_COL_ORDER.indexOf(from);
  const toIdx   = KANBAN_COL_ORDER.indexOf(toColId);
  if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return false;
  if (toIdx < fromIdx) return true;       // backward: always ok
  if (toIdx === fromIdx + 1) return true; // one step forward: ok
  return false;                           // skip forward: blocked
}

const KanbanCard = React.memo(function KanbanCard({ item, selected, dragging, saving, onSelect, onLlamada, onDragStart, onDragEnd, onMove }) {
  // Columnas a las que se puede mover esta tarjeta — mismo criterio que el
  // drag & drop (canKanbanDrop), para tener también una forma de moverla sin
  // arrastrar: en móvil/tablet el drag & drop nativo no funciona, así que
  // sin esto no había manera de mover una tarjeta de columna desde el móvil.
  const destinosValidos = KANBAN_COLS.filter(c => canKanbanDrop(item.estado, c.id));
  const sinRespuesta   = item.estado === 'sin_respuesta';
  const diasUltimoC    = item.ultimoContacto ? diasDesde(item.ultimoContacto) : null;
  const alertaC        = diasUltimoC !== null && diasUltimoC >= 5;
  const gcfg           = GRUPO_CFG[item.grupo];
  const esCaliente     = esClienteCaliente(item);

  // Semáforo de urgencia para "Clientes potenciales" (Grupo A ya mantenido este
  // año): rojo si quedan ≤5 días o ya se pasó la fecha sugerida sin contrato,
  // naranja ≤15 días, amarillo ≤30 días.
  // Reloj sutil con el tiempo en el estado actual — en "Contrato enviado"
  // cuenta desde que se mandó el enlace (contratoEnviadoAt; si el contrato es
  // de antes de que ese campo existiera, cae a createdAt); en "Contactado"
  // cuenta desde que la tarjeta ENTRÓ en esa columna (contactadoAt, sellado
  // en moverA/las reactivaciones — no hay reserva razonable para tarjetas
  // más antiguas, así que simplemente no se muestra si falta el dato).
  const tiempoEnEstado = item.estado === 'contrato_enviado'
    ? tiempoDesdeEnvio(item.contratoEnviadoAt || item.createdAt)
    : item.estado === 'contactado'
    ? tiempoDesdeEnvio(item.contactadoAt)
    : null;

  const fechaSugerida = fechaContactoSugerida(item);
  const diasSugerida  = fechaSugerida ? diasHasta(fechaSugerida) : null;
  const urgencia = diasSugerida === null ? null
    : diasSugerida <= 5  ? 'roja'
    : diasSugerida <= 15 ? 'naranja'
    : diasSugerida <= 30 ? 'amarilla'
    : null;
  const urgenciaCfg = {
    roja:     { border: 'border-red-300',    bg: 'bg-red-50',    text: 'text-red-600',    label: diasSugerida <= 0 ? '⚠ Fecha ya pasada' : `⚠ Contactar en ${diasSugerida}d` },
    naranja:  { border: 'border-orange-300', bg: 'bg-orange-50', text: 'text-orange-600', label: `Contactar en ${diasSugerida}d` },
    amarilla: { border: 'border-amber-200',  bg: 'bg-amber-50',  text: 'text-amber-700',  label: `Contactar en ${diasSugerida}d` },
  }[urgencia];

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, item.id)}
      onDragEnd={onDragEnd}
      onClick={() => onSelect(selected ? null : item)}
      className={`rounded-xl border px-3 py-2.5 cursor-grab active:cursor-grabbing transition-all select-none ${
        dragging ? 'opacity-30 scale-95' : ''
      } ${selected ? 'ring-2 ring-red-400 border-red-200 bg-white' : esCaliente ? 'border-orange-300 bg-orange-50 hover:shadow-sm' : urgenciaCfg ? `${urgenciaCfg.border} ${urgenciaCfg.bg} hover:shadow-sm` : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'} ${
        saving ? 'opacity-60 pointer-events-none' : ''
      }`}
    >
      {sinRespuesta && (
        <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 mb-1.5">
          ⏱ Sin respuesta
        </span>
      )}
      {esCaliente && (
        <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-full px-2 py-0.5 mb-1.5">
          🔥 A+ · contactar ya
        </span>
      )}
      {!esCaliente && urgenciaCfg && (
        <span className={`inline-flex items-center gap-1 text-xs font-bold ${urgenciaCfg.text} bg-white/70 rounded-full px-2 py-0.5 mb-1.5`}>
          {urgenciaCfg.label}
        </span>
      )}
      <p className="font-semibold text-gray-900 text-sm leading-tight truncate">{item.clienteNombre || <span className="italic text-gray-300">Sin nombre</span>}</p>
      {(item.marca || item.modelo) && (
        <p className="text-xs text-gray-400 truncate mt-0.5">{[item.marca, item.modelo].filter(Boolean).join(' ')}</p>
      )}
      {item.municipio && <p className="text-xs text-gray-400">{item.municipio}</p>}
      <div className="flex items-center gap-2 mt-1.5">
        {esCaliente ? (
          <span className="inline-flex items-center justify-center px-1.5 h-5 rounded-full text-xs font-bold flex-shrink-0 bg-gradient-to-r from-orange-500 to-red-500 text-white">
            A+
          </span>
        ) : gcfg && (
          <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold flex-shrink-0 ${gcfg.bg} ${gcfg.text}`}>
            {item.grupo}
          </span>
        )}
        {diasUltimoC !== null && (
          <span className={`text-xs ${alertaC ? (diasUltimoC >= 10 ? 'text-red-500 font-semibold' : 'text-orange-500') : 'text-gray-400'}`}>
            {diasUltimoC === 0 ? 'Hoy' : `Hace ${diasUltimoC}d`}
          </span>
        )}
        {tiempoEnEstado && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400 flex-shrink-0"
            title={item.estado === 'contrato_enviado' ? 'Tiempo desde que se envió el enlace de firma' : 'Tiempo desde que se marcó como contactado'}>
            🕐 {tiempoEnEstado}
          </span>
        )}
        {item.referenciaContrato && (
          <span className="text-xs text-purple-500 ml-auto font-mono truncate">{item.referenciaContrato}</span>
        )}
      </div>
      <div className="mt-2 flex gap-1.5" onClick={e => e.stopPropagation()}>
        <button onClick={onLlamada}
          className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
          📞 Llamada
        </button>
        {item.clienteTelefono && item.estado !== 'no_interesado' && (
          <button onClick={() => {
            const tel = normTel(item.clienteTelefono);
            if (!tel) return;
            window.open(`https://wa.me/${tel}?text=${encodeURIComponent(generarMensajeKanban(item))}`, '_blank', 'noopener,noreferrer');
          }}
            className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">
            💬 WA
          </button>
        )}
        {destinosValidos.length > 0 && (
          <select value="" onChange={e => { if (e.target.value) onMove(item, e.target.value); }}
            title="Mover a otra columna (también funciona en móvil, sin arrastrar)"
            className="ml-auto text-xs font-bold text-gray-500 bg-gray-50 hover:bg-gray-100 rounded-lg px-1.5 py-0.5 border-none focus:outline-none">
            <option value="">Mover a…</option>
            {destinosValidos.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        )}
      </div>
    </div>
  );
});

// No repetir el escaneo completo de activos si ya se lanzó hace menos de esto
// en la misma pestaña del navegador — los activos solo cambian con el cron
// (cada 30 min), así que relanzarlo en cada cambio de tab no aporta nada.
const LEADS_ACTIVOS_THROTTLE_MS = 10 * 60 * 1000;

const MESES = [
  { value: '01', label: 'Enero' },     { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },     { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },      { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },     { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },{ value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' }, { value: '12', label: 'Diciembre' },
];

function TabCaptacion({ data, onRefresh, isAdmin, onSelect, selected, onNuevoContrato }) {
  const [dragId,         setDragId]         = useState(null);
  const [dragOverCol,    setDragOverCol]    = useState(null);
  const [saving,         setSaving]         = useState(null);
  const [llamadaItem,    setLlamadaItem]    = useState(null);
  const [showCalcGrupos, setShowCalcGrupos] = useState(false);
  const [search,         setSearch]         = useState('');
  const [grupoFiltro,    setGrupoFiltro]    = useState('');
  const [mesMantFiltro,  setMesMantFiltro]  = useState('');
  const [mesGarFiltro,   setMesGarFiltro]   = useState('');
  const [visibleCounts,  setVisibleCounts]  = useState({});
  const [leadsAviso,     setLeadsAviso]     = useState('');
  const [nuosCorrigiendo,setNuosCorrigiendo]= useState(false);
  const [nuosAviso,      setNuosAviso]      = useState('');
  const [garCorrigiendo, setGarCorrigiendo] = useState(false);
  const [garAviso,       setGarAviso]       = useState('');

  // Corrección puntual (idempotente): leads creados antes del ajuste de
  // detectarTipo que quedaron como AEROTERMIA siendo en realidad NUOS
  // (bomba de calor). Se puede pulsar varias veces sin problema.
  const corregirNuos = async () => {
    setNuosCorrigiendo(true); setNuosAviso('');
    try {
      const resp = await fetch('/.netlify/functions/corregirTipoEquipoNuos', { method: 'POST', headers: { ...authService.authHeader() } });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error desconocido');
      setNuosAviso(d.corregidos > 0 ? `${d.corregidos} corregidos a Bomba de calor` : 'Nada que corregir');
      if (d.corregidos > 0) onRefresh();
    } catch (e) {
      setNuosAviso(`Error: ${e.message}`);
    } finally {
      setNuosCorrigiendo(false);
      setTimeout(() => setNuosAviso(''), 8000);
    }
  };

  // Corrección puntual (idempotente): rellena fechaInicioGarantia en leads
  // creados antes de que generarLeadsDesdeActivos.js copiara ese campo.
  // Se puede pulsar varias veces sin problema.
  const corregirGarantias = async () => {
    setGarCorrigiendo(true); setGarAviso('');
    try {
      const resp = await fetch('/.netlify/functions/backfillFechaGarantiaCaptacion', { method: 'POST' });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error desconocido');
      setGarAviso(d.corregidos > 0 ? `${d.corregidos} rellenados con fecha de garantía` : 'Nada que rellenar');
      if (d.corregidos > 0) onRefresh();
    } catch (e) {
      setGarAviso(`Error: ${e.message}`);
    } finally {
      setGarCorrigiendo(false);
      setTimeout(() => setGarAviso(''), 8000);
    }
  };

  // Al entrar en Captación (admin), buscar leads nuevos en activos en segundo
  // plano — sin botón, sin bloquear la vista — y refrescar si se crea alguno.
  useEffect(() => {
    if (!isAdmin) return;
    const ultima = Number(sessionStorage.getItem('leadsActivosUltimaEjecucion') || 0);
    if (Date.now() - ultima < LEADS_ACTIVOS_THROTTLE_MS) return;
    sessionStorage.setItem('leadsActivosUltimaEjecucion', String(Date.now()));

    fetch('/.netlify/functions/generarLeadsDesdeActivos', { method: 'POST', headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        if (d.creados > 0) {
          const potenciales = d.potenciales > 0 ? ` (${d.potenciales} ya mantenidos)` : '';
          setLeadsAviso(`+${d.creados} lead${d.creados === 1 ? '' : 's'} nuevo${d.creados === 1 ? '' : 's'} desde activos${potenciales}`);
          onRefresh();
          setTimeout(() => setLeadsAviso(''), 8000);
        }
      })
      .catch(err => console.error('[generarLeadsDesdeActivos]', err.message));
  }, [isAdmin]); // eslint-disable-line

  const captacion = useMemo(() => leadsCaptacion(data), [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return captacion.filter(m =>
      (!q ||
        (m.clienteNombre    || '').toLowerCase().includes(q) ||
        (m.municipio        || '').toLowerCase().includes(q) ||
        (m.marca            || '').toLowerCase().includes(q) ||
        (m.modelo           || '').toLowerCase().includes(q) ||
        (m.tipoEquipo       || '').toLowerCase().includes(q) ||
        (m.clienteTelefono  || '').toLowerCase().includes(q) ||
        (m.clienteEmail     || '').toLowerCase().includes(q)) &&
      (!grupoFiltro   || (grupoFiltro === 'A+' ? esClienteCaliente(m) : m.grupo === grupoFiltro)) &&
      // Por mes, sin importar el año — "todos los de marzo" da igual cuándo.
      (!mesMantFiltro || (m.fechaUltimoMantenimiento && m.fechaUltimoMantenimiento.slice(5, 7) === mesMantFiltro)) &&
      (!mesGarFiltro  || (m.fechaInicioGarantia && m.fechaInicioGarantia.slice(5, 7) === mesGarFiltro))
    );
  }, [captacion, search, grupoFiltro, mesMantFiltro, mesGarFiltro]);

  // Al cambiar filtros/búsqueda, volver a mostrar solo la primera página de
  // cada columna (si no, "Cargar más" quedaría en un punto que ya no aplica).
  useEffect(() => { setVisibleCounts({}); }, [search, grupoFiltro, mesMantFiltro, mesGarFiltro]);

  const colItems = useMemo(() => {
    const map = {};
    KANBAN_COLS.forEach(c => { map[c.id] = []; });
    filtered.forEach(m => {
      // Grupo A ya mantenido este año → tarjeta de "Clientes potenciales" en vez de
      // "Sin contacto" (misma fila de datos, solo cambia dónde se muestra).
      const col = esClientePotencial(m) ? 'potenciales' : getKanbanColId(m.estado);
      if (map[col]) map[col].push(m);
    });
    map.sin_contrato.sort((a, b) =>
      (GRUPO_ORDER[a.grupo] ?? 3) - (GRUPO_ORDER[b.grupo] ?? 3) ||
      (a.clienteNombre || '').localeCompare(b.clienteNombre || '')
    );
    // Potenciales: primero los "calientes" (mantenimiento reciente, orden por
    // más reciente primero), después el resto por fecha de renovación más urgente.
    map.potenciales.sort((a, b) => {
      const calienteA = esClienteCaliente(a), calienteB = esClienteCaliente(b);
      if (calienteA !== calienteB) return calienteA ? -1 : 1;
      if (calienteA) return diasDesde(a.fechaUltimoMantenimiento) - diasDesde(b.fechaUltimoMantenimiento);
      return diasHasta(fechaContactoSugerida(a)) - diasHasta(fechaContactoSugerida(b));
    });
    return map;
  }, [filtered]);

  const dragItem = dragId ? data.find(m => m.id === dragId) : null;

  const handleDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragEnd   = ()       => { setDragId(null); setDragOverCol(null); };

  const handleDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = (dragItem && canKanbanDrop(dragItem.estado, colId)) ? 'move' : 'none';
    setDragOverCol(colId);
  };

  // Misma lógica tanto si la tarjeta se suelta arrastrando (ratón) como si
  // se mueve con el selector de la propia tarjeta (móvil/tablet — el drag &
  // drop nativo no funciona en pantallas táctiles, así que sin esto no había
  // forma de mover una tarjeta de columna desde el móvil).
  const moverA = async (item, toColId) => {
    if (!item || !canKanbanDrop(item.estado, toColId)) return;
    if (toColId === 'contrato_enviado') { onNuevoContrato(item); return; }
    const extra = {
      ...(toColId === 'contactado' && item.estado === 'sin_respuesta' ? { ultimoContacto: hoy() } : {}),
      // Sello estable de "entró en Contactado" — es lo que alimenta el reloj
      // de la tarjeta (tiempoDesdeEnvio), no updatedAt (que cambia con
      // cualquier otro campo y reiniciaría el contador sin venir a cuento).
      ...(toColId === 'contactado' ? { contactadoAt: new Date().toISOString() } : {}),
    };
    setSaving(item.id);
    try { await saveApi({ id: item.id, estado: toColId, updatedAt: hoy(), ...extra }); onRefresh(); }
    catch (err) { console.error(err); }
    finally { setSaving(null); }
  };

  const handleDrop = (e, toColId) => {
    e.preventDefault();
    setDragOverCol(null);
    const item = data.find(m => m.id === dragId);
    setDragId(null);
    moverA(item, toColId);
  };

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative">
          <Icons.Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar cliente, municipio, marca, modelo..."
            className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 bg-white w-56" />
        </div>
        <select value={grupoFiltro} onChange={e => setGrupoFiltro(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 bg-white">
          <option value="">Todos los grupos</option>
          <option value="A+">🔥 A+ (calientes)</option>
          <option value="A">Grupo A</option>
          <option value="B">Grupo B</option>
          <option value="C">Grupo C</option>
        </select>
        <select value={mesMantFiltro} onChange={e => setMesMantFiltro(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 bg-white">
          <option value="">Últ. mant.: todos los meses</option>
          {MESES.map(m => <option key={m.value} value={m.value}>{m.label} (cualquier año)</option>)}
        </select>
        <select value={mesGarFiltro} onChange={e => setMesGarFiltro(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-red-400 bg-white">
          <option value="">Inicio garantía: todos los meses</option>
          {MESES.map(m => <option key={m.value} value={m.value}>{m.label} (cualquier año)</option>)}
        </select>
        {(grupoFiltro || search || mesMantFiltro || mesGarFiltro) && (
          <button onClick={() => { setSearch(''); setGrupoFiltro(''); setMesMantFiltro(''); setMesGarFiltro(''); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline">
            Limpiar filtros
          </button>
        )}
        {isAdmin && (
          <button onClick={() => setShowCalcGrupos(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-indigo-700 bg-white border border-indigo-200 hover:bg-indigo-50 rounded-xl transition-colors">
            Calcular grupos
          </button>
        )}
        {isAdmin && (
          <button onClick={corregirNuos} disabled={nuosCorrigiendo}
            title="Corrige leads NUOS mal clasificados como Aerotermia (StelOrder los agrupa igual que Nimbus)"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-teal-700 bg-white border border-teal-200 hover:bg-teal-50 rounded-xl transition-colors disabled:opacity-50">
            {nuosCorrigiendo ? 'Corrigiendo…' : 'Corregir NUOS'}
          </button>
        )}
        {nuosAviso && (
          <span className="text-xs font-bold text-teal-700 bg-teal-50 rounded-full px-2.5 py-1">{nuosAviso}</span>
        )}
        {isAdmin && (
          <button onClick={corregirGarantias} disabled={garCorrigiendo}
            title="Rellena la fecha de inicio de garantía en leads creados antes de que se empezara a guardar (cruzando con los activos ya sincronizados)"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-teal-700 bg-white border border-teal-200 hover:bg-teal-50 rounded-xl transition-colors disabled:opacity-50">
            {garCorrigiendo ? 'Rellenando…' : 'Rellenar fechas de garantía'}
          </button>
        )}
        {garAviso && (
          <span className="text-xs font-bold text-teal-700 bg-teal-50 rounded-full px-2.5 py-1">{garAviso}</span>
        )}
        {leadsAviso && (
          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">{leadsAviso}</span>
        )}
        <span className="ml-auto text-xs text-gray-400">
          {filtered.length === captacion.length ? `${captacion.length} en captación` : `${filtered.length} de ${captacion.length} en captación`}
        </span>
      </div>

      {/* Kanban board */}
      <div className="flex gap-3 overflow-x-auto pb-4 items-start">
        {KANBAN_COLS.map(col => {
          const items     = colItems[col.id] || [];
          const shown     = visibleCounts[col.id] || PAGINA_KANBAN;
          const visibles  = items.slice(0, shown);
          const restantes = items.length - visibles.length;
          const isOver    = dragOverCol === col.id;
          const canAccept = dragItem ? canKanbanDrop(dragItem.estado, col.id) : false;
          return (
            <div key={col.id}
              className={`flex-shrink-0 w-64 rounded-2xl border-2 flex flex-col transition-colors duration-150 ${
                isOver && canAccept   ? 'border-blue-400 shadow-lg shadow-blue-100/50' :
                isOver && !canAccept  ? 'border-red-300' :
                dragItem && canAccept ? 'border-dashed border-gray-300' :
                'border-transparent'
              }`}
              onDragOver={e => handleDragOver(e, col.id)}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
              onDrop={e => handleDrop(e, col.id)}
            >
              {/* Column header */}
              <div className={`px-3 py-2.5 rounded-t-xl border ${col.border} ${col.hdrBg} flex-shrink-0`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${col.dot}`} />
                  <span className="text-sm font-bold text-gray-700">{col.label}</span>
                  <span className="ml-auto text-xs font-bold text-gray-400 bg-white rounded-full px-2 py-0.5 border border-gray-200 min-w-[1.5rem] text-center">
                    {items.length}
                  </span>
                </div>
                {col.desc && <p className="text-xs text-gray-400 mt-0.5 pl-5">{col.desc}</p>}
              </div>

              {/* Cards drop zone */}
              <div className={`flex-1 p-2 space-y-2 rounded-b-2xl min-h-40 transition-colors duration-150 ${
                isOver && canAccept ? 'bg-blue-50' : isOver && !canAccept ? 'bg-red-50/40' : col.colBg
              }`}>
                {visibles.map(m => (
                  <KanbanCard key={m.id} item={m}
                    selected={selected?.id === m.id}
                    dragging={dragId === m.id}
                    saving={saving === m.id}
                    onSelect={onSelect}
                    onLlamada={() => setLlamadaItem(m)}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onMove={moverA}
                  />
                ))}
                {restantes > 0 && (
                  <button
                    onClick={() => setVisibleCounts(v => ({ ...v, [col.id]: shown + PAGINA_KANBAN }))}
                    className="w-full py-2 text-xs font-bold text-gray-500 bg-white hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors">
                    Cargar {Math.min(PAGINA_KANBAN, restantes)} más ({restantes} restantes)
                  </button>
                )}
                {items.length === 0 && (
                  <div className={`rounded-xl border-2 border-dashed py-10 text-center text-xs transition-colors duration-150 ${
                    isOver && canAccept ? 'border-blue-400 text-blue-400' :
                    canAccept           ? 'border-blue-200 text-blue-300' :
                                         'border-gray-200 text-gray-300'
                  }`}>
                    {canAccept
                      ? '↓ Suelta aquí'
                      : col.id === 'contrato_enviado' ? 'Arrastra un interesado aquí'
                      : col.id === 'potenciales'      ? 'Sin clientes potenciales por ahora'
                      : col.id === 'no_interesado'    ? 'Nadie sin responder por ahora'
                      : 'Sin contactos'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showCalcGrupos && (
        <CalcularGruposModal
          onClose={() => setShowCalcGrupos(false)}
          onDone={() => { setShowCalcGrupos(false); onRefresh(); }}
        />
      )}
      {llamadaItem && (
        <RegistrarLlamadaModal
          item={llamadaItem}
          onClose={() => setLlamadaItem(null)}
          onSaved={() => { setLlamadaItem(null); onRefresh(); }}
        />
      )}
    </>
  );
}

// ─── Tab: Comunidades ─────────────────────────────────────────────────────────

function TabComunidades({ data }) {
  const [comunidades, setComunidades] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [form, setForm]               = useState({ nombre: '', personaContacto: '', telefono: '', umbralDescuento: 5, descuentoPct: 10, codigo: '' });
  const [saving, setSaving]           = useState(false);
  const [selectedCom, setSelectedCom] = useState(null);
  const [msg, setMsg]                 = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await fetch('/.netlify/functions/gestionarComunidades', { headers: authService.authHeader() });
      const d = await r.json();
      if (d.success) setComunidades(d.comunidades || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const crearComunidad = async () => {
    if (!form.nombre.trim()) return;
    setSaving(true); setMsg('');
    try {
      const r = await fetch('/.netlify/functions/gestionarComunidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error');
      setMsg(`✓ Comunidad creada. Código: ${d.codigo}`);
      setForm({ nombre: '', personaContacto: '', telefono: '', umbralDescuento: 5, descuentoPct: 10, codigo: '' });
      setShowForm(false);
      cargar();
    } catch (e) { setMsg(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  const toggleActivo = async (com) => {
    try {
      const r = await fetch(`/.netlify/functions/gestionarComunidades?id=${com.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ activo: !com.activo }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setMsg(`Error: ${d.error || r.status}`); return; }
    } catch (e) { setMsg(`Error: ${e.message}`); return; }
    cargar();
  };

  const actualizarUmbral = async (com, nuevoUmbral) => {
    const umbral = Number(nuevoUmbral);
    if (!Number.isFinite(umbral) || umbral < 1 || umbral === com.umbralDescuento) return;
    try {
      const r = await fetch(`/.netlify/functions/gestionarComunidades?id=${com.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ umbralDescuento: umbral }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setMsg(`Error: ${d.error || r.status}`); return; }
    } catch (e) { setMsg(`Error: ${e.message}`); return; }
    cargar();
  };

  const actualizarDescuentoPct = async (com, nuevoPct) => {
    const pct = Number(nuevoPct);
    if (!Number.isFinite(pct) || pct <= 0 || pct >= 100 || pct === com.descuentoPct) return;
    try {
      const r = await fetch(`/.netlify/functions/gestionarComunidades?id=${com.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ descuentoPct: pct }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setMsg(`Error: ${d.error || r.status}`); return; }
    } catch (e) { setMsg(`Error: ${e.message}`); return; }
    cargar();
  };

  const eliminarComunidad = async (com, force = false) => {
    if (!window.confirm(force
      ? `"${com.nombre}" tiene ${com.totalFirmados} contrato${com.totalFirmados === 1 ? '' : 's'} vinculado${com.totalFirmados === 1 ? '' : 's'}. Esos contratos NO se borran, pero dejarán de tener comunidad asociada. ¿Eliminar de todas formas?`
      : `¿Eliminar la comunidad "${com.nombre}" (${com.codigo})? Esta acción no se puede deshacer.`)) return;
    try {
      const r = await fetch(`/.netlify/functions/gestionarComunidades?id=${com.id}${force ? '&force=true' : ''}`, { method: 'DELETE', headers: authService.authHeader() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (d.requiereConfirmacion) { await eliminarComunidad(com, true); return; }
        setMsg(`Error: ${d.error || r.status}`);
        return;
      }
      setMsg(`✓ Comunidad "${com.nombre}" eliminada`);
      if (selectedCom?.id === com.id) setSelectedCom(null);
    } catch (e) { setMsg(`Error: ${e.message}`); return; }
    cargar();
  };

  // Contratos vinculados a la comunidad seleccionada
  const contratosCom = selectedCom
    ? data.filter(m => m.codigoComunidad === selectedCom.codigo)
    : [];

  if (loading) return (
    <div className="bg-white rounded-2xl border shadow-lg p-12 text-center text-gray-400">
      <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin mx-auto mb-2" />
      Cargando comunidades…
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Cabecera + botón crear */}
      <div className="bg-white rounded-2xl border shadow-lg p-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800">Comunidades de propietarios</h3>
          <p className="text-xs text-gray-400 mt-0.5">Descuento personalizable por comunidad, al alcanzar el nº de contratos firmados que definas</p>
        </div>
        <button onClick={() => { setShowForm(v => !v); setMsg(''); }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors">
          <Icons.Plus className="w-4 h-4" /> Nueva comunidad
        </button>
      </div>

      {/* Formulario nuevo */}
      {showForm && (
        <div className="bg-white rounded-2xl border shadow-lg p-5 space-y-4">
          <p className="text-sm font-bold text-gray-700">Nueva comunidad de propietarios</p>
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-4 sm:col-span-1">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Nombre *</label>
              <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Comunidad Los Pinos" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Contacto</label>
              <input value={form.personaContacto} onChange={e => setForm(f => ({ ...f, personaContacto: e.target.value }))}
                placeholder="Juan García" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Teléfono</label>
              <input value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
                placeholder="666 123 456" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Contratos para activar el descuento</label>
              <input type="number" min="1" value={form.umbralDescuento}
                onChange={e => setForm(f => ({ ...f, umbralDescuento: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">% de descuento</label>
              <input type="number" min="1" max="99" value={form.descuentoPct}
                onChange={e => setForm(f => ({ ...f, descuentoPct: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Código (opcional — si lo dejas vacío se genera uno)</label>
              <input value={form.codigo} onChange={e => setForm(f => ({ ...f, codigo: e.target.value.toUpperCase() }))}
                placeholder="LOSPINOS" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:border-red-400" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={crearComunidad} disabled={saving || !form.nombre.trim()}
              className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-xl">
              {saving ? 'Creando…' : form.codigo.trim() ? 'Crear comunidad' : 'Crear y generar código'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
          </div>
          {msg && <p className={`text-sm font-medium ${msg.startsWith('✓') ? 'text-green-600' : 'text-red-600'}`}>{msg}</p>}
        </div>
      )}

      {/* Tabla */}
      {comunidades.length === 0 ? (
        <div className="bg-white rounded-2xl border shadow-lg p-12 text-center text-gray-400">
          <p className="font-medium">Sin comunidades todavía</p>
          <p className="text-xs mt-1">Crea la primera comunidad para generar su código</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border shadow-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Código', 'Nombre', 'Contacto', 'Firmados', 'Umbral descuento', '% descuento', 'Descuento', 'Estado', ''].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {comunidades.map(com => (
                <tr key={com.id}
                  onClick={() => setSelectedCom(selectedCom?.id === com.id ? null : com)}
                  className={`hover:bg-gray-50 cursor-pointer ${selectedCom?.id === com.id ? 'bg-red-50' : ''}`}>
                  <td className="py-3 px-4 font-mono font-bold text-gray-900">{com.codigo}</td>
                  <td className="py-3 px-4 font-semibold text-gray-800">{com.nombre}</td>
                  <td className="py-3 px-4 text-gray-500">{com.personaContacto || '—'}</td>
                  <td className="py-3 px-4">
                    <span className={`font-bold ${com.totalFirmados >= com.umbralDescuento ? 'text-green-700' : 'text-gray-700'}`}>
                      {com.totalFirmados}
                    </span>
                    {com.totalFirmados < com.umbralDescuento && (
                      <span className="text-xs text-gray-400 ml-1">/ {com.umbralDescuento}</span>
                    )}
                  </td>
                  <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                    <input type="number" min="1" defaultValue={com.umbralDescuento} key={com.id + com.umbralDescuento}
                      onBlur={e => actualizarUmbral(com, e.target.value)}
                      className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-red-400" />
                  </td>
                  <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                    <input type="number" min="1" max="99" defaultValue={com.descuentoPct} key={com.id + '-pct-' + com.descuentoPct}
                      onBlur={e => actualizarDescuentoPct(com, e.target.value)}
                      className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-red-400" />
                  </td>
                  <td className="py-3 px-4">
                    {com.descuentoActivo
                      ? <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">✓ −{com.descuentoPct}% activo</span>
                      : <span className="text-xs text-gray-400">{Math.max(0, com.umbralDescuento - com.totalFirmados)} para activar</span>}
                  </td>
                  <td className="py-3 px-4">
                    <button onClick={e => { e.stopPropagation(); toggleActivo(com); }}
                      className={`text-xs font-bold px-2 py-0.5 rounded-full border ${com.activo ? 'text-green-700 bg-green-50 border-green-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200' : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-green-50 hover:text-green-700'}`}>
                      {com.activo ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <button onClick={e => { e.stopPropagation(); eliminarComunidad(com); }}
                      title="Eliminar comunidad"
                      className="p-1.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Icons.Trash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Contratos de la comunidad seleccionada */}
      {selectedCom && (
        <div className="bg-white rounded-2xl border shadow-lg overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <p className="font-bold text-gray-800">{selectedCom.nombre} — contratos vinculados</p>
              <p className="text-xs text-gray-400 mt-0.5">Código: <span className="font-mono font-bold text-gray-700">{selectedCom.codigo}</span></p>
            </div>
            <button onClick={() => setSelectedCom(null)} className="text-xs text-gray-400 hover:text-gray-600">✕ cerrar</button>
          </div>
          {contratosCom.length === 0 ? (
            <p className="p-6 text-sm text-gray-400 text-center">Ningún contrato vinculado todavía</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Cliente', 'Estado', 'Precio/año', 'Firmado'].map(h => (
                    <th key={h} className="text-left py-2 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {contratosCom.map(m => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="py-2 px-4 font-semibold text-gray-900">{m.clienteNombre}</td>
                    <td className="py-2 px-4 text-gray-500">{m.estado}</td>
                    <td className="py-2 px-4 text-gray-700">{m.precioAnual ? `${Number(m.precioAnual).toFixed(2).replace('.', ',')}€` : '—'}</td>
                    <td className="py-2 px-4 text-gray-400 text-xs">{fmt(m.fechaFirma) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Socios ──────────────────────────────────────────────────────────────

const TIPOS_SOCIO = {
  instalador:    { label: 'Instalador',       cls: 'bg-blue-100 text-blue-800'   },
  gestor_fincas: { label: 'Gestor de fincas', cls: 'bg-green-100 text-green-800' },
  promotor:      { label: 'Promotor',         cls: 'bg-purple-100 text-purple-800' },
  otro:          { label: 'Otro',             cls: 'bg-gray-100 text-gray-700'   },
};

function TipoBadge({ tipo }) {
  const t = TIPOS_SOCIO[tipo] || TIPOS_SOCIO.otro;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t.cls}`}>{t.label}</span>;
}

function TabSocios({ data }) {
  const [socios,       setSocios]      = useState([]);
  const [loadingSoc,   setLoadingSoc]  = useState(true);
  const [showForm,     setShowForm]    = useState(false);
  const [editando,     setEditando]    = useState(null);
  const [form,         setForm]        = useState({ nombre: '', tipo: 'instalador', telefono: '', email: '', notas: '', comisionFija: 10, codigo: '' });
  const [saving,       setSaving]      = useState(false);
  const [msg,          setMsg]         = useState('');
  const [selectedSocio,setSelectedSocio] = useState(null);
  const [comisiones,   setComisiones]  = useState([]);
  const [loadingCom,   setLoadingCom]  = useState(false);
  const [marcando,     setMarcando]    = useState(null); // id de comisión que se está marcando

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadSocios = React.useCallback(async () => {
    setLoadingSoc(true);
    try {
      const r = await fetch('/.netlify/functions/gestionarSocios', { headers: authService.authHeader() });
      const d = await r.json();
      if (d.success) setSocios(d.socios);
    } catch {}
    finally { setLoadingSoc(false); }
  }, []);

  useEffect(() => { loadSocios(); }, [loadSocios]);

  const cargarComisiones = React.useCallback(async (socioId) => {
    setLoadingCom(true);
    try {
      const r = await fetch(`/.netlify/functions/gestionarComisionesSocios?socioId=${socioId}`, { headers: authService.authHeader() });
      const d = await r.json();
      if (d.success) setComisiones(d.comisiones || []);
    } catch {}
    finally { setLoadingCom(false); }
  }, []);

  const abrirNuevo = () => {
    setEditando(null);
    // activo:true explícito — sin esto, el socio se creaba SIN el campo
    // `activo` en Firestore. La lista de admin lo mostraba "Activo" igual
    // (docToSocio en gestionarSocios.js asume activo por defecto si falta),
    // pero la verificación pública de código (página de firma) exige
    // activo==true de verdad — un campo ausente nunca cumple esa igualdad,
    // así que NINGÚN socio podía verificarse nunca (bug real, Rafa 2026-10-21:
    // "MILENIUN" se veía activo en el panel pero la firma pública decía
    // "código no válido o inactivo").
    setForm({ nombre: '', tipo: 'instalador', telefono: '', email: '', notas: '', comisionFija: 10, codigo: '', activo: true });
    setMsg('');
    setShowForm(true);
  };

  const abrirEditar = (s) => {
    setEditando(s.id);
    setForm({ nombre: s.nombre, tipo: s.tipo || 'instalador', telefono: s.telefono || '', email: s.email || '', notas: s.notas || '', comisionFija: s.comisionFija ?? 10, codigo: s.codigo || '', activo: s.activo !== false });
    setMsg('');
    setShowForm(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { setMsg('El nombre es obligatorio'); return; }
    setSaving(true); setMsg('');
    try {
      const payload = editando ? { id: editando, ...form } : form;
      const r = await fetch('/.netlify/functions/gestionarSocios', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error);
      setShowForm(false); setEditando(null);
      await loadSocios();
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  const eliminar = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar el socio "${nombre}"?`)) return;
    try {
      const r = await fetch(`/.netlify/functions/gestionarSocios?id=${id}`, { method: 'DELETE', headers: authService.authHeader() });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error al eliminar');
      if (selectedSocio?.id === id) setSelectedSocio(null);
      await loadSocios();
    } catch (e) { setMsg(e.message); }
  };

  const seleccionarSocio = (s) => {
    if (selectedSocio?.id === s.id) {
      setSelectedSocio(null);
      setComisiones([]);
    } else {
      setSelectedSocio(s);
      cargarComisiones(s.id);
    }
  };

  const marcarPagada = async (comision) => {
    const nuevoEstado = comision.estado === 'pagada' ? 'pendiente' : 'pagada';
    setMarcando(comision.id);
    try {
      await fetch(`/.netlify/functions/gestionarComisionesSocios?id=${comision.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      if (selectedSocio) await cargarComisiones(selectedSocio.id);
    } catch (e) { setMsg(e.message); }
    finally { setMarcando(null); }
  };

  // Contratos vinculados: por socioId (campo nuevo) o por nombre en instaladorReferido (legado)
  const contratosDelSocio = (s) => data.filter(m =>
    m.socioId === s.id ||
    (m.instaladorReferido && m.instaladorReferido === s.nombre)
  );

  const stats = useMemo(() => {
    const map = {};
    for (const s of socios) {
      const contratos = data.filter(m =>
        m.socioId === s.id ||
        (m.instaladorReferido && m.instaladorReferido === s.nombre)
      );
      // Antes esta lista se había retipeado a mano y se dejó 'vencido' fuera
      // — un contrato vencido de un socio no contaba como "activo" para él,
      // aunque sí contara en el ARR general. Ahora usa el mismo conjunto que
      // el resto de la Analítica.
      const activos = contratos.filter(m => ESTADOS_VIGENTE_AMPLIO.includes(m.estado));
      // firmados: excluye 'contrato_enviado' y el resto de estados de
      // captación — solo cuenta lo que el cliente ha firmado de verdad (ver
      // nota en TabAnalitica sobre fechaInicioContrato vs fechaPrimeraFirma).
      const firmados = contratos.filter(m => !ESTADOS_CAPTACION.includes(m.estado));
      const esteMes = firmados.filter(m => esMesActual(m.fechaPrimeraFirma || m.fechaFirma));
      map[s.id] = {
        total:     contratos.length,
        activos:   activos.length,
        esteMes:   esteMes.length,
        ingresos:  firmados.reduce((sum, m) => sum + (m.precioAnual || 0), 0),
      };
    }
    return map;
  }, [socios, data]);

  const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400';

  if (loadingSoc) return (
    <div className="bg-white rounded-2xl border shadow-lg p-12 text-center text-gray-400">
      <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin mx-auto mb-2" />
      Cargando socios…
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Cabecera */}
      <div className="bg-white rounded-2xl border shadow-lg p-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800">Red de socios</h3>
          <p className="text-xs text-gray-400 mt-0.5">Instaladores, gestores de fincas, promotores y otros partners que nos refieren clientes</p>
        </div>
        <button onClick={abrirNuevo}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors">
          <Icons.Plus className="w-4 h-4" /> Nuevo socio
        </button>
      </div>

      {/* Formulario crear / editar */}
      {showForm && (
        <div className="bg-white rounded-2xl border shadow-lg p-5 space-y-4">
          <p className="text-sm font-bold text-gray-700">{editando ? 'Editar socio' : 'Nuevo socio'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className={editando ? 'sm:col-span-2' : 'sm:col-span-1'}>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Nombre *</label>
              <input value={form.nombre} onChange={e => setF('nombre', e.target.value)}
                placeholder="Nombre o empresa" className={inputCls} />
            </div>
            {!editando && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Código <span className="font-normal text-gray-400">(opcional — se autogenera si se deja vacío)</span>
                </label>
                <input
                  value={form.codigo}
                  onChange={e => setF('codigo', e.target.value.toUpperCase())}
                  placeholder="SOC-XXXXX"
                  maxLength={20}
                  className={`${inputCls} font-mono`}
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Tipo</label>
              <select value={form.tipo} onChange={e => setF('tipo', e.target.value)} className={inputCls}>
                {Object.entries(TIPOS_SOCIO).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Teléfono</label>
              <input value={form.telefono} onChange={e => setF('telefono', e.target.value)}
                placeholder="666 123 456" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Email</label>
              <input value={form.email} onChange={e => setF('email', e.target.value)}
                placeholder="socio@ejemplo.com" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Comisión fija (€/contrato)</label>
              <input type="number" min="0" step="1" value={form.comisionFija} onChange={e => setF('comisionFija', Number(e.target.value))}
                className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Notas</label>
              <textarea rows={2} value={form.notas} onChange={e => setF('notas', e.target.value)}
                placeholder="Zona de trabajo, acuerdo, condiciones…" className={inputCls} />
            </div>
            {editando && (
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.activo !== false} onChange={e => setF('activo', e.target.checked)} />
                  Socio activo — si se desmarca, su código deja de funcionar en la firma pública
                </label>
              </div>
            )}
          </div>
          {msg && <p className="text-sm text-red-600 font-medium">{msg}</p>}
          <div className="flex gap-2">
            <button onClick={guardar} disabled={saving || !form.nombre.trim()}
              className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 rounded-xl">
              {saving ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear socio'}
            </button>
            <button onClick={() => { setShowForm(false); setEditando(null); }} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Sin socios */}
      {socios.length === 0 && !showForm && (
        <div className="bg-white rounded-2xl border shadow-lg p-12 text-center text-gray-400">
          <p className="font-medium">Sin socios todavía</p>
          <p className="text-xs mt-1">Añade instaladores, gestores de fincas o promotores con los que trabajes</p>
        </div>
      )}

      {/* Lista de socios */}
      {socios.length > 0 && (
        <div className="bg-white rounded-2xl border shadow-lg overflow-hidden">
          {/* Desktop: tabla */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Socio', 'Código', 'Tipo', 'Contacto', 'Contratos', 'Comisión/ctto', ''].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {socios.map(s => {
                  const st = stats[s.id] || { total: 0, activos: 0, esteMes: 0, ingresos: 0 };
                  return (
                    <tr key={s.id}
                      onClick={() => seleccionarSocio(s)}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedSocio?.id === s.id ? 'bg-red-50' : ''}`}>
                      <td className="py-3 px-4 font-semibold text-gray-900">{s.nombre}</td>
                      <td className="py-3 px-4 font-mono text-xs text-gray-500">{s.codigo || '—'}</td>
                      <td className="py-3 px-4"><TipoBadge tipo={s.tipo} /></td>
                      <td className="py-3 px-4 text-gray-500 text-xs">
                        {s.telefono && <p>{s.telefono}</p>}
                        {s.email    && <p>{s.email}</p>}
                        {!s.telefono && !s.email && <span className="text-gray-300">—</span>}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-bold text-gray-900">{st.activos}</span>
                        {st.total > st.activos && <span className="text-gray-400 text-xs ml-1">/{st.total}</span>}
                      </td>
                      <td className="py-3 px-4 text-gray-600 font-semibold">
                        {(s.comisionFija ?? 10).toFixed(2).replace('.', ',')}€
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                          <button onClick={() => abrirEditar(s)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold">Editar</button>
                          <button onClick={() => eliminar(s.id, s.nombre)}
                            className="text-xs text-red-500 hover:text-red-700 font-semibold">Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Móvil: tarjetas */}
          <div className="sm:hidden divide-y divide-gray-100">
            {socios.map(s => {
              const st = stats[s.id] || { total: 0, activos: 0, esteMes: 0, ingresos: 0 };
              return (
                <div key={s.id}
                  onClick={() => seleccionarSocio(s)}
                  className={`px-4 py-3 cursor-pointer active:bg-gray-50 ${selectedSocio?.id === s.id ? 'bg-red-50' : ''}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm leading-tight">{s.nombre}</p>
                      {s.codigo && <p className="font-mono text-xs text-gray-400">{s.codigo}</p>}
                    </div>
                    <TipoBadge tipo={s.tipo} />
                  </div>
                  {(s.telefono || s.email) && (
                    <p className="text-xs text-gray-400 mb-1">{[s.telefono, s.email].filter(Boolean).join(' · ')}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-600"><strong>{st.activos}</strong> activos</span>
                    {st.esteMes > 0 && <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">+{st.esteMes} este mes</span>}
                    {st.ingresos > 0 && <span className="text-xs text-gray-500">{st.ingresos.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€/año</span>}
                  </div>
                  <div className="flex gap-3 mt-2" onClick={e => e.stopPropagation()}>
                    <button onClick={() => abrirEditar(s)} className="text-xs text-indigo-600 font-semibold">Editar</button>
                    <button onClick={() => eliminar(s.id, s.nombre)} className="text-xs text-red-500 font-semibold">Eliminar</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Panel del socio seleccionado */}
      {selectedSocio && (() => {
        const contratos  = contratosDelSocio(selectedSocio);
        const pendientes = comisiones.filter(c => c.estado === 'pendiente');
        const pagadas    = comisiones.filter(c => c.estado === 'pagada');
        const totalPendiente = pendientes.reduce((s, c) => s + (c.monto || 0), 0);
        const totalPagado    = pagadas.reduce((s, c) => s + (c.monto || 0), 0);
        return (
          <div className="space-y-4">
            {/* Cabecera del panel */}
            <div className="bg-white rounded-2xl border shadow-lg p-4 flex items-center justify-between">
              <div>
                <p className="font-bold text-gray-800">{selectedSocio.nombre}</p>
                {selectedSocio.codigo && <p className="font-mono text-xs text-gray-400 mt-0.5">{selectedSocio.codigo}</p>}
              </div>
              <button onClick={() => { setSelectedSocio(null); setComisiones([]); }} className="text-xs text-gray-400 hover:text-gray-600">✕ cerrar</button>
            </div>

            {/* Resumen de comisiones */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-2xl border shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{pendientes.length}</p>
                <p className="text-xs text-gray-500 mt-0.5">Pendientes</p>
                <p className="text-sm font-semibold text-orange-500 mt-1">{totalPendiente.toFixed(2).replace('.', ',')}€</p>
              </div>
              <div className="bg-white rounded-2xl border shadow-sm p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{pagadas.length}</p>
                <p className="text-xs text-gray-500 mt-0.5">Pagadas</p>
                <p className="text-sm font-semibold text-green-500 mt-1">{totalPagado.toFixed(2).replace('.', ',')}€</p>
              </div>
              <div className="bg-white rounded-2xl border shadow-sm p-4 text-center col-span-2 sm:col-span-1">
                <p className="text-2xl font-bold text-gray-800">{contratos.length}</p>
                <p className="text-xs text-gray-500 mt-0.5">Contratos referidos</p>
              </div>
            </div>

            {/* Lista de comisiones */}
            <div className="bg-white rounded-2xl border shadow-lg overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <p className="font-bold text-gray-800 text-sm">Comisiones</p>
              </div>
              {loadingCom ? (
                <div className="p-8 text-center">
                  <div className="w-4 h-4 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin mx-auto" />
                </div>
              ) : comisiones.length === 0 ? (
                <p className="p-6 text-sm text-gray-400 text-center">Sin comisiones todavía. Se generan cuando un cliente firma usando el código de este socio.</p>
              ) : (
                <>
                  {/* Desktop */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          {['Cliente', 'Importe', 'Estado', 'Generada', 'Pagada', ''].map(h => (
                            <th key={h} className="text-left py-2 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {comisiones.map(c => (
                          <tr key={c.id} className="hover:bg-gray-50">
                            <td className="py-2 px-4 font-semibold text-gray-900">{c.clienteNombre || '—'}</td>
                            <td className="py-2 px-4 font-bold text-gray-700">{c.monto.toFixed(2).replace('.', ',')}€</td>
                            <td className="py-2 px-4">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.estado === 'pagada' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>
                                {c.estado === 'pagada' ? '✓ Pagada' : 'Pendiente'}
                              </span>
                            </td>
                            <td className="py-2 px-4 text-gray-400 text-xs">{fmt(c.fechaGenerada) || '—'}</td>
                            <td className="py-2 px-4 text-gray-400 text-xs">{c.fechaPagada ? fmt(c.fechaPagada) : '—'}</td>
                            <td className="py-2 px-4">
                              <button
                                onClick={() => marcarPagada(c)}
                                disabled={marcando === c.id}
                                className={`text-xs font-semibold px-2 py-0.5 rounded-lg border transition-colors ${c.estado === 'pagada' ? 'text-gray-500 border-gray-200 hover:border-orange-300 hover:text-orange-600' : 'text-green-700 border-green-200 hover:bg-green-50'}`}>
                                {marcando === c.id ? '…' : c.estado === 'pagada' ? 'Desmarcar' : 'Marcar pagada'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Móvil */}
                  <div className="sm:hidden divide-y divide-gray-100">
                    {comisiones.map(c => (
                      <div key={c.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="font-semibold text-gray-900 text-sm">{c.clienteNombre || '—'}</p>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${c.estado === 'pagada' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>
                            {c.estado === 'pagada' ? '✓ Pagada' : 'Pendiente'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold text-gray-700">{c.monto.toFixed(2).replace('.', ',')}€</span>
                            {c.fechaGenerada && <span className="text-xs text-gray-400">{fmt(c.fechaGenerada)}</span>}
                          </div>
                          <button
                            onClick={() => marcarPagada(c)}
                            disabled={marcando === c.id}
                            className={`text-xs font-semibold px-2 py-0.5 rounded-lg border flex-shrink-0 ${c.estado === 'pagada' ? 'text-gray-500 border-gray-200' : 'text-green-700 border-green-200'}`}>
                            {marcando === c.id ? '…' : c.estado === 'pagada' ? 'Desmarcar' : 'Pagar'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Contratos referidos */}
            <div className="bg-white rounded-2xl border shadow-lg overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <p className="font-bold text-gray-800 text-sm">Contratos referidos</p>
              </div>
              {contratos.length === 0 ? (
                <p className="p-6 text-sm text-gray-400 text-center">Ningún contrato vinculado todavía</p>
              ) : (
                <>
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          {['Cliente', 'Estado', 'Pack', 'Precio/año', 'Firmado'].map(h => (
                            <th key={h} className="text-left py-2 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {contratos.map(m => (
                          <tr key={m.id} className="hover:bg-gray-50">
                            <td className="py-2 px-4 font-semibold text-gray-900">{m.clienteNombre}</td>
                            <td className="py-2 px-4"><EstadoBadge estado={m.estado} /></td>
                            <td className="py-2 px-4">{m.pack ? <PackBadge pack={m.pack} /> : <span className="text-gray-300">—</span>}</td>
                            <td className="py-2 px-4 text-gray-700">{m.precioAnual ? `${Number(m.precioAnual).toFixed(2).replace('.', ',')}€` : '—'}</td>
                            <td className="py-2 px-4 text-gray-400 text-xs">{fmt(m.fechaFirma) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="sm:hidden divide-y divide-gray-100">
                    {contratos.map(m => (
                      <div key={m.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="font-semibold text-gray-900 text-sm">{m.clienteNombre}</p>
                          <EstadoBadge estado={m.estado} />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {m.pack && <PackBadge pack={m.pack} />}
                          {m.precioAnual && <span className="text-xs text-gray-600">{Number(m.precioAnual).toFixed(2).replace('.', ',')}€/año</span>}
                          {m.fechaFirma && <span className="text-xs text-gray-400">{fmt(m.fechaFirma)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Helper: descarga XML desde string en memoria ────────────────────────────

function descargarXML(xmlContent, filename) {
  const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Modal: Remesa SEPA general ──────────────────────────────────────────────

function SepaModal({ onClose, data }) {
  const [estado,  setEstado]  = useState('idle');
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState('');

  const preview = React.useMemo(() => {
    if (!data) return null;
    const hoy = new Date().toISOString().split('T')[0];
    const incluidos = data.filter(m =>
      ['firmado_activo', 'pendiente_renovar'].includes(m._storedEstado) ||
      (m._storedEstado === 'mantenimiento_agendado' && m.fechaProximoMantenimiento && m.fechaProximoMantenimiento < hoy)
    );
    const conIban = incluidos.filter(m => m.ibanEncriptado);
    const sinIban = incluidos.filter(m => !m.ibanEncriptado);
    const totalEur = conIban.reduce((s, m) => s + (m.precioAnual || 0) * 1.21, 0);
    return { total: incluidos.length, conIban: conIban.length, sinIban: sinIban.length, totalEur };
  }, [data]);

  async function generar() {
    setEstado('loading'); setError('');
    try {
      const r = await fetch('/.netlify/functions/generarRemesaSepa', { headers: authService.authHeader() });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Error generando remesa');
      setResult(d);
      setEstado('done');
    } catch (e) { setError(e.message); setEstado('error'); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Generar remesa SEPA</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {estado === 'idle' && (
          <>
            <div className="bg-blue-50 rounded-xl p-3 text-sm text-blue-700 space-y-1">
              <p>Las fechas de cobro se asignan <strong>automáticamente</strong>:</p>
              <p>• <strong>Contratos nuevos</strong> → día 10 del mes siguiente a la firma</p>
              <p>• <strong>Renovaciones</strong> → 10 de enero del próximo año</p>
            </div>
            {preview && (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-indigo-50 rounded-xl p-3">
                  <p className="text-xl font-bold text-indigo-700">{preview.total}</p>
                  <p className="text-xs text-indigo-500">Contratos</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3">
                  <p className="text-xl font-bold text-green-700">{preview.conIban}</p>
                  <p className="text-xs text-green-500">Con IBAN</p>
                </div>
                <div className={`rounded-xl p-3 ${preview.sinIban > 0 ? 'bg-orange-50' : 'bg-gray-50'}`}>
                  <p className={`text-xl font-bold ${preview.sinIban > 0 ? 'text-orange-700' : 'text-gray-400'}`}>{preview.sinIban}</p>
                  <p className={`text-xs ${preview.sinIban > 0 ? 'text-orange-500' : 'text-gray-400'}`}>Sin IBAN</p>
                </div>
              </div>
            )}
            {preview && preview.conIban > 0 && (
              <p className="text-sm text-gray-600 text-center">Importe estimado: <strong>{preview.totalEur.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€</strong> (c/IVA)</p>
            )}
            {preview && preview.total === 0 && (
              <p className="text-sm text-orange-600 bg-orange-50 rounded-xl px-3 py-2 text-center">No hay contratos listos para incluir en la remesa.</p>
            )}
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={generar} disabled={preview?.total === 0} className="px-4 py-2 text-sm font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">Generar remesa</button>
            </div>
          </>
        )}

        {estado === 'loading' && (
          <div className="flex items-center gap-3 py-4 text-indigo-600">
            <div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin flex-shrink-0" />
            Generando remesa…
          </div>
        )}

        {estado === 'done' && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-indigo-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-indigo-700">{result.nbOfTxs}</p>
                <p className="text-xs text-indigo-500">Total cobros</p>
              </div>
              <div className="bg-indigo-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-indigo-700">{result.ctrlSum}€</p>
                <p className="text-xs text-indigo-500">Importe total</p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Desglose por fecha de cobro</p>
              {(result.grupos || []).map((g, i) => (
                <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${g.seqTp === 'FRST' ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'}`}>
                  <span><strong>{g.seqTp}</strong> · {g.fechaCobro}</span>
                  <span className="font-bold">{g.count} cobros · {g.total}€</span>
                </div>
              ))}
            </div>
            {result.errores?.length > 0 && <p className="text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">Sin IBAN: {result.errores.join(', ')}</p>}
            {result.futuros?.length > 0 && (
              <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <p className="font-bold mb-1">{result.futuros.length} contrato(s) excluido(s) — cobro fuera de los próximos 45 días:</p>
                {result.futuros.map((f, i) => (
                  <p key={i}>{f.nombre} → {f.fechaCobro}</p>
                ))}
                <p className="mt-1 text-blue-500">Se incluirán automáticamente al generar la remesa en su mes correspondiente.</p>
              </div>
            )}
            {result.xmlContent && (
              <button onClick={() => descargarXML(result.xmlContent, result.filename)}
                className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl">
                <Icons.Download className="w-4 h-4" />Descargar XML ({result.filename})
              </button>
            )}
            <button onClick={onClose} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-xl">Cerrar</button>
          </div>
        )}

        {estado === 'error' && (
          <>
            <div className="bg-red-50 rounded-xl p-3 text-sm text-red-700">{error}</div>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cerrar</button>
              <button onClick={() => setEstado('idle')} className="px-4 py-2 text-sm font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700">Reintentar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Modal: Crear facturas en StelOrder ─────────────────────────────────────

function FacturasModal({ contratoIds, onClose }) {
  const [estado, setEstado] = useState('idle');
  const [result, setResult] = useState(null);
  const [error,  setError]  = useState('');
  const creandoRef = React.useRef(false); // bloqueo síncrono anti-doble-clic

  async function crear() {
    if (creandoRef.current) return;
    creandoRef.current = true;
    setEstado('loading'); setError('');
    try {
      const r = await fetch('/.netlify/functions/crearFacturasMantenimiento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ contratoIds }),
      });
      const d = await r.json();
      if (!d.success && !d.resultados) throw new Error(d.error || 'Error creando facturas');
      setResult(d);
      setEstado('done');
    } catch (e) { setError(e.message); setEstado('error'); }
    finally { creandoRef.current = false; }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Crear facturas en StelOrder</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        <p className="text-sm text-gray-500">
          <strong>{contratoIds.length}</strong> contratos seleccionados. Se creará una factura ordinaria en StelOrder para cada uno y pasarán a <strong>pendiente agendar</strong>.
        </p>

        {estado === 'idle' && (
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
            <button onClick={crear} className="px-4 py-2 text-sm font-bold rounded-xl bg-yellow-600 text-white hover:bg-yellow-700">
              Crear facturas
            </button>
          </div>
        )}

        {estado === 'loading' && (
          <div className="flex items-center gap-3 py-4 text-yellow-600">
            <div className="w-5 h-5 border-2 border-yellow-200 border-t-yellow-600 rounded-full animate-spin flex-shrink-0" />
            Creando facturas en StelOrder… puede tardar unos segundos.
          </div>
        )}

        {estado === 'done' && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-green-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-green-700">{result.exitosos}</p>
                <p className="text-xs text-green-600">Facturas creadas</p>
              </div>
              <div className={`rounded-xl p-3 ${result.fallidos > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <p className={`text-2xl font-bold ${result.fallidos > 0 ? 'text-red-700' : 'text-gray-400'}`}>{result.fallidos}</p>
                <p className={`text-xs ${result.fallidos > 0 ? 'text-red-600' : 'text-gray-400'}`}>Errores</p>
              </div>
            </div>
            {result.resultados?.filter(r => r.success).length > 0 && (
              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-700 max-h-40 overflow-y-auto space-y-1">
                <p className="font-bold text-gray-500 uppercase tracking-wide text-[10px] mb-2">Facturas creadas</p>
                {result.resultados.filter(r => r.success).map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="truncate flex-1">{r.clienteNombre}</span>
                    <span className="font-bold text-green-700 shrink-0">{r.facturaRef}</span>
                  </div>
                ))}
              </div>
            )}
            {result.resultados?.filter(r => !r.success).length > 0 && (
              <div className="bg-red-50 rounded-xl p-3 text-xs text-red-700 max-h-32 overflow-y-auto space-y-1">
                <p className="font-bold uppercase tracking-wide text-[10px] mb-1">Errores</p>
                {result.resultados.filter(r => !r.success).map((r, i) => (
                  <p key={i}><strong>{r.clienteNombre}</strong>: {r.error}</p>
                ))}
              </div>
            )}
            <button onClick={onClose} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-xl">Cerrar</button>
          </div>
        )}

        {estado === 'error' && (
          <>
            <div className="bg-red-50 rounded-xl p-3 text-sm text-red-700">{error}</div>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cerrar</button>
              <button onClick={() => setEstado('idle')} className="px-4 py-2 text-sm font-bold rounded-xl bg-yellow-600 text-white hover:bg-yellow-700">Reintentar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Vista principal ──────────────────────────────────────────────────────────

// ─── Modal: Procesar devolución SEPA (pain.002) ──────────────────────────────

const MOTIVOS_SEPA = {
  AM04: 'Fondos insuficientes',
  AC01: 'IBAN incorrecto',
  MD01: 'Sin mandato',
  MD02: 'Información mandato incompleta',
  MS02: 'Rechazado por el deudor',
  MS03: 'Razón no especificada',
  SL01: 'Cuenta protegida',
  AG01: 'Transacción prohibida',
  AG02: 'Código de transacción inválido',
  RR04: 'Razón regulatoria',
  FF01: 'Formato de fichero inválido',
};

function DevolucionSepaModal({ onClose, onDone }) {
  const [xml,    setXml]    = useState('');
  const [estado, setEstado] = useState('idle'); // idle | running | done | error
  const [result, setResult] = useState(null);
  const [error,  setError]  = useState('');

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setXml(ev.target.result); setEstado('idle'); setResult(null); setError(''); };
    reader.readAsText(file, 'UTF-8');
  }

  async function handleProcesar() {
    if (!xml) return;
    setEstado('running'); setError('');
    try {
      const resp = await fetch('/.netlify/functions/procesarDevolucionSepa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/xml', ...authService.authHeader() },
        body: xml,
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');
      setResult(data);
      setEstado('done');
      onDone();
    } catch (err) {
      setError(err.message);
      setEstado('error');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Devoluciones SEPA (pain.002)</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        <p className="text-sm text-gray-500">
          Sube el fichero <code className="bg-gray-100 px-1 rounded text-xs">pain.002</code> que te envía el banco. Los contratos rechazados pasan a «SEPA devuelto» con el motivo del banco; los cobrados pasan a «Pendiente factura».
        </p>

        <input type="file" accept=".xml,text/xml,application/xml" onChange={handleFile}
          className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-red-50 file:text-red-700 file:font-medium hover:file:bg-red-100" />

        {xml && estado === 'idle' && (
          <div className="bg-blue-50 rounded-xl p-3 text-sm text-blue-800">
            Fichero XML cargado. Pulsa "Procesar" para detectar rechazos.
          </div>
        )}

        {estado === 'running' && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin flex-shrink-0" />
            Procesando fichero…
          </div>
        )}

        {estado === 'done' && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-red-50 rounded-xl p-3">
                <div className="text-xl font-bold text-red-700">{result.devueltos ?? 0}</div>
                <div className="text-xs text-red-600">Contratos devueltos</div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3">
                <div className="text-xl font-bold text-emerald-700">{result.pendienteFactura ?? 0}</div>
                <div className="text-xs text-emerald-600">Cobrados → pendiente factura</div>
              </div>
            </div>
            {result.message && (
              <p className="text-sm text-gray-500 text-center">{result.message}</p>
            )}
            {result.noEncontrados?.length > 0 && (
              <div className="bg-yellow-50 rounded-xl p-3 text-xs text-yellow-800">
                <p className="font-bold mb-1">Sin coincidencia ({result.noEncontrados.length}) — referencia no encontrada:</p>
                <div className="space-y-0.5 max-h-24 overflow-y-auto">
                  {result.noEncontrados.map(r => <p key={r} className="font-mono">{r}</p>)}
                </div>
              </div>
            )}
            {result.sinReconocer?.length > 0 && (
              <div className="bg-orange-50 rounded-xl p-3 text-xs text-orange-800">
                <p className="font-bold mb-1">⚠ Revisión manual ({result.sinReconocer.length}) — estado del banco no reconocido, no se ha tocado el contrato:</p>
                <div className="space-y-0.5 max-h-24 overflow-y-auto">
                  {result.sinReconocer.map(r => <p key={r.endToEndId} className="font-mono">{r.endToEndId} — {r.txSts}</p>)}
                </div>
              </div>
            )}
            {result.detalle?.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-700 max-h-40 overflow-y-auto space-y-1">
                <p className="font-bold text-gray-500 uppercase tracking-wide text-[10px] mb-2">Contratos actualizados</p>
                {result.detalle.map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="truncate flex-1 font-mono text-gray-500">{d.referencia}</span>
                    <span className="font-bold text-red-700 shrink-0">{d.codigo}</span>
                    {MOTIVOS_SEPA[d.codigo] && (
                      <span className="text-gray-400 shrink-0 hidden sm:inline">{MOTIVOS_SEPA[d.codigo]}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {estado === 'error' && (
          <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{error}</div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose}
            className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
            {estado === 'done' ? 'Cerrar' : 'Cancelar'}
          </button>
          {estado !== 'done' && (
            <button onClick={handleProcesar} disabled={!xml || estado === 'running'}
              className="px-4 py-2 text-sm font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">
              {estado === 'running' ? 'Procesando…' : 'Procesar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Tab: Analítica ────────────────────────────────────────────────────────
// Algunas métricas (canal, LTV, altas/mes, tasa de baja) se calculan sobre los
// datos ya existentes. La tasa de renovación real y el tiempo medio
// vencido→renovado dependen de historialRenovaciones, que empieza a
// registrarse desde ahora (no hay forma de reconstruir renovaciones pasadas,
// porque cada renovación sobreescribía las fechas en el mismo documento).
const VALORACIONES_POR_PAGINA = 10;

function TabAnalitica({ data, kpis, isAdmin, setTab, setAutoFiltroContratos, setAutoFiltroAriston, setAutoFiltroVisitaPendiente, setAutoFiltroSinFechaVencimiento }) {
  const { obras: obrasParque, loading: cargandoObrasParque, estadoDe: estadoDeObraParque } = useObrasConEstado();
  const [paginaValoraciones, setPaginaValoraciones] = useState(0);
  useEffect(() => { setPaginaValoraciones(0); }, [data]);

  const stats = useMemo(() => {
    const conContrato = data.filter(m => m.tieneContrato);

    // Canal de captación (sobre todos los contratos firmados alguna vez, incluida baja)
    const porCanal = {};
    for (const m of conContrato) {
      const canal = m.canalCaptacion || 'otro';
      if (!porCanal[canal]) porCanal[canal] = { total: 0, baja: 0 };
      porCanal[canal].total++;
      if (m.estado === 'baja') porCanal[canal].baja++;
    }

    // Tipo de servicio (solo aerotermia / solo aire / ambas) — sobre contratos
    // vigentes: cuántos hay de cada clase, su facturación anual y su tasa de baja.
    const porTipoServicio = {};
    for (const clave of CLASES_SERVICIO) porTipoServicio[clave] = { total: 0, arr: 0, baja: 0, vigentes: 0 };
    for (const m of conContrato) {
      const c = claseServicioContrato(m);
      porTipoServicio[c].total++;
      if (m.estado === 'baja') porTipoServicio[c].baja++;
      else if (ESTADOS_VIGENTE_AMPLIO.includes(m.estado)) { porTipoServicio[c].vigentes++; porTipoServicio[c].arr += Number(m.precioAnual || 0); }
    }
    // Base para "% de la cartera" de cada clase — sobre vigentes, no sobre el
    // total histórico de esa clase (que incluye pendientes de firmar). Antes
    // se dividía por totalConContrato (histórico global, con baja incluida),
    // así que el % no usaba la misma "cartera" que el resto de tarjetas.
    const totalVigentesPortfolio = Object.values(porTipoServicio).reduce((s, c) => s + c.vigentes, 0);

    // Nuevos contratos por mes (últimos 12), según createdAt (nunca se sobreescribe)
    const porMes = {};
    const hoyD = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(hoyD.getFullYear(), hoyD.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      porMes[key] = { total: 0, visita: 0, sepa: 0 };
    }
    for (const m of conContrato) {
      const key = (m.createdAt || '').slice(0, 7);
      if (porMes[key]) {
        porMes[key].total++;
        if (m.modoCobro === 'visita') porMes[key].visita++; else porMes[key].sepa++;
      }
    }

    // Tasa de baja global + motivos
    const totalConContrato = conContrato.length;
    const totalBaja = conContrato.filter(m => m.estado === 'baja').length;
    const motivos = {};
    for (const m of conContrato) {
      if (m.estado === 'baja' && m.motivoBaja) motivos[m.motivoBaja] = (motivos[m.motivoBaja] || 0) + 1;
    }

    // LTV acumulado (crece con cada renovación registrada en historialRenovaciones)
    const vigentes = data.filter(m => ESTADOS_VIGENTE_AMPLIO.includes(m.estado));
    const ltvValores = vigentes.map(m => (m.precioAnual || 0) * (1 + (m.historialRenovaciones?.length || 0)));
    const ltvMedio = ltvValores.length ? ltvValores.reduce((a, b) => a + b, 0) / ltvValores.length : 0;

    // Renovaciones registradas (acumulando desde hoy) + tiempo medio vencido→renovado
    const todasRenovaciones = data.flatMap(m => m.historialRenovaciones || []);
    const conDias = todasRenovaciones.filter(r => r.diasDesdeVencimiento !== null && r.diasDesdeVencimiento !== undefined);
    const diasMedioRenovacion = conDias.length ? conDias.reduce((a, r) => a + r.diasDesdeVencimiento, 0) / conDias.length : null;

    // Satisfacción del cliente — encuesta enviada tras cada visita anual
    // (emailEncuestaSatisfaccion), registrada al pulsar el emoji en el email.
    const satisfaccionCounts = { mal: 0, regular: 0, excelente: 0 };
    for (const m of data) {
      if (satisfaccionCounts[m.satisfaccionValor] !== undefined) satisfaccionCounts[m.satisfaccionValor]++;
    }
    const totalSatisfaccion = satisfaccionCounts.mal + satisfaccionCounts.regular + satisfaccionCounts.excelente;
    const pctExcelente = totalSatisfaccion ? Math.round(satisfaccionCounts.excelente / totalSatisfaccion * 100) : 0;
    const respuestasSatisfaccion = data
      .filter(m => m.satisfaccionValor)
      .sort((a, b) => (b.satisfaccionFecha || '').localeCompare(a.satisfaccionFecha || ''));

    return { porCanal, porTipoServicio, totalVigentesPortfolio, porMes, totalConContrato, totalBaja, motivos, ltvMedio, todasRenovaciones, diasMedioRenovacion, satisfaccionCounts, totalSatisfaccion, pctExcelente, respuestasSatisfaccion };
  }, [data]);

  // ── Captación: pipeline del Kanban, ya disponible en `data` (sin fetch extra) ──
  const captacionStats = useMemo(() => {
    const leads = leadsCaptacion(data);
    const porColumna = {};
    KANBAN_COLS.forEach(c => { porColumna[c.id] = 0; });
    const porGrupo = { A: 0, B: 0, C: 0 };
    let calientes = 0;
    for (const m of leads) {
      const col = esClientePotencial(m) ? 'potenciales' : getKanbanColId(m.estado);
      if (porColumna[col] !== undefined) porColumna[col]++;
      if (m.grupo && porGrupo[m.grupo] !== undefined) porGrupo[m.grupo]++;
      if (esClienteCaliente(m)) calientes++;
    }
    return { total: leads.length, porColumna, porGrupo, calientes };
  }, [data]);

  // ── Socios: lista + comisiones (fetch propio, TabSocios usa los mismos endpoints) ──
  const [sociosAnalitica, setSociosAnalitica] = useState([]);
  const [comisionesAnalitica, setComisionesAnalitica] = useState([]);
  const [cargandoSocios, setCargandoSocios] = useState(true);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const r = await fetch('/.netlify/functions/gestionarSocios', { headers: authService.authHeader() });
        const d = await r.json();
        const socios = d.success ? (d.socios || []) : [];
        if (cancelado) return;
        setSociosAnalitica(socios);
        const resultados = await Promise.all(
          socios.map(s => fetch(`/.netlify/functions/gestionarComisionesSocios?socioId=${s.id}`, { headers: authService.authHeader() }).then(r => r.json()).catch(() => ({ comisiones: [] })))
        );
        if (cancelado) return;
        setComisionesAnalitica(resultados.flatMap(r => r.comisiones || []));
      } catch { /* deja los KPIs de socios en 0 si falla */ }
      finally { if (!cancelado) setCargandoSocios(false); }
    })();
    return () => { cancelado = true; };
  }, []);

  const sociosStats = useMemo(() => {
    let totalContratos = 0, ingresos = 0, activos = 0;
    for (const s of sociosAnalitica) {
      const contratos = data.filter(m => m.socioId === s.id || (m.instaladorReferido && m.instaladorReferido === s.nombre));
      if (contratos.length > 0) activos++;
      totalContratos += contratos.length;
      ingresos += contratos.reduce((sum, m) => sum + (m.precioAnual || 0), 0);
    }
    const comisionesPendientes = comisionesAnalitica.filter(c => c.estado === 'pendiente');
    const montoPendiente = comisionesPendientes.reduce((sum, c) => sum + (c.monto || 0), 0);
    return {
      totalSocios: sociosAnalitica.length, sociosActivos: activos, totalContratos, ingresos,
      comisionesPendientesCount: comisionesPendientes.length, montoPendiente,
    };
  }, [sociosAnalitica, comisionesAnalitica, data]);

  // ── Comunidades: lista con contadores de firmados/descuento (fetch propio) ──
  const [comunidadesAnalitica, setComunidadesAnalitica] = useState([]);
  const [cargandoComunidades, setCargandoComunidades] = useState(true);

  useEffect(() => {
    let cancelado = false;
    fetch('/.netlify/functions/gestionarComunidades', { headers: authService.authHeader() })
      .then(r => r.json())
      .then(d => { if (!cancelado && d.success) setComunidadesAnalitica(d.comunidades || []); })
      .catch(() => {})
      .finally(() => { if (!cancelado) setCargandoComunidades(false); });
    return () => { cancelado = true; };
  }, []);

  const comunidadesStats = useMemo(() => {
    const activas = comunidadesAnalitica.filter(c => c.activo);
    const conDescuento = comunidadesAnalitica.filter(c => c.descuentoActivo);
    const cercaDescuento = comunidadesAnalitica.filter(c => {
      if (c.descuentoActivo) return false;
      const faltan = (c.umbralDescuento || 5) - (c.totalFirmados || 0);
      return faltan >= 1 && faltan <= 2;
    });
    const totalFirmados = comunidadesAnalitica.reduce((sum, c) => sum + (c.totalFirmados || 0), 0);
    return { total: comunidadesAnalitica.length, activas: activas.length, conDescuento: conDescuento.length, cercaDescuento: cercaDescuento.length, totalFirmados };
  }, [comunidadesAnalitica]);

  const tasaBajaPct = stats.totalConContrato ? Math.round(stats.totalBaja / stats.totalConContrato * 100) : 0;

  return (
    <div className="space-y-6">
      <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
        <span>📋</span>Contratos
      </h3>

      {/* KPIs — gestión diaria */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-12 gap-3">
        <KPICard icon={<Icons.Clock className="w-4 h-4" />}     label="Pendiente firmar"    value={kpis.pendienteFirmar}  sub="enviados sin firmar"            colorBg="bg-purple-100" colorIcon="text-purple-600" alert={kpis.pendienteFirmar > 0} onClick={() => setTab('captacion')} />
        <KPICard icon={<Icons.X className="w-4 h-4" />}          label="Vencidos"            value={kpis.vencidos}         sub="llamar para renovar"            colorBg="bg-red-100"    colorIcon="text-red-600"    alert={kpis.vencidos > 0}       onClick={() => { setTab('contratos'); setAutoFiltroContratos('vencido'); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.Clock className="w-4 h-4" />}     label="Vence pronto"        value={kpis.vencePronto}      sub="≤30 días — avisar antes"        colorBg="bg-amber-100"  colorIcon="text-amber-600"  alert={kpis.vencePronto > 0}    onClick={() => { setTab('contratos'); setAutoFiltroContratos('vence_pronto'); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.Refresh className="w-4 h-4" />}   label="SEPA devueltos"      value={kpis.sepaDevueltos}    sub="corregir IBAN"                  colorBg="bg-red-100"    colorIcon="text-red-600"    alert={kpis.sepaDevueltos > 0}  onClick={() => { setTab('contratos'); setAutoFiltroContratos('sepa_devuelto'); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.Wrench className="w-4 h-4" />}    label="Pendiente agendar"   value={kpis.pendAgendar}      sub="visita sin programar"    colorBg="bg-cyan-100"   colorIcon="text-cyan-600"                                   onClick={() => { setTab('contratos'); setAutoFiltroContratos('pendiente_agendar'); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.Calendar className="w-4 h-4" />}  label="Visita pendiente"    value={kpis.visitaPendiente}  sub="en visita, sin agendar"         colorBg="bg-cyan-100"   colorIcon="text-cyan-600"   alert={kpis.visitaPendiente > 0} onClick={() => { setTab('contratos'); setAutoFiltroContratos(''); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(true); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.Calendar className="w-4 h-4" />}  label="Visitas agendadas"   value={kpis.visitasProg}      sub="pendiente de realizar"          colorBg="bg-blue-100"   colorIcon="text-blue-600"                                   onClick={() => { setTab('contratos'); setAutoFiltroContratos('mantenimiento_agendado'); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.DollarSign className="w-4 h-4" />} label="Pendiente renovar"  value={kpis.pendRenovar}      sub="agendar próxima visita"         colorBg="bg-orange-100" colorIcon="text-orange-600" alert={kpis.pendRenovar > 0}    onClick={() => { setTab('contratos'); setAutoFiltroContratos('pendiente_renovar'); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.Calendar className="w-4 h-4" />}  label="Pendiente reagendar" value={kpis.pendReagendar}    sub="visita agendada no realizada"   colorBg="bg-amber-100"  colorIcon="text-amber-600"  alert={kpis.pendReagendar > 0}  onClick={() => { setTab('contratos'); setAutoFiltroContratos('pendiente_reagendar'); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.Plus className="w-4 h-4" />}      label="Nuevos este mes"     value={kpis.nuevosEste}       sub={`total cartera: ${kpis.totalVigentes}`} colorBg="bg-green-100" colorIcon="text-green-600"                   onClick={() => { setTab('contratos'); setAutoFiltroContratos(''); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.Tag className="w-4 h-4" />}       label="Ariston sin ID"      value={kpis.aristonSinId}     sub="ID contrato pendiente"          colorBg="bg-amber-100"  colorIcon="text-amber-600"  alert={kpis.aristonSinId > 0}   onClick={() => { setTab('contratos'); setAutoFiltroContratos(''); setAutoFiltroAriston(true); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} />
        <KPICard icon={<Icons.X className="w-4 h-4" />}         label="Sin fecha vencimiento" value={kpis.sinFechaVencimiento} sub="dato incompleto"           colorBg="bg-gray-100"   colorIcon="text-gray-500"   alert={kpis.sinFechaVencimiento > 0} onClick={() => { setTab('contratos'); setAutoFiltroContratos(''); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(true); }} />
      </div>

      {/* KPIs — financiero (solo admin) */}
      {isAdmin && (
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
          {/* kpis.totalVigentes cuenta ahora los 10 estados de
              ESTADOS_VIGENTE_AMPLIO, no solo 'activo' — el filtro de la
              pestaña Contratos es de un único estado a la vez, así que no
              hay un filtro equivalente exacto: se limpia en vez de aplicar
              uno parcial que no cuadraría con el número mostrado aquí. */}
          <KPICard icon={<Icons.Wrench className="w-4 h-4" />}    label="Contratos vigentes"  value={kpis.totalVigentes}                                                          colorBg="bg-gray-100"   colorIcon="text-gray-600"   onClick={() => { setTab('contratos'); setAutoFiltroContratos(''); }} />
          <KPICard icon={<Icons.DollarSign className="w-4 h-4" />} label="Ingresos / mes"      value={`${Math.round(kpis.mrr).toLocaleString('es-ES')}€`} sub={`ARR: ${Math.round(kpis.arr).toLocaleString('es-ES')}€`} colorBg="bg-blue-100" colorIcon="text-blue-600" />
          <KPICard icon={<Icons.Zap className="w-4 h-4" />}       label="Domiciliados (SEPA)" value={kpis.modoSepa}         sub={`${(kpis.modoVisita + kpis.modoSepa) ? Math.round(kpis.modoVisita / (kpis.modoVisita + kpis.modoSepa) * 100) : 0}% ya en visita`} colorBg="bg-indigo-100" colorIcon="text-indigo-600" />
          <KPICard icon={<Icons.DollarSign className="w-4 h-4" />} label="En cobro SEPA"       value={kpis.sepaGenerados}    sub={`${kpis.pendCobrarEur.toLocaleString('es-ES', {minimumFractionDigits:2,maximumFractionDigits:2})}€`}  colorBg="bg-orange-100" colorIcon="text-orange-600" onClick={() => { setTab('contratos'); setAutoFiltroContratos('sepa_generado'); }} />
          <KPICard icon={<Icons.Tag className="w-4 h-4" />}        label="Pendiente facturar"  value={kpis.pendFactura}       sub="cobrado, sin factura"  colorBg="bg-yellow-100" colorIcon="text-yellow-600" alert={kpis.pendFactura > 0} onClick={() => { setTab('contratos'); setAutoFiltroContratos('pendiente_factura'); }} />
          <KPICard icon={<Icons.Refresh className="w-4 h-4" />}    label="Importe devuelto"    value={`${kpis.devueltosEur.toLocaleString('es-ES', {minimumFractionDigits:2,maximumFractionDigits:2})}€`} sub={`${kpis.sepaDevueltos} contratos`} colorBg="bg-red-100" colorIcon="text-red-600" alert={kpis.sepaDevueltos > 0} onClick={() => { setTab('contratos'); setAutoFiltroContratos('sepa_devuelto'); }} />
          <KPICard icon={<Icons.DollarSign className="w-4 h-4" />} label="Por renovar"          value={`${kpis.renovarEur.toLocaleString('es-ES', {minimumFractionDigits:2,maximumFractionDigits:2})}€`}  sub={`${kpis.pendRenovar} contratos`}  colorBg="bg-orange-100" colorIcon="text-orange-600" onClick={() => { setTab('contratos'); setAutoFiltroContratos('pendiente_renovar'); }} />
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPICard icon={<Icons.DollarSign className="w-4 h-4" />} label="LTV medio (acumulado)" value={`${Math.round(stats.ltvMedio).toLocaleString('es-ES')}€`} sub="crece con cada renovación registrada" colorBg="bg-blue-100" colorIcon="text-blue-600" />
        <KPICard icon={<Icons.X className="w-4 h-4" />} label="Tasa de baja" value={`${tasaBajaPct}%`} sub={`${stats.totalBaja} de ${stats.totalConContrato} contratos`} colorBg="bg-red-100" colorIcon="text-red-600" />
        <KPICard icon={<Icons.Refresh className="w-4 h-4" />} label="Renovaciones registradas" value={stats.todasRenovaciones.length} sub="acumulando desde hoy" colorBg="bg-green-100" colorIcon="text-green-600" />
        <KPICard icon={<Icons.Clock className="w-4 h-4" />} label="Días medios vencido→renovado" value={stats.diasMedioRenovacion !== null ? Math.round(stats.diasMedioRenovacion) : '—'} sub={stats.diasMedioRenovacion !== null ? (stats.diasMedioRenovacion > 0 ? 'de media, tras vencer' : 'de media, antes de vencer') : 'sin datos aún'} colorBg="bg-amber-100" colorIcon="text-amber-600" />
        <KPICard icon={<Icons.Zap className="w-4 h-4" />} label="Satisfacción (excelente)" value={stats.totalSatisfaccion ? `${stats.pctExcelente}%` : '—'} sub={stats.totalSatisfaccion ? `${stats.totalSatisfaccion} valoraciones` : 'sin valoraciones aún'} colorBg="bg-emerald-100" colorIcon="text-emerald-600" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-3">Satisfacción del cliente (encuesta post-visita)</h3>
        {stats.totalSatisfaccion === 0 ? (
          <p className="text-sm text-gray-400">Sin valoraciones todavía.</p>
        ) : (
          <div className="space-y-2">
            {[
              { key: 'excelente', label: '😊 Excelente', color: 'bg-emerald-500' },
              { key: 'regular',   label: '😐 Regular',   color: 'bg-amber-500' },
              { key: 'mal',       label: '😞 Mal',       color: 'bg-red-500' },
            ].map(({ key, label, color }) => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-sm text-gray-700 w-28 flex-shrink-0">{label}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                  <div className={`${color} h-full rounded-full`} style={{ width: `${stats.totalSatisfaccion ? (stats.satisfaccionCounts[key] / stats.totalSatisfaccion * 100) : 0}%` }} />
                </div>
                <span className="text-xs font-bold text-gray-600 w-8 text-right flex-shrink-0">{stats.satisfaccionCounts[key]}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {stats.respuestasSatisfaccion.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-3">Clientes que han respondido a la encuesta</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 text-xs uppercase">
                <th className="pb-2">Cliente</th><th className="pb-2">Valoración</th><th className="pb-2 text-right">Fecha</th>
              </tr></thead>
              <tbody>
                {stats.respuestasSatisfaccion
                  .slice(paginaValoraciones * VALORACIONES_POR_PAGINA, (paginaValoraciones + 1) * VALORACIONES_POR_PAGINA)
                  .map(m => {
                  const cfg = {
                    excelente: { emoji: '😊', label: 'Excelente', cls: 'text-emerald-700' },
                    regular:   { emoji: '😐', label: 'Regular',   cls: 'text-amber-700' },
                    mal:       { emoji: '😞', label: 'Mal',       cls: 'text-red-700' },
                  }[m.satisfaccionValor] || { emoji: '', label: m.satisfaccionValor, cls: 'text-gray-700' };
                  return (
                    <React.Fragment key={m.id}>
                      <tr className="border-t border-gray-50">
                        <td className="py-1.5 text-gray-700">{m.clienteNombre || '—'}</td>
                        <td className={`py-1.5 font-semibold ${cfg.cls}`}>{cfg.emoji} {cfg.label}</td>
                        <td className="py-1.5 text-right text-gray-500">{fmt((m.satisfaccionFecha || '').slice(0, 10))}</td>
                      </tr>
                      {m.satisfaccionComentario && (
                        <tr>
                          <td colSpan={3} className="pb-1.5 text-xs text-gray-500 italic">"{m.satisfaccionComentario}"</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Paginacion total={stats.respuestasSatisfaccion.length} pagina={paginaValoraciones} onChange={setPaginaValoraciones} porPagina={VALORACIONES_POR_PAGINA} />
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-1">Tipo de servicio contratado</h3>
        <p className="text-xs text-gray-400 mb-3">Sobre contratos vigentes. La facturación es la suma de precios anuales de esa clase.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CLASES_SERVICIO.map(clave => {
            const s = stats.porTipoServicio[clave];
            if (!s || s.total === 0) return null;
            // Sobre vigentes de esa clase / vigentes totales — no sobre el
            // histórico (s.total incluye pendientes de firmar y bajas), para
            // que "cartera" signifique lo mismo aquí que en el resto de
            // tarjetas de Analítica.
            const pct = stats.totalVigentesPortfolio ? Math.round(s.vigentes / stats.totalVigentesPortfolio * 100) : 0;
            return (
              <button key={clave} onClick={() => { setTab('contratos'); }}
                className="text-left rounded-xl border border-gray-100 bg-gray-50 hover:bg-gray-100 p-3 transition-colors">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide leading-tight">{CLASE_SERVICIO_LABEL[clave]}</p>
                <p className="text-2xl font-bold text-gray-900 leading-none mt-1">{s.vigentes}<span className="text-sm font-semibold text-gray-400"> / {s.total}</span></p>
                <p className="text-[11px] text-gray-500 mt-1">{pct}% de la cartera · {fmtEur0(s.arr)}/año</p>
                {s.baja > 0 && <p className="text-[11px] text-red-500 mt-0.5">{Math.round(s.baja / s.total * 100)}% baja</p>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-3">Canal de captación</h3>
        <div className="space-y-2">
          {Object.entries(stats.porCanal).sort((a, b) => b[1].total - a[1].total).map(([canal, s]) => (
            <div key={canal} className="flex items-center gap-3">
              <span className="text-sm text-gray-700 w-32 flex-shrink-0">{CANALES[canal] || canal}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                <div className="bg-red-500 h-full rounded-full" style={{ width: `${stats.totalConContrato ? (s.total / stats.totalConContrato * 100) : 0}%` }} />
              </div>
              <span className="text-xs font-bold text-gray-600 w-32 flex-shrink-0 text-right">{s.total} ({s.total ? Math.round(s.baja / s.total * 100) : 0}% baja)</span>
            </div>
          ))}
          {stats.totalConContrato === 0 && <p className="text-sm text-gray-400">Sin contratos firmados todavía.</p>}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-3">Nuevos contratos por mes (últimos 12 meses)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 text-xs uppercase"><th className="pb-2">Mes</th><th className="pb-2 text-right">Total</th><th className="pb-2 text-right">En visita</th><th className="pb-2 text-right">SEPA</th></tr></thead>
            <tbody>
              {Object.entries(stats.porMes).map(([mes, s]) => (
                <tr key={mes} className="border-t border-gray-50">
                  <td className="py-1.5 text-gray-700">{mes}</td>
                  <td className="py-1.5 text-right font-bold text-gray-900">{s.total}</td>
                  <td className="py-1.5 text-right text-indigo-600">{s.visita}</td>
                  <td className="py-1.5 text-right text-orange-600">{s.sepa}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {Object.keys(stats.motivos).length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 mb-3">Motivos de baja más comunes</h3>
          <div className="space-y-1.5">
            {Object.entries(stats.motivos).sort((a, b) => b[1] - a[1]).map(([motivo, count]) => (
              <div key={motivo} className="flex justify-between text-sm">
                <span className="text-gray-700">{motivo}</span>
                <span className="font-bold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Captación */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
          <span>🎯</span>Captación
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <KPICard icon={<Icons.Layout className="w-4 h-4" />}  label="Leads en pipeline"     value={captacionStats.total}                    colorBg="bg-gray-100"   colorIcon="text-gray-600"   onClick={() => setTab('captacion')} />
          <KPICard icon={<Icons.Zap className="w-4 h-4" />}     label="🔥 Calientes (A+)"     value={captacionStats.calientes}                sub={`mantenimiento ≤${DIAS_CALIENTE}d`} colorBg="bg-orange-100" colorIcon="text-orange-600" alert={captacionStats.calientes > 0} onClick={() => setTab('captacion')} />
          <KPICard icon={<Icons.Clock className="w-4 h-4" />}   label="Clientes potenciales"  value={captacionStats.porColumna.potenciales}    sub="Grupo A · ya mantenidos" colorBg="bg-amber-100" colorIcon="text-amber-600" onClick={() => setTab('captacion')} />
          <KPICard icon={<Icons.FileText className="w-4 h-4" />} label="Contrato enviado"      value={captacionStats.porColumna.contrato_enviado} sub="pendiente de firma"   colorBg="bg-purple-100" colorIcon="text-purple-600" onClick={() => setTab('captacion')} />
          <KPICard icon={<Icons.X className="w-4 h-4" />}       label="No interesados"        value={captacionStats.porColumna.no_interesado}                              colorBg="bg-red-100"    colorIcon="text-red-600"    onClick={() => setTab('captacion')} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h4 className="font-bold text-gray-900 mb-3">Pipeline por columna</h4>
            <div className="space-y-2">
              {KANBAN_COLS.map(col => (
                <div key={col.id} className="flex items-center gap-3">
                  <span className="text-sm text-gray-700 w-36 flex-shrink-0 truncate">{col.label}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                    <div className={`${col.dot} h-full rounded-full`} style={{ width: `${captacionStats.total ? (captacionStats.porColumna[col.id] / captacionStats.total * 100) : 0}%` }} />
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-8 text-right flex-shrink-0">{captacionStats.porColumna[col.id]}</span>
                </div>
              ))}
              {captacionStats.total === 0 && <p className="text-sm text-gray-400">Sin leads en captación todavía.</p>}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h4 className="font-bold text-gray-900 mb-3">Por grupo de prioridad</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full w-20 flex-shrink-0 text-center bg-gradient-to-r from-orange-500 to-red-500 text-white">🔥 A+</span>
                <span className="text-xs text-gray-400 flex-1 truncate">Mantenimiento reciente · contactar ya</span>
                <span className="text-sm font-bold text-gray-800 flex-shrink-0">{captacionStats.calientes}</span>
              </div>
              {['A', 'B', 'C'].map(g => (
                <div key={g} className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full w-20 flex-shrink-0 text-center ${GRUPO_CFG[g].bg} ${GRUPO_CFG[g].text}`}>{GRUPO_CFG[g].label}</span>
                  <span className="text-xs text-gray-400 flex-1 truncate">{GRUPO_CFG[g].desc}</span>
                  <span className="text-sm font-bold text-gray-800 flex-shrink-0">{captacionStats.porGrupo[g]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Socios */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
          <span>🤝</span>Socios
        </h3>
        {cargandoSocios ? (
          <div className="flex items-center justify-center py-10 text-gray-400 gap-3">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
            Cargando socios...
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPICard icon={<Icons.Users className="w-4 h-4" />}      label="Socios"                value={sociosStats.totalSocios}   sub={`${sociosStats.sociosActivos} con contratos`} colorBg="bg-blue-100"   colorIcon="text-blue-600"   onClick={() => setTab('socios')} />
            <KPICard icon={<Icons.Wrench className="w-4 h-4" />}     label="Contratos generados"   value={sociosStats.totalContratos}                                                     colorBg="bg-purple-100" colorIcon="text-purple-600" onClick={() => setTab('socios')} />
            <KPICard icon={<Icons.DollarSign className="w-4 h-4" />} label="Ingresos generados"    value={`${Math.round(sociosStats.ingresos).toLocaleString('es-ES')}€`}                colorBg="bg-green-100"  colorIcon="text-green-600"  onClick={() => setTab('socios')} />
            <KPICard icon={<Icons.Clock className="w-4 h-4" />}      label="Comisiones pendientes" value={sociosStats.comisionesPendientesCount} sub={`${sociosStats.montoPendiente.toFixed(2).replace('.', ',')}€`} colorBg="bg-amber-100" colorIcon="text-amber-600" alert={sociosStats.comisionesPendientesCount > 0} onClick={() => setTab('socios')} />
          </div>
        )}
      </div>

      {/* Comunidades */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
          <span>🏘️</span>Comunidades
        </h3>
        {cargandoComunidades ? (
          <div className="flex items-center justify-center py-10 text-gray-400 gap-3">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
            Cargando comunidades...
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPICard icon={<Icons.Layout className="w-4 h-4" />}     label="Comunidades"           value={comunidadesStats.total}      sub={`${comunidadesStats.activas} activas`}  colorBg="bg-gray-100"   colorIcon="text-gray-600"   onClick={() => setTab('comunidades')} />
            <KPICard icon={<Icons.Tag className="w-4 h-4" />}        label="Con descuento activo"  value={comunidadesStats.conDescuento}                                              colorBg="bg-green-100"  colorIcon="text-green-600"  onClick={() => setTab('comunidades')} />
            <KPICard icon={<Icons.Clock className="w-4 h-4" />}      label="Cerca de activarlo"    value={comunidadesStats.cercaDescuento} sub="a 1-2 firmas de su umbral"            colorBg="bg-amber-100"  colorIcon="text-amber-600"  onClick={() => setTab('comunidades')} />
            <KPICard icon={<Icons.Wrench className="w-4 h-4" />}     label="Contratos vía comunidad" value={comunidadesStats.totalFirmados}                                           colorBg="bg-purple-100" colorIcon="text-purple-600" onClick={() => setTab('comunidades')} />
          </div>
        )}
      </div>

      {/* Obras — puesta en marcha Ariston */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
          <span>🏗️</span>Obras
        </h3>
        {cargandoObrasParque ? (
          <div className="flex items-center justify-center py-10 text-gray-400 gap-3">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
            Cargando obras...
          </div>
        ) : (
          <ObrasAnalitica obras={obrasParque} estadoDe={estadoDeObraParque} />
        )}
      </div>
    </div>
  );
}

function MantenimientosView({ clienteInicial, onClienteInicialConsumed }) {
  const { user }   = useAuth();
  const isAdmin    = user?.role === 'admin' || user?.role === 'coordinador_operaciones';
  const canEditPrecios = isAdmin;

  const [raw,          setRaw]         = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [error,        setError]       = useState('');
  const [tab,          setTab]         = useState('contratos');
  const [autoFiltroContratos, setAutoFiltroContratos] = useState('');
  const [autoFiltroAriston,  setAutoFiltroAriston]   = useState(false);
  const [autoFiltroVisitaPendiente, setAutoFiltroVisitaPendiente] = useState(false);
  const [autoFiltroSinFechaVencimiento, setAutoFiltroSinFechaVencimiento] = useState(false);
  const [selected,     setSelected]    = useState(null);
  const [showNuevo,          setShowNuevo]          = useState(false);
  const [nuevoContratoItem,  setNuevoContratoItem]   = useState(null);
  const [captacionSourceId,  setCaptacionSourceId]   = useState(null);
  const [showDevolucionSepa, setShowDevolucionSepa]  = useState(false);
  const [limpiando,          setLimpiando]           = useState(false);
  const [limpiezaAviso,      setLimpiezaAviso]       = useState('');

  // Corrección puntual (idempotente): antes, updateWorkorderState.js escribía
  // el cambio de estado en ordenes/stel-{id} en vez de ordenes/{id}, creando
  // un documento huérfano y desactualizado — aparecía duplicado (con otro
  // estado) en el historial del cliente. Se puede pulsar varias veces sin
  // problema; ya no se vuelven a crear huérfanos tras el fix.
  const limpiarOrdenesDuplicadas = async () => {
    setLimpiando(true); setLimpiezaAviso('');
    try {
      const resp = await fetch('/.netlify/functions/limpiarOrdenesDuplicadas', { method: 'POST', headers: { ...authService.authHeader() } });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error desconocido');
      if (d.borrados > 0) {
        setLimpiezaAviso(`${d.borrados} duplicados eliminados`);
      } else if (d.pendientes > 0) {
        setLimpiezaAviso(`${d.pendientes} huérfano(s) detectado(s), pendientes de que el cron complete el documento real`);
      } else {
        setLimpiezaAviso('Sin duplicados');
      }
    } catch (e) {
      setLimpiezaAviso(`Error: ${e.message}`);
    } finally {
      setLimpiando(false);
      setTimeout(() => setLimpiezaAviso(''), 8000);
    }
  };
  const [showSepaModal,      setShowSepaModal]       = useState(false);
  const [showFacturasModal,  setShowFacturasModal]   = useState(false);
  const [facturasSelIds,     setFacturasSelIds]      = useState([]);

  // Abrir modal de nuevo contrato si venimos desde el CRM con datos de cliente
  useEffect(() => {
    if (clienteInicial) {
      setNuevoContratoItem(clienteInicial);
      setShowNuevo(true);
      if (onClienteInicialConsumed) onClienteInicialConsumed();
    }
  }, []); // eslint-disable-line

  // Real-time Firestore listener — updates automáticamente cuando cambia cualquier contrato
  const load = useCallback(() => {}, []); // no-op: onSnapshot maneja los updates

  useEffect(() => {
    setLoading(true); setError('');
    const unsub = onSnapshot(
      collection(db, 'mantenimientos'),
      (snap) => {
        setRaw(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[onSnapshot mantenimientos]', err);
        setError(`Error en tiempo real: ${err.message}`);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []); // eslint-disable-line

  const data = useMemo(() => mapEstados(raw), [raw]);

  // Cálculo compartido con el dashboard de atención al cliente — ver
  // src/utils/mantenimientosKpis.js (única fuente de verdad para estos conteos).
  const kpis = useMemo(() => calcularKpisMantenimientos(data), [data]);

  const onSaved = async (newContratoId) => {
    if (captacionSourceId && captacionSourceId !== newContratoId) {
      try { await fetch(`/.netlify/functions/saveMantenimiento?id=${captacionSourceId}`, { method: 'DELETE', headers: authService.authHeader() }); }
      catch (e) { console.error('[onSaved] Error eliminando registro de captación:', e.message); }
      setCaptacionSourceId(null);
    }
    setShowNuevo(false); setNuevoContratoItem(null); setSelected(null);
  };

  const TABS = [
    { id: 'contratos',   label: 'Contratos' },
    { id: 'captacion',   label: 'Captación' },
    ...(isAdmin ? [{ id: 'socios', label: 'Socios' }] : []),
    ...(isAdmin ? [{ id: 'comunidades',  label: 'Comunidades'  }] : []),
    { id: 'obras',       label: 'Obras' },
    ...(isAdmin ? [{ id: 'analitica',    label: 'Analítica'    }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <div className="bg-white rounded-2xl shadow-lg border p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-1">
            <Icons.Wrench className="w-6 h-6 text-red-600" />
            Mantenimientos
          </h2>
          <p className="text-gray-500 text-sm">Contratos anuales · SAT Oficial Ariston · {data.length} equipos en base</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button onClick={() => exportarCSV(data)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
            <Icons.Download className="w-4 h-4" />CSV
          </button>
          {isAdmin && (
            <button onClick={() => setShowSepaModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors">
              <Icons.DollarSign className="w-4 h-4" />Remesa SEPA
            </button>
          )}
          {isAdmin && kpis.sepaGenerados > 0 && (
            <button onClick={async () => {
              try {
                const snap = await getDoc(doc(db, 'configuracion', 'ultimaRemesa'));
                if (!snap.exists()) { alert('No hay ninguna remesa guardada.'); return; }
                const { xml, filename } = snap.data();
                if (!xml) { alert('El fichero XML no está disponible.'); return; }
                descargarXML(xml, filename || 'remesa_sepa.xml');
              } catch (e) { alert('Error al recuperar la remesa: ' + e.message); }
            }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded-xl transition-colors" title="Volver a descargar el XML de la última remesa generada">
              <Icons.Download className="w-4 h-4" />Recuperar remesa
            </button>
          )}
          {isAdmin && (() => { const ids = data.filter(m => m.estado === 'pendiente_factura').map(m => m.id); return ids.length > 0 ? (
            <button onClick={() => { setFacturasSelIds(ids); setShowFacturasModal(true); }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-yellow-700 bg-yellow-50 hover:bg-yellow-100 rounded-xl transition-colors">
              <Icons.Tag className="w-4 h-4" />Crear facturas ({ids.length})
            </button>
          ) : null; })()}
          {isAdmin && (
            <button onClick={() => setShowDevolucionSepa(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-red-700 bg-red-50 hover:bg-red-100 rounded-xl transition-colors">
              <Icons.Refresh className="w-4 h-4" />Comprobar SEPA
            </button>
          )}
          {isAdmin && (
            <button onClick={limpiarOrdenesDuplicadas} disabled={limpiando}
              title="Elimina workorders duplicadas en el historial (documentos huérfanos de antes del fix)"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50">
              <Icons.Refresh className="w-4 h-4" />{limpiando ? 'Limpiando…' : 'Limpiar duplicados'}
            </button>
          )}
          {limpiezaAviso && (
            <span className="text-xs font-bold text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">{limpiezaAviso}</span>
          )}
          <button onClick={() => setShowNuevo(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors">
            <Icons.Plus className="w-4 h-4" />Nuevo contrato
          </button>
        </div>
      </div>

      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-max sm:w-fit">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Estado carga */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
          Cargando contratos...
        </div>
      )}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-6 text-center">
          <p className="font-semibold">{error}</p>
          <button onClick={load} className="mt-2 text-sm underline">Reintentar</button>
        </div>
      )}

      {/* Contenido por tab */}
      {!loading && !error && (
        <>
          {tab === 'contratos'   && <TabContratos   data={data.filter(m => !ESTADOS_CAPTACION.includes(m.estado))} onSelect={setSelected} selected={selected} onRefresh={load} isAdmin={isAdmin} autoFiltroEstado={autoFiltroContratos} onClearAutoFiltro={() => { setAutoFiltroContratos(''); setAutoFiltroAriston(false); setAutoFiltroVisitaPendiente(false); setAutoFiltroSinFechaVencimiento(false); }} autoFiltroAriston={autoFiltroAriston} onClearAutoFiltroAriston={() => setAutoFiltroAriston(false)} autoFiltroVisitaPendiente={autoFiltroVisitaPendiente} onClearAutoFiltroVisitaPendiente={() => setAutoFiltroVisitaPendiente(false)} autoFiltroSinFechaVencimiento={autoFiltroSinFechaVencimiento} onClearAutoFiltroSinFechaVencimiento={() => setAutoFiltroSinFechaVencimiento(false)} />}
          {tab === 'captacion'   && <TabCaptacion   data={data} onRefresh={load} isAdmin={isAdmin} onSelect={setSelected} selected={selected} onNuevoContrato={(item) => { setNuevoContratoItem(item); setCaptacionSourceId(item.id); }} />}
          {tab === 'socios' && <TabSocios data={data} />}
          {tab === 'comunidades'  && <TabComunidades  data={data} />}
          {tab === 'obras'        && <ObrasView       usuario={user} />}
          {tab === 'analitica'    && <TabAnalitica    data={data} kpis={kpis} isAdmin={isAdmin} setTab={setTab} setAutoFiltroContratos={setAutoFiltroContratos} setAutoFiltroAriston={setAutoFiltroAriston} setAutoFiltroVisitaPendiente={setAutoFiltroVisitaPendiente} setAutoFiltroSinFechaVencimiento={setAutoFiltroSinFechaVencimiento} />}
        </>
      )}

      {/* Panel lateral */}
      {selected && (
        <SidePanel
          key={selected.id}
          item={data.find(m => m.id === selected.id) || selected}
          onClose={() => setSelected(null)}
          onRefresh={() => { load(); setSelected(null); }}
          canEditPrecios={canEditPrecios}
          onNuevoContrato={() => { setNuevoContratoItem(selected); setCaptacionSourceId(selected.id); setSelected(null); }}
        />
      )}

      {/* Modal nuevo contrato (normal o pre-cargado desde captación) */}
      {(showNuevo || nuevoContratoItem) && (
        <NuevoContratoModal
          onClose={() => { setShowNuevo(false); setNuevoContratoItem(null); }}
          onSaved={onSaved}
          canEditPrecios={canEditPrecios}
          clienteInicial={nuevoContratoItem || null}
          existingData={data}
        />
      )}

      {/* Modal devoluciones SEPA */}
      {showDevolucionSepa && (
        <DevolucionSepaModal
          onClose={() => setShowDevolucionSepa(false)}
          onDone={() => { load(); }}
        />
      )}

      {/* Modal Remesa SEPA general */}
      {showSepaModal && (
        <SepaModal
          onClose={() => { setShowSepaModal(false); load(); }}
          data={data}
        />
      )}

      {/* Modal Crear facturas StelOrder */}
      {showFacturasModal && (
        <FacturasModal
          contratoIds={facturasSelIds}
          onClose={() => { setShowFacturasModal(false); setFacturasSelIds([]); load(); }}
        />
      )}
    </div>
  );
}

export default MantenimientosView;
export { NuevoContratoModal, calcularPrecioServicio, calcularDesglosePrecio, derivarServicios, derivarPack };
